/**
 * AI Agents — Multi-Agent Platform admin page.
 *
 * Features:
 * - Agent flow chart showing the 3 system agents and their relationships
 * - Agent list with details (tools, knowledge collections, status)
 * - Knowledge collection management with document upload
 * - Agent run history / observability
 * - Chat interface to test the orchestrator
 *
 * Gated by ai:agents_view permission.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  Bot,
  Brain,
  FileText,
  Loader2,
  Network,
  Send,
  Trash2,
  Upload,
  RefreshCw,
  CircleCheck,
  CircleX,
  Clock,
  Sparkles,
  Wand2,
  MessageSquare,
  Database,
  Settings,
  Plus,
  Play,
  Copy,
  History,
  RotateCcw,
  X,
  Pencil,
  BookOpen,
  Search,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Wrench,
  Mail,
  Phone,
  MessageCircle,
  Shield,
  Cpu,
  Eye,
  ArrowRight,
  ArrowLeft,
  ArrowRightLeft,
  Mic,
  Languages,
  AlertTriangle,
  Info,
  Lightbulb,
  Download,
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { api, apiPostStream } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { useConfirmDialog } from '../../components/ui/ConfirmDialog.js'
import { confirmDialog } from 'primereact/confirmdialog'
import EditAgentDrawer, {
  type Agent as DrawerAgent,
  type ToolDef,
  type KnowledgeDef,
  type CreateAgentData,
} from '../../components/admin/ai/EditAgentDrawer.js'
import ToolPickerDrawer from '../../components/admin/ai/ToolPickerDrawer.js'
import KnowledgePickerDrawer from '../../components/admin/ai/KnowledgePickerDrawer.js'
import AiPromptsPage from './AiPromptsPage.js'
import OrchestratorsPanel from '../../components/admin/ai/OrchestratorsPanel.js'
import GuardrailsPanel from '../../components/admin/ai/GuardrailsPanel.js'
import HandoffRulesPanel from '../../components/admin/ai/HandoffRulesPanel.js'
import { OrchestratorGraph } from '../../components/admin/ai/OrchestratorGraph.js'
import ResourceDrawer from '../../components/admin/ResourceDrawer.js'
import DocumentViewer from '../../components/admin/ai/DocumentViewer.js'
import Select from '../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Agent {
  id: string
  slug: string
  name: string
  description: string
  type: string
  is_system: boolean
  is_enabled: boolean
  config: Record<string, unknown> | null
  system_prompt: string | null
  prompt_id: string | null
  created_at: string
  updated_at: string
  tools: Array<{ tool_name: string; is_enabled: boolean; config?: Record<string, unknown> | null }>
  knowledge_collections: Array<{ id: string; slug: string; name: string; description?: string }>
}

interface ToolCatalogEntry {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  requiredPermissions: string[]
  accessesTenantData: boolean
  isCustom?: boolean
  category?: string
  handlerType?: string
  handlerConfig?: Record<string, unknown> | null
  isEnabled?: boolean
  version?: number
  id?: string
  createdAt?: string
  updatedAt?: string
}

interface KnowledgeCollection {
  id: string
  slug: string
  name: string
  description: string
  is_system: boolean
  created_at: string
  document_count: number
  version_count?: number
  latest_version?: number
}

interface Document {
  id: string
  filename: string
  mime_type: string
  size_bytes: number
  status: string
  processing_error: string | null
  created_at: string
  chunk_count: number
  current_version?: number
  version_count?: number
  /** True for the two seeded portal-FAQ docs — cannot be deleted via the UI. */
  is_builtin?: boolean
}

interface DocumentVersion {
  id: string
  version: number
  filename: string
  mime_type: string
  size_bytes: number
  checksum: string | null
  chunk_count: number
  notes: string | null
  created_by: string | null
  created_at: string
}

interface ChatFile {
  filename: string
  contentType: string
  bufferBase64: string
  sizeBytes: number
}

interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  intent?: string
  selectedAgent?: string
  progress?: string[]
  failed?: boolean
  files?: ChatFile[]
}

/** Orchestrator with members — shape returned by /admin/ai/orchestrators?all=true. */
interface GraphOrchestratorMember {
  id: string
  agentId: string
  slug: string
  name: string
  type: string
  isEnabled: boolean
}

interface GraphOrchestrator {
  id: string
  slug: string
  name: string
  description: string | null
  type: 'main' | 'side'
  isEnabled: boolean
  isArchived: boolean
  sortOrder: number
  members?: GraphOrchestratorMember[]
}

// ---------------------------------------------------------------------------
// Agent type metadata — icons, colors, and labels shared across agent UI
// ---------------------------------------------------------------------------

const AGENT_TYPE_META: Record<string, { icon: JSX.Element; border: string; bg: string; text: string; iconBg: string; label: string }> = {
  routing: { icon: <Network className="h-4 w-4" />, border: 'border-blue-200', bg: 'bg-blue-50/50', text: 'text-blue-700', iconBg: 'bg-blue-100 text-blue-600', label: 'Routing' },
  response: { icon: <Brain className="h-4 w-4" />, border: 'border-purple-200', bg: 'bg-purple-50/50', text: 'text-purple-700', iconBg: 'bg-purple-100 text-purple-600', label: 'Response' },
  data: { icon: <FileText className="h-4 w-4" />, border: 'border-emerald-200', bg: 'bg-emerald-50/50', text: 'text-emerald-700', iconBg: 'bg-emerald-100 text-emerald-600', label: 'Data' },
  translation: { icon: <Languages className="h-4 w-4" />, border: 'border-cyan-200', bg: 'bg-cyan-50/50', text: 'text-cyan-700', iconBg: 'bg-cyan-100 text-cyan-600', label: 'Translation' },
  custom: { icon: <Bot className="h-4 w-4" />, border: 'border-gray-200', bg: 'bg-gray-50', text: 'text-gray-700', iconBg: 'bg-gray-100 text-gray-600', label: 'Custom' },
}

// ---------------------------------------------------------------------------
// Agent detail panel
// ---------------------------------------------------------------------------

function _AgentDetail({ agent, onUpdate }: { agent: Agent; onUpdate: () => void }): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(agent.name)
  const [editDescription, setEditDescription] = useState(agent.description)
  const [systemPrompt, setSystemPrompt] = useState(agent.system_prompt ?? '')
  const [isEnabled, setIsEnabled] = useState(agent.is_enabled)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [isToolPickerOpen, setIsToolPickerOpen] = useState(false)
  const [isKnowledgePickerOpen, setIsKnowledgePickerOpen] = useState(false)
  const [toolCatalog, setToolCatalog] = useState<ToolCatalogEntry[]>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerKnowledgeCollections, setDrawerKnowledgeCollections] = useState<KnowledgeCollection[]>([])

  // Prompt source toggle — same UI as orchestrator form
  // 'inline' = type the system prompt directly; 'template' = pick a shared prompt template
  type PromptSource = 'inline' | 'template'
  const [promptSource, setPromptSource] = useState<PromptSource>(agent.prompt_id ? 'template' : 'inline')
  const [selectedPromptId, setSelectedPromptId] = useState(agent.prompt_id ?? '')
  const [availablePrompts, setAvailablePrompts] = useState<{ id: string; slug: string; name: string; content: string; category: string }[]>([])
  const [pendingSourceChange, setPendingSourceChange] = useState<{
    source: PromptSource
    discarding: string
    apply: () => void
  } | null>(null)
  const [pendingPromptChange, setPendingPromptChange] = useState<{
    promptId: string
    previousName: string
    nextName: string | null
  } | null>(null)
  const [promptSearch, setPromptSearch] = useState('')

  // AI-assisted prompt generation state (shared fields with orchestrator/template)
  const [showGenerate, setShowGenerate] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPolishing, setIsPolishing] = useState(false)
  const [polishPreview, setPolishPreview] = useState<{ before: string; after: string } | null>(null)
  const [isSavingAsPrompt, setIsSavingAsPrompt] = useState(false)
  const [genBrief, setGenBrief] = useState({
    task: '',
    audience: '',
    tone: 'professional' as 'professional' | 'friendly' | 'concise' | 'detailed',
    constraints: '',
    goal: '',
    rolePersona: '',
    instructions: '',
  })

  useEffect(() => {
    setIsEnabled(agent.is_enabled)
    setEditName(agent.name)
    setEditDescription(agent.description)
    setSystemPrompt(agent.system_prompt ?? '')
    setPromptSource(agent.prompt_id ? 'template' : 'inline')
    setSelectedPromptId(agent.prompt_id ?? '')
  }, [agent])

  // Load tool and knowledge catalogs when the side panels or edit drawer open
  useEffect(() => {
    if ((isToolPickerOpen || isDrawerOpen) && toolCatalog.length === 0) {
      api.get<{ tools: ToolCatalogEntry[] }>('/admin/ai/tools').then((res) => {
        setToolCatalog(res.tools ?? [])
      }).catch((err) => toast.error('Failed to load tools', err instanceof Error ? err.message : String(err)))
    }
    if ((isKnowledgePickerOpen || isDrawerOpen) && drawerKnowledgeCollections.length === 0) {
      api.get<{ collections: KnowledgeCollection[] }>('/admin/ai/knowledge-collections').then((res) => {
        setDrawerKnowledgeCollections(res.collections ?? [])
      }).catch((err) => toast.error('Failed to load knowledge collections', err instanceof Error ? err.message : String(err)))
    }
  }, [isToolPickerOpen, isKnowledgePickerOpen, isDrawerOpen, toolCatalog.length, drawerKnowledgeCollections.length])

  // Load available prompt templates when editing starts (for the template picker)
  useEffect(() => {
    if (editing && availablePrompts.length === 0) {
      api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch((err) => toast.error('Failed to load prompt templates', err instanceof Error ? err.message : String(err)))
    }
  }, [editing, availablePrompts.length])

  // Filtered prompt templates for the template picker (same as orchestrator form)
  const filteredPrompts = useMemo(() => {
    const q = promptSearch.toLowerCase()
    return availablePrompts.filter((p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
  }, [availablePrompts, promptSearch])

  const selectedPrompt = useMemo(
    () => availablePrompts.find((p) => p.id === selectedPromptId),
    [availablePrompts, selectedPromptId],
  )

  /**
   * Switch between inline and template prompt sources. Same confirmation
   * pattern as the orchestrator form: the inactive source is cleared so a
   * stale value can't compete at runtime, but the user is warned first
   * whenever that would throw away their work.
   */
  const handlePromptSourceChange = (nextSource: PromptSource): void => {
    if (nextSource === promptSource) return

    const losingInlineText = nextSource === 'template' && systemPrompt.trim().length > 0
    const losingTemplate = nextSource === 'inline' && !!selectedPromptId

    const applySwitch = (): void => {
      setPromptSource(nextSource)
      if (nextSource === 'inline') {
        setSelectedPromptId('')
      }
    }

    if (!losingInlineText && !losingTemplate) {
      applySwitch()
      return
    }

    setPendingSourceChange({
      source: nextSource,
      discarding: losingInlineText
        ? 'the inline prompt text you typed'
        : `the assigned template "${availablePrompts.find((p) => p.id === selectedPromptId)?.name ?? 'template'}"`,
      apply: applySwitch,
    })
  }

  /**
   * An agent uses exactly one prompt template, so swapping the selection
   * discards the previous one — confirm before replacing it.
   */
  const handlePromptSelect = (nextPromptId: string): void => {
    const previous = availablePrompts.find((p) => p.id === selectedPromptId)
    if (!previous || nextPromptId === selectedPromptId) {
      setSelectedPromptId(nextPromptId)
      return
    }

    const next = availablePrompts.find((p) => p.id === nextPromptId)
    setPendingPromptChange({
      promptId: nextPromptId,
      previousName: previous.name,
      nextName: next?.name ?? null,
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Exactly one prompt source is persisted — the inactive one is nulled
      // so a stale value can't linger and compete at runtime (same as orchestrator).
      await api.put(`/admin/ai/agents/${agent.slug}`, {
        name: editName || undefined,
        description: editDescription || undefined,
        system_prompt: promptSource === 'inline' ? (systemPrompt || undefined) : null,
        prompt_id: promptSource === 'template' ? (selectedPromptId || null) : null,
        is_enabled: isEnabled,
      })
      setEditing(false)
      onUpdate()
    } catch (err) {
      toast.error('Failed to update agent:', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const handleGeneratePrompt = async (): Promise<void> => {
    const hasBrief = genBrief.task.trim() || genBrief.constraints.trim()
    const hasStructured = genBrief.goal.trim() || genBrief.rolePersona.trim() || genBrief.instructions.trim()
    if (!hasBrief && !hasStructured) {
      toast.error('Nothing to generate from', 'Fill in a task, constraints, or a structured field first.')
      return
    }
    setIsGenerating(true)
    try {
      // Send agent context so the AI knows what kind of prompt to write:
      //   - agent type drives the role prefix ("You are the [type] agent ...")
      //   - name + description give identity and purpose
      //   - tool names tell it which tools the agent has access to
      //   - knowledge collection names tell it what it can search
      const toolNames = agent.tools.filter((t) => t.is_enabled).map((t) => t.tool_name)
      const knowledgeCollectionNames = agent.knowledge_collections.map((kc) => kc.name)
      const res = await api.post<{ content: string }>('/admin/ai/agents/generate-prompt', {
        ...(genBrief.task.trim() ? { task: genBrief.task.trim() } : {}),
        ...(genBrief.audience.trim() ? { audience: genBrief.audience.trim() } : {}),
        tone: genBrief.tone,
        ...(genBrief.constraints.trim() ? { constraints: genBrief.constraints.trim() } : {}),
        ...(genBrief.goal.trim() ? { goal: genBrief.goal.trim() } : {}),
        ...(genBrief.rolePersona.trim() ? { rolePersona: genBrief.rolePersona.trim() } : {}),
        ...(genBrief.instructions.trim() ? { instructions: genBrief.instructions.trim() } : {}),
        agentName: agent.name,
        agentType: agent.type as 'routing' | 'response' | 'data' | 'translation' | 'custom',
        ...(agent.description ? { agentDescription: agent.description } : {}),
        toolNames,
        knowledgeCollectionNames,
      })
      setSystemPrompt(res.content)
      // Generated text goes into the inline prompt — switch source and clear template
      setPromptSource('inline')
      setSelectedPromptId('')
      setShowGenerate(false)
      toast.success('Prompt generated', 'Review and edit the system prompt before saving.')
    } catch (err) {
      toast.error('Failed to generate prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePolishPrompt = async (): Promise<void> => {
    if (!systemPrompt.trim()) {
      toast.error('Nothing to polish', 'Write or generate a system prompt first.')
      return
    }
    setIsPolishing(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/agents/polish-prompt', {
        content: systemPrompt,
      })
      setPolishPreview({ before: systemPrompt, after: res.content })
    } catch (err) {
      toast.error('Failed to polish prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsPolishing(false)
    }
  }

  const handleAcceptPolish = (): void => {
    if (polishPreview) {
      setSystemPrompt(polishPreview.after)
      setPolishPreview(null)
      toast.success('Prompt polished', 'The improved prompt has been applied. Review and save.')
    }
  }

  const handleRejectPolish = (): void => {
    setPolishPreview(null)
  }

  // Save the current inline prompt as a reusable Prompt Studio template.
  // Same pattern as the create wizard — creates an ai_prompts row and
  // switches the prompt source to 'template' so the agent references it.
  const handleSaveAsPrompt = async (): Promise<void> => {
    if (!systemPrompt.trim()) {
      toast.error('Nothing to save', 'Write or generate a system prompt first.')
      return
    }
    setIsSavingAsPrompt(true)
    try {
      const promptSlug = `${agent.slug}-prompt`
      const promptName = `${editName.trim() || agent.slug} Prompt`
      const created = await api.post<{ id: string }>('/admin/ai/prompts', {
        slug: promptSlug,
        name: promptName,
        description: `System prompt for ${editName.trim() || agent.slug}`,
        category: 'agent',
        content: systemPrompt.trim(),
      })
      setSelectedPromptId(created.id)
      setPromptSource('template')
      // Refresh the available prompts list so the new template appears
      api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch((err) => toast.error('Saved, but failed to refresh the template list', err instanceof Error ? err.message : String(err)))
      toast.success('Saved to Prompt Studio', 'The prompt is now a reusable template. Save the agent to apply.')
    } catch (err) {
      toast.error('Failed to save prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsSavingAsPrompt(false)
    }
  }

  const handleDelete = () => {
    confirm({
      title: 'Delete Agent',
      message: `Delete agent "${agent.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        setDeleting(true)
        try {
          await api.delete(`/admin/ai/agents/${agent.slug}`)
          onUpdate()
        } catch (err) {
          toast.error('Failed to delete agent:', err instanceof Error ? err.message : String(err))
        } finally {
          setDeleting(false)
        }
      },
    })
  }

  const handleToggleEnabled = async (enabled: boolean) => {
    setIsEnabled(enabled)
    try {
      await api.put(`/admin/ai/agents/${agent.slug}`, { is_enabled: enabled })
      onUpdate()
    } catch (err) {
      toast.error('Failed to toggle agent:', err instanceof Error ? err.message : String(err))
      setIsEnabled(!enabled)
    }
  }

  const _handleAddTool = async (toolName: string) => {
    try {
      await api.put(`/admin/ai/agents/${agent.slug}/tools`, { toolName, isEnabled: true })
      onUpdate()
    } catch (err) {
      toast.error('Failed to add tool:', err instanceof Error ? err.message : String(err))
    }
  }

  const handleRemoveTool = async (toolName: string) => {
    try {
      await api.delete(`/admin/ai/agents/${agent.slug}/tools/${toolName}`)
      onUpdate()
    } catch (err) {
      toast.error('Failed to remove tool:', err instanceof Error ? err.message : String(err))
    }
  }

  const handleToggleTool = async (toolName: string, enabled: boolean) => {
    try {
      await api.put(`/admin/ai/agents/${agent.slug}/tools`, { toolName, isEnabled: !enabled })
      onUpdate()
    } catch (err) {
      toast.error('Failed to toggle tool:', err instanceof Error ? err.message : String(err))
    }
  }

  const handleSaveTools = async (selected: string[]): Promise<void> => {
    const current = new Set(agent.tools.map((t) => t.tool_name))
    const next = new Set(selected)
    const toAdd = selected.filter((name) => !current.has(name))
    const toRemove = agent.tools.filter((t) => !next.has(t.tool_name)).map((t) => t.tool_name)

    const promises: Promise<unknown>[] = []
    for (const toolName of toAdd) {
      promises.push(api.put(`/admin/ai/agents/${agent.slug}/tools`, { toolName, isEnabled: true }))
    }
    for (const toolName of toRemove) {
      promises.push(api.delete(`/admin/ai/agents/${agent.slug}/tools/${toolName}`))
    }

    try {
      await Promise.all(promises)
      setIsToolPickerOpen(false)
      onUpdate()
    } catch (err) {
      toast.error('Failed to save tools:', err instanceof Error ? err.message : String(err))
    }
  }

  const handleSaveKnowledge = async (selected: string[]): Promise<void> => {
    const current = new Set(agent.knowledge_collections.map((kc) => kc.id))
    const next = new Set(selected)
    const toAdd = selected.filter((id) => !current.has(id))
    const toRemove = agent.knowledge_collections.filter((kc) => !next.has(kc.id)).map((kc) => kc.id)

    const promises: Promise<unknown>[] = []
    for (const id of toAdd) {
      promises.push(api.post(`/admin/ai/agents/${agent.slug}/knowledge`, { collectionId: id }))
    }
    for (const id of toRemove) {
      promises.push(api.delete(`/admin/ai/agents/${agent.slug}/knowledge/${id}`))
    }

    try {
      await Promise.all(promises)
      setIsKnowledgePickerOpen(false)
      onUpdate()
    } catch (err) {
      toast.error('Failed to save knowledge:', err instanceof Error ? err.message : String(err))
    }
  }

  const toDrawerAgent = (a: Agent): DrawerAgent => {
    const category = (a.type === 'data'
      ? 'Data'
      : a.type === 'routing'
        ? 'Routing'
        : 'Response') as DrawerAgent['category']
    return {
      id: a.id,
      slug: a.slug,
      name: a.name,
      description: a.description,
      type: a.type,
      category,
      color: a.type === 'data' ? 'emerald' : a.type === 'routing' ? 'blue' : 'purple',
      status: { enabled: a.is_enabled, isSystem: a.is_system },
      tools: a.tools.map((t) => t.tool_name),
      knowledge: a.knowledge_collections.map((kc) => kc.id),
      instructions: a.system_prompt ?? undefined,
      promptId: a.prompt_id ?? null,
    }
  }

  const drawerTools: ToolDef[] = useMemo(
    () => toolCatalog.map((t) => ({ name: t.name, category: t.handlerType || 'General' })),
    [toolCatalog],
  )

  const drawerKnowledge: KnowledgeDef[] = useMemo(
    () => drawerKnowledgeCollections.map((kc) => ({ id: kc.id, name: kc.name, category: kc.is_system ? 'System' : 'Custom' })),
    [drawerKnowledgeCollections],
  )

  const pickerTools = useMemo(
    () => toolCatalog.map((t) => ({ name: t.name, description: t.description, category: t.handlerType || 'General' })),
    [toolCatalog],
  )

  const pickerKnowledge = useMemo(
    () => drawerKnowledgeCollections.map((kc) => ({ id: kc.id, name: kc.name, description: kc.description, category: kc.is_system ? 'System' : 'Custom' })),
    [drawerKnowledgeCollections],
  )

  const handleDrawerSave = async (updated: DrawerAgent, selectedGuardrailIds: string[]): Promise<void> => {
    setSaving(true)
    try {
      await api.put(`/admin/ai/agents/${agent.slug}`, {
        name: updated.name,
        description: updated.description,
        system_prompt: updated.instructions,
        is_enabled: updated.status.enabled,
        prompt_id: updated.promptId ?? null,
      })

      // Save guardrails attached to this agent
      await api.put(`/admin/ai/agents/${agent.slug}/guardrails`, {
        guardrails: selectedGuardrailIds.map((guardrailId, index) => ({ guardrailId, sortOrder: index })),
      })

      const currentTools = new Set(agent.tools.map((t) => t.tool_name))
      const updatedTools = new Set(updated.tools)
      const toolPromises: Promise<unknown>[] = []
      for (const toolName of updated.tools) {
        if (!currentTools.has(toolName)) {
          toolPromises.push(api.put(`/admin/ai/agents/${agent.slug}/tools`, { toolName, isEnabled: true }))
        }
      }
      for (const t of agent.tools) {
        if (!updatedTools.has(t.tool_name)) {
          toolPromises.push(api.delete(`/admin/ai/agents/${agent.slug}/tools/${t.tool_name}`))
        }
      }

      const currentKnowledge = new Set(agent.knowledge_collections.map((kc) => kc.id))
      const updatedKnowledge = new Set(updated.knowledge)
      const knowledgePromises: Promise<unknown>[] = []
      for (const id of updated.knowledge) {
        if (!currentKnowledge.has(id)) {
          knowledgePromises.push(api.post(`/admin/ai/agents/${agent.slug}/knowledge`, { collectionId: id }))
        }
      }
      for (const kc of agent.knowledge_collections) {
        if (!updatedKnowledge.has(kc.id)) {
          knowledgePromises.push(api.delete(`/admin/ai/agents/${agent.slug}/knowledge/${kc.id}`))
        }
      }

      await Promise.all([...toolPromises, ...knowledgePromises])
      setIsDrawerOpen(false)
      onUpdate()
    } catch (err) {
      toast.error('Failed to save agent:', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDrawerDelete = async (_id: string): Promise<void> => {
    try {
      await api.delete(`/admin/ai/agents/${agent.slug}`)
      setIsDrawerOpen(false)
      onUpdate()
    } catch (err) {
      toast.error('Failed to delete agent:', err instanceof Error ? err.message : String(err))
    }
  }

  const meta = AGENT_TYPE_META[agent.type] ?? AGENT_TYPE_META['custom']!

  const inputClass = 'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
  const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

  return (
    <div className="rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-7 w-7 items-center justify-center rounded-sm ${meta.iconBg}`}>
            {meta.icon}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{agent.name}</h3>
            <div className="flex items-center gap-1.5">
              {agent.is_system && (
                <span className="rounded-sm bg-[#3454cf]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#3454cf]">
                  SYSTEM
                </span>
              )}
              <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${agent.is_enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                {agent.is_enabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${meta.text} opacity-70`}>
                {meta.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!agent.is_system && (
            <button
              className="inline-flex items-center justify-center rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
              onClick={handleDelete}
              disabled={deleting}
              title="Delete agent"
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
              disabled={saving}
              className="h-3.5 w-3.5 rounded-sm border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
            />
            <span>Enabled</span>
          </label>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {editing ? (
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Name</label>
              <input type="text" className={inputClass} value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Agent name" />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea className={inputClass} rows={2} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Agent description" />
            </div>
            <div>
              {/* Prompt source toggle — identical to orchestrator form */}
              <div className="mb-1 flex items-center justify-between">
                <label className={labelClass}>System Prompt</label>
              </div>
              <div
                role="radiogroup"
                aria-label="Prompt source"
                className="mb-2 grid grid-cols-2 gap-1 rounded-sm bg-gray-100 p-1"
              >
                {([
                  { value: 'inline', label: 'Write inline', icon: <FileText className="h-3 w-3" /> },
                  { value: 'template', label: 'Use a template', icon: <Bot className="h-3 w-3" /> },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={promptSource === opt.value}
                    onClick={() => handlePromptSourceChange(opt.value)}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold transition-colors ${
                      promptSource === opt.value
                        ? 'bg-white text-[#3454cf] shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Inline prompt mode */}
              {promptSource === 'inline' ? (
                <div>
                  <div className="mb-1 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowGenerate(!showGenerate)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
                    >
                      <Sparkles className="h-3 w-3" />
                      {showGenerate ? 'Hide' : 'Generate with AI'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handlePolishPrompt()}
                      disabled={isPolishing || !systemPrompt.trim()}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline disabled:opacity-40"
                    >
                      {isPolishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                      Polish with AI
                    </button>
                  </div>
                  {showGenerate && (
                    <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                      <div className="space-y-3">
                        <div>
                          <label className={labelClass}>What should this prompt do?</label>
                          <textarea
                            value={genBrief.task}
                            onChange={(e) => setGenBrief((p) => ({ ...p, task: e.target.value }))}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="e.g. Answer customer questions using the knowledge base"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelClass}>Audience</label>
                            <input
                              type="text"
                              value={genBrief.audience}
                              onChange={(e) => setGenBrief((p) => ({ ...p, audience: e.target.value }))}
                              className={inputClass}
                              placeholder="e.g. Customer support"
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Tone</label>
                                                        <Select
                              ariaLabel={"Tone"}
                              size="sm"
                              value={genBrief.tone}
                              onChange={(v) => setGenBrief((p) => ({ ...p, tone: v as 'professional' | 'friendly' | 'concise' | 'detailed' }))}
                            >
                              <option value="professional">Professional</option>
                              <option value="friendly">Friendly</option>
                              <option value="concise">Concise</option>
                              <option value="detailed">Detailed</option>
                                                        </Select>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowGenerate(false)}
                            className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleGeneratePrompt()}
                            disabled={isGenerating || !genBrief.task.trim()}
                            className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                          >
                            {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                            Generate
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Polish preview — before/after diff with accept/reject */}
                  {polishPreview && (
                    <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3454cf]">Polish preview</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleRejectPolish}
                            className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            onClick={handleAcceptPolish}
                            className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                          >
                            <Check className="h-3 w-3" />
                            Accept
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Before</p>
                          <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-600 whitespace-pre-wrap">{polishPreview.before}</pre>
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">After</p>
                          <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-900 whitespace-pre-wrap">{polishPreview.after}</pre>
                        </div>
                      </div>
                    </div>
                  )}
                  <textarea className={`${inputClass} font-mono`} rows={6} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} placeholder="You are an AI agent that…" />
                  <div className="mt-1.5 flex items-center justify-between">
                    <p className="text-[10px] text-gray-400">
                      This prompt is sent to the AI model on every interaction.
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleSaveAsPrompt()}
                      disabled={isSavingAsPrompt || !systemPrompt.trim()}
                      className="inline-flex items-center gap-1 rounded bg-[#3454cf] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#2a45b0] disabled:opacity-50"
                    >
                      {isSavingAsPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookOpen className="h-3 w-3" />}
                      {isSavingAsPrompt ? 'Saving…' : 'Save to Prompt Studio'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {/* Template picker — same as orchestrator form */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Filter prompt templates..."
                      value={promptSearch}
                      onChange={(e) => setPromptSearch(e.target.value)}
                      className="w-full rounded-sm border border-gray-200 bg-white px-2.5 py-1.5 pl-8 text-xs outline-none focus:border-[#3454cf]"
                    />
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                  </div>
                                    <Select
                    ariaLabel={"Selected Prompt ID"}
                    size="sm"
                    value={selectedPromptId}
                    onChange={(v) => handlePromptSelect(v)}
                  >
                    <option value="">-- Select a prompt template --</option>
                    {filteredPrompts.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.slug})
                      </option>
                    ))}
                                    </Select>
                  {selectedPrompt && (
                    <div className="rounded border border-blue-100 bg-blue-50/50 p-2">
                      <div className="flex items-center justify-between text-xs text-blue-900">
                        <span>
                          Using <strong>{selectedPrompt.name}</strong>
                        </span>
                        <span className="font-mono text-[10px] text-blue-700">{selectedPrompt.slug}</span>
                      </div>
                      {/* Read-only: edit the text in Prompt Studio, not here. */}
                      <pre className="mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap border-t border-blue-100 pt-1.5 font-mono text-[10px] leading-snug text-blue-900/80">
                        {selectedPrompt.content || 'This template has no content yet.'}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50" onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                Save Changes
              </button>
              <button className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 text-xs leading-relaxed text-gray-500">{agent.description || 'No description provided.'}</p>

            {/* Tools — manageable */}
            <div className="mb-4">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Wrench className="h-3 w-3 text-gray-400" />
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Tools ({agent.tools.length})</h4>
                </div>
                <button
                  className="flex items-center gap-0.5 text-[11px] font-medium text-[#3454cf] hover:underline"
                  onClick={() => setIsToolPickerOpen(true)}
                >
                  <Plus className="h-3 w-3" />
                  Add
                </button>
              </div>

              {/* Tool chips with toggle/remove */}
              {agent.tools.length === 0 ? (
                <p className="text-[11px] text-gray-400">No tools attached. Click "Add" to assign capabilities.</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {agent.tools.map((tool) => (
                    <div
                      key={tool.tool_name}
                      className={`group flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${tool.is_enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}
                    >
                      <button
                        onClick={() => handleToggleTool(tool.tool_name, tool.is_enabled)}
                        className="transition-opacity"
                        title={tool.is_enabled ? 'Disable tool' : 'Enable tool'}
                      >
                        {tool.tool_name}
                      </button>
                      {!agent.is_system && (
                        <button
                          onClick={() => handleRemoveTool(tool.tool_name)}
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                          title="Remove tool"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Knowledge Collections */}
            <div className="mb-4">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Database className="h-3 w-3 text-gray-400" />
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Knowledge ({agent.knowledge_collections.length})</h4>
                </div>
                <button
                  className="flex items-center gap-0.5 text-[11px] font-medium text-[#3454cf] hover:underline"
                  onClick={() => setIsKnowledgePickerOpen(true)}
                >
                  <Plus className="h-3 w-3" />
                  Add
                </button>
              </div>
              {agent.knowledge_collections.length === 0 ? (
                <p className="text-[11px] text-gray-400">No knowledge collections attached. Click "Add" to assign sources.</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {agent.knowledge_collections.map((kc) => (
                    <span key={kc.id} className="rounded-sm bg-purple-50 px-1.5 py-0.5 text-[11px] font-medium text-purple-600">
                      {kc.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* System prompt preview */}
            {agent.system_prompt && !editing && (
              <div className="mb-4">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <Settings className="h-3 w-3 text-gray-400" />
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">System Prompt</h4>
                </div>
                <div className="rounded-sm border border-gray-100 bg-gray-50/50 p-2 font-mono text-[10px] leading-relaxed text-gray-500">
                  {agent.system_prompt.slice(0, 200)}
                  {agent.system_prompt.length > 200 && <span className="text-gray-300">... ({agent.system_prompt.length} chars)</span>}
                </div>
              </div>
            )}

            <button
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#3454cf] hover:underline"
              onClick={() => setIsDrawerOpen(true)}
            >
              <Pencil className="h-3 w-3" />
              Edit agent
            </button>
          </>
        )}
      </div>

      <EditAgentDrawer
        agent={toDrawerAgent(agent)}
        isOpen={isDrawerOpen}
        allTools={drawerTools}
        allKnowledge={drawerKnowledge}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleDrawerSave}
        onDelete={handleDrawerDelete}
      />

      <ToolPickerDrawer
        title="Manage Tools"
        tools={pickerTools}
        assigned={agent.tools.map((t) => t.tool_name)}
        isOpen={isToolPickerOpen}
        onClose={() => setIsToolPickerOpen(false)}
        onSave={handleSaveTools}
      />

      <KnowledgePickerDrawer
        title="Manage Knowledge"
        collections={pickerKnowledge}
        assigned={agent.knowledge_collections.map((kc) => kc.id)}
        isOpen={isKnowledgePickerOpen}
        onClose={() => setIsKnowledgePickerOpen(false)}
        onSave={handleSaveKnowledge}
      />

      {/* Prompt Source Switch Confirmation */}
      {pendingSourceChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">
                Switch to {pendingSourceChange.source === 'template' ? 'a prompt template' : 'an inline prompt'}?
              </h3>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-gray-600">
              An agent can only use one prompt source, so switching will discard{' '}
              <strong>{pendingSourceChange.discarding}</strong>. The previous prompt is preserved in the audit history and can be restored later.
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingSourceChange(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  pendingSourceChange.apply()
                  setPendingSourceChange(null)
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Yes, switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Template Override Confirmation */}
      {pendingPromptChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">
                {pendingPromptChange.nextName ? 'Override the existing prompt?' : 'Remove the assigned prompt?'}
              </h3>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-gray-600">
              An agent can only use one prompt template at a time.
              {pendingPromptChange.nextName
                ? ' Assigning a new template will replace the one currently assigned.'
                : ' Clearing the selection will unassign the current template.'}
            </p>
            <div className="mb-4 space-y-1.5 rounded bg-gray-50 p-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-[10px] uppercase text-gray-400">Current</span>
                <span className="min-w-0 truncate font-medium text-red-700 line-through">
                  {pendingPromptChange.previousName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-[10px] uppercase text-gray-400">New</span>
                <span className="min-w-0 truncate font-medium text-emerald-700">
                  {pendingPromptChange.nextName ?? 'None'}
                </span>
              </div>
            </div>
            <p className="mb-4 text-xs font-medium text-gray-700">
              Are you sure you want to assign it like this?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingPromptChange(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSelectedPromptId(pendingPromptChange.promptId)
                  setPendingPromptChange(null)
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Yes, override
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Create Agent Wizard — multi-step creation flow
// ---------------------------------------------------------------------------

const TOOL_CATEGORIES: Record<string, { label: string; icon: JSX.Element; tools: string[] }> = {
  'Data & Reports': {
    label: 'Data & Reports',
    icon: <Database className="h-3.5 w-3.5" />,
    tools: ['database_read', 'csv_generator', 'xlsx_generator', 'pdf_generator', 'json_generator'],
  },
  'Knowledge & Search': {
    label: 'Knowledge & Search',
    icon: <Search className="h-3.5 w-3.5" />,
    tools: ['search_knowledge', 'search_faq', 'search_sop', 'get_campaign_information', 'get_property_information'],
  },
  'Conversations': {
    label: 'Conversations',
    icon: <MessageSquare className="h-3.5 w-3.5" />,
    tools: ['get_conversation_context', 'get_conversation_history', 'get_customer_context', 'update_conversation_properties', 'assign_conversation', 'set_priority'],
  },
  'Routing': {
    label: 'Routing',
    icon: <Network className="h-3.5 w-3.5" />,
    tools: ['get_campaigns', 'get_branches', 'get_available_agents', 'get_routing_rules', 'assign_campaign', 'assign_branch'],
  },
  'Future Capabilities': {
    label: 'Future Capabilities',
    icon: <Sparkles className="h-3.5 w-3.5" />,
    tools: ['send_email', 'send_sms', 'send_message', 'close_conversation', 'create_ticket', 'database_write'],
  },
}

const FUTURE_TOOL_INFO: Record<string, { description: string; icon: JSX.Element; available: boolean }> = {
  send_email: { description: 'Send emails to customers or team members', icon: <Mail className="h-3.5 w-3.5" />, available: false },
  send_sms: { description: 'Send SMS messages to customers', icon: <Phone className="h-3.5 w-3.5" />, available: false },
  send_message: { description: 'Send messages on chat channels (WhatsApp, Web, etc.)', icon: <MessageCircle className="h-3.5 w-3.5" />, available: false },
  close_conversation: { description: 'Close or resolve conversations automatically', icon: <CircleCheck className="h-3.5 w-3.5" />, available: false },
  create_ticket: { description: 'Create new support tickets from conversations', icon: <FileText className="h-3.5 w-3.5" />, available: false },
  database_write: { description: 'Create, update, delete records in the database (CRUD)', icon: <Database className="h-3.5 w-3.5" />, available: false },
}

const AGENT_TYPE_PRESETS: Record<string, { description: string; recommendedTools: string[]; systemPromptHint: string }> = {
  routing: {
    description: 'Classifies intent and routes conversations to the right agent, team, or campaign',
    recommendedTools: ['get_conversation_context', 'get_customer_context', 'get_campaigns', 'get_available_agents', 'get_routing_rules', 'assign_conversation', 'assign_campaign'],
    systemPromptHint: 'You are a routing agent. Analyze the conversation and determine the best agent, team, or campaign to assign it to.',
  },
  response: {
    description: 'Handles customer-facing responses using knowledge base, FAQs, and SOPs',
    recommendedTools: ['search_knowledge', 'search_faq', 'search_sop', 'get_campaign_information', 'get_property_information', 'get_conversation_history'],
    systemPromptHint: 'You are a response agent. Search the knowledge base and provide accurate, helpful answers to customer questions.',
  },
  data: {
    description: 'Answers data questions by querying the database and generating reports',
    recommendedTools: ['database_read', 'csv_generator', 'xlsx_generator', 'pdf_generator', 'json_generator'],
    systemPromptHint: 'You are a data analysis agent. Understand natural language data requests and generate SQL queries and reports.',
  },
  translation: {
    description: 'Translates messages between languages for multilingual customer support',
    recommendedTools: ['get_conversation_context'],
    systemPromptHint: 'You are a translation agent. Translate messages accurately between languages while preserving meaning and context.',
  },
  custom: {
    description: 'Build a custom agent with any combination of tools and capabilities',
    recommendedTools: [],
    systemPromptHint: 'You are a custom AI agent. Define your purpose in the system prompt below.',
  },
}

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5

const WIZARD_STEPS = [
  { label: 'Identity', icon: <Bot className="h-3.5 w-3.5" /> },
  { label: 'Type', icon: <Cpu className="h-3.5 w-3.5" /> },
  { label: 'Tools', icon: <Wrench className="h-3.5 w-3.5" /> },
  { label: 'Knowledge', icon: <Database className="h-3.5 w-3.5" /> },
  { label: 'Prompt', icon: <FileText className="h-3.5 w-3.5" /> },
  { label: 'Review', icon: <Settings className="h-3.5 w-3.5" /> },
]

const SLUG_REGEX = /^[a-z0-9-]+$/
const SLUG_MAX = 100

function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function _CreateAgentWizard({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }): JSX.Element | null {
  const [step, setStep] = useState<WizardStep>(0)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [type, setType] = useState('custom')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [promptId, setPromptId] = useState('')
  const [isEnabled, setIsEnabled] = useState(true)
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set())
  const [selectedKnowledge, setSelectedKnowledge] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [toolCatalog, setToolCatalog] = useState<ToolCatalogEntry[]>([])
  const [knowledgeCollections, setKnowledgeCollections] = useState<KnowledgeCollection[]>([])
  const [availablePrompts, setAvailablePrompts] = useState<{ id: string; slug: string; name: string; content: string; category: string }[]>([])
  const [selectedPromptId, setSelectedPromptId] = useState('')
  const [isSavingAsPrompt, setIsSavingAsPrompt] = useState(false)

  // Prompt source toggle — same UI as agent edit form and orchestrator form
  type WizardPromptSource = 'inline' | 'template'
  const [wizardPromptSource, setWizardPromptSource] = useState<WizardPromptSource>('inline')
  const [wizardPromptSearch, setWizardPromptSearch] = useState('')
  const [wizardPendingSourceChange, setWizardPendingSourceChange] = useState<{
    source: WizardPromptSource
    discarding: string
    apply: () => void
  } | null>(null)

  // AI generate / polish state — same as agent edit form
  const [wizardShowGenerate, setWizardShowGenerate] = useState(false)
  const [wizardIsGenerating, setWizardIsGenerating] = useState(false)
  const [wizardIsPolishing, setWizardIsPolishing] = useState(false)
  const [wizardPolishPreview, setWizardPolishPreview] = useState<{ before: string; after: string } | null>(null)
  const [wizardGenBrief, setWizardGenBrief] = useState({
    task: '',
    audience: '',
    tone: 'professional' as 'professional' | 'friendly' | 'concise' | 'detailed',
    constraints: '',
    goal: '',
    rolePersona: '',
    instructions: '',
  })

  // Auto-generate slug from name until the user edits it manually
  useEffect(() => {
    if (name && !slugTouched) {
      setSlug(normalizeSlug(name))
    }
  }, [name, slugTouched])

  // Load tool catalog, knowledge collections, and prompt templates when wizard opens
  useEffect(() => {
    if (!open) return
    api.get<{ tools: ToolCatalogEntry[] }>('/admin/ai/tools').then((res) => {
      setToolCatalog(res.tools ?? [])
    }).catch((err) => toast.error('Failed to load tools', err instanceof Error ? err.message : String(err)))
    api.get<{ collections: KnowledgeCollection[] }>('/admin/ai/knowledge-collections').then((res) => {
      setKnowledgeCollections(res.collections ?? [])
    }).catch((err) => toast.error('Failed to load knowledge collections', err instanceof Error ? err.message : String(err)))
    api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
      setAvailablePrompts(res.prompts ?? [])
    }).catch((err) => toast.error('Failed to load prompt templates', err instanceof Error ? err.message : String(err)))
  }, [open])

  // Reset wizard when closed
  useEffect(() => {
    if (!open) {
      setStep(0)
      setName('')
      setSlug('')
      setSlugTouched(false)
      setDescription('')
      setType('custom')
      setSystemPrompt('')
      setPromptId('')
      setIsEnabled(true)
      setSelectedTools(new Set())
      setSelectedKnowledge(new Set())
      setSelectedPromptId('')
      setAvailablePrompts([])
      setError(null)
      setWizardPromptSource('inline')
      setWizardPromptSearch('')
      setWizardShowGenerate(false)
      setWizardIsGenerating(false)
      setWizardIsPolishing(false)
      setWizardPolishPreview(null)
      setWizardGenBrief({ task: '', audience: '', tone: 'professional', constraints: '', goal: '', rolePersona: '', instructions: '' })
    }
  }, [open])

  // Apply type preset
  useEffect(() => {
    const preset = AGENT_TYPE_PRESETS[type]
    if (preset && type !== 'custom') {
      setSelectedTools(new Set(preset.recommendedTools))
      if (!systemPrompt) setSystemPrompt(preset.systemPromptHint)
      if (!description) setDescription(preset.description)
    }
  }, [type])

  const slugError = useMemo(() => {
    const s = slug.trim()
    if (!s) return 'Slug is required'
    if (s.length > SLUG_MAX) return `Slug must be ${SLUG_MAX} characters or less`
    if (!SLUG_REGEX.test(s)) return 'Only lowercase letters, numbers and hyphens are allowed'
    return ''
  }, [slug])

  // All hooks MUST be called before the early return to satisfy the
  // Rules of Hooks — React error #310 fires if the hook count varies
  // between renders (e.g. when `open` flips from false to true).
  const toast = useToast()

  const wizardFilteredPrompts = useMemo(() => {
    const q = wizardPromptSearch.toLowerCase()
    return availablePrompts.filter((p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
  }, [availablePrompts, wizardPromptSearch])

  const wizardSelectedPrompt = useMemo(
    () => availablePrompts.find((p) => p.id === selectedPromptId),
    [availablePrompts, selectedPromptId],
  )

  if (!open) return null

  const toggleTool = (toolName: string) => {
    setSelectedTools((prev) => {
      const next = new Set(prev)
      if (next.has(toolName)) next.delete(toolName)
      else next.add(toolName)
      return next
    })
  }

  const toggleKnowledge = (id: string) => {
    setSelectedKnowledge((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSaveAsPrompt = async () => {
    if (!systemPrompt.trim()) {
      setError('System prompt is empty — nothing to save')
      return
    }
    setIsSavingAsPrompt(true)
    setError(null)
    try {
      const promptSlug = `${slug.trim()}-prompt`
      const promptName = `${name.trim() || slug.trim()} Prompt`
      const created = await api.post<{ id: string }>('/admin/ai/prompts', {
        slug: promptSlug,
        name: promptName,
        description: `System prompt for ${name.trim() || slug.trim()}`,
        category: 'agent',
        content: systemPrompt.trim(),
      })
      setPromptId(created.id)
      setSelectedPromptId(created.id)
      toast.success('Prompt saved to Prompt Studio')
      api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch((err) => toast.error('Saved, but failed to refresh the template list', err instanceof Error ? err.message : String(err)))
    } catch (err) {
      toast.error('Failed to save prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsSavingAsPrompt(false)
    }
  }

  // AI generate handler — same fields and endpoint as the agent edit form
  const handleWizardGenerate = async (): Promise<void> => {
    const hasBrief = wizardGenBrief.task.trim() || wizardGenBrief.constraints.trim()
    const hasStructured = wizardGenBrief.goal.trim() || wizardGenBrief.rolePersona.trim() || wizardGenBrief.instructions.trim()
    if (!hasBrief && !hasStructured) {
      toast.error('Nothing to generate from', 'Fill in a task, constraints, or a structured field first.')
      return
    }
    setWizardIsGenerating(true)
    try {
      const toolNames = [...selectedTools]
      const res = await api.post<{ content: string }>('/admin/ai/agents/generate-prompt', {
        ...(wizardGenBrief.task.trim() ? { task: wizardGenBrief.task.trim() } : {}),
        ...(wizardGenBrief.audience.trim() ? { audience: wizardGenBrief.audience.trim() } : {}),
        tone: wizardGenBrief.tone,
        ...(wizardGenBrief.constraints.trim() ? { constraints: wizardGenBrief.constraints.trim() } : {}),
        ...(wizardGenBrief.goal.trim() ? { goal: wizardGenBrief.goal.trim() } : {}),
        ...(wizardGenBrief.rolePersona.trim() ? { rolePersona: wizardGenBrief.rolePersona.trim() } : {}),
        ...(wizardGenBrief.instructions.trim() ? { instructions: wizardGenBrief.instructions.trim() } : {}),
        agentName: name.trim() || undefined,
        agentType: type as 'routing' | 'response' | 'data' | 'translation' | 'custom',
        ...(description.trim() ? { agentDescription: description.trim() } : {}),
        toolNames,
      })
      setSystemPrompt(res.content)
      setWizardPromptSource('inline')
      setPromptId('')
      setSelectedPromptId('')
      setWizardShowGenerate(false)
      toast.success('Prompt generated', 'Review and edit the system prompt before creating.')
    } catch (err) {
      toast.error('Failed to generate prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setWizardIsGenerating(false)
    }
  }

  // AI polish handler — same endpoint and preview pattern as the agent edit form
  const handleWizardPolish = async (): Promise<void> => {
    if (!systemPrompt.trim()) {
      toast.error('Nothing to polish', 'Write or generate a system prompt first.')
      return
    }
    setWizardIsPolishing(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/agents/polish-prompt', {
        content: systemPrompt,
      })
      setWizardPolishPreview({ before: systemPrompt, after: res.content })
    } catch (err) {
      toast.error('Failed to polish prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setWizardIsPolishing(false)
    }
  }

  const handleWizardPromptSourceChange = (nextSource: WizardPromptSource): void => {
    if (nextSource === wizardPromptSource) return

    const losingInlineText = nextSource === 'template' && systemPrompt.trim().length > 0
    const losingTemplate = nextSource === 'inline' && !!selectedPromptId

    const applySwitch = (): void => {
      setWizardPromptSource(nextSource)
      if (nextSource === 'inline') {
        setPromptId('')
        setSelectedPromptId('')
      }
    }

    if (!losingInlineText && !losingTemplate) {
      applySwitch()
      return
    }

    setWizardPendingSourceChange({
      source: nextSource,
      discarding: losingInlineText
        ? 'the inline prompt text you typed'
        : `the assigned template "${availablePrompts.find((p) => p.id === selectedPromptId)?.name ?? 'template'}"`,
      apply: applySwitch,
    })
  }

  const canProceed = (): boolean => {
    switch (step) {
      case 0: return name.trim().length > 0 && slug.trim().length > 0 && !slugError
      case 1: return type.length > 0
      case 2: return true // tools are optional
      case 3: return true // knowledge is optional
      case 4: return true // prompt is optional
      case 5: return true
      default: return false
    }
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required')
      setStep(0)
      return
    }
    if (slugError) {
      setError(slugError)
      setStep(0)
      return
    }
    setSaving(true)
    setError(null)
    try {
      // 1. Create the agent
      const res = await api.post<{ agent: { id: string; slug: string } }>('/admin/ai/agents', {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
        type,
        system_prompt: systemPrompt.trim() || undefined,
        prompt_id: promptId || undefined,
        is_enabled: isEnabled,
      })

      const agentSlug = res.agent.slug

      // 2. Attach selected tools / 3. knowledge collections. The agent is
      // already created; a failed attach must not abort the flow, but it
      // must be surfaced — otherwise the user gets an agent silently missing
      // a tool or collection they picked.
      const attachFailures: string[] = []
      for (const toolName of selectedTools) {
        try {
          await api.put(`/admin/ai/agents/${agentSlug}/tools`, { toolName, isEnabled: true })
        } catch {
          attachFailures.push(`tool "${toolName}"`)
        }
      }
      for (const collectionId of selectedKnowledge) {
        try {
          await api.post(`/admin/ai/agents/${agentSlug}/knowledge`, { collectionId })
        } catch {
          attachFailures.push(`knowledge collection ${collectionId}`)
        }
      }
      if (attachFailures.length > 0) {
        toast.error(
          'Agent created, but some attachments failed',
          `Re-add them from the agent's edit screen: ${attachFailures.join(', ')}.`,
        )
      }

      onCreated()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = 'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
  const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="create-agent-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[800px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-[#3454cf]" />
            <h3 className="text-sm font-semibold text-gray-900">Create New Agent</h3>
          </div>
          <button className="rounded-sm p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex border-b border-gray-200 px-4">
          {WIZARD_STEPS.map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                step === i
                  ? 'border-[#3454cf] text-[#3454cf]'
                  : step > i
                    ? 'border-transparent text-emerald-600'
                    : 'border-transparent text-gray-500'
              }`}
            >
              {step > i ? <Check className="h-3.5 w-3.5" /> : s.icon}
              {s.label}
            </div>
          ))}
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}

          {/* Step 0: Identity */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Agent Identity</h4>
                <p className="text-xs text-gray-500">Name and describe your new AI agent.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input type="text" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Email Response Agent" />
                </div>
                <div>
                  <label className={labelClass}>Slug *</label>
                  <input
                    type="text"
                    className={`${inputClass} ${slugError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      setSlug(normalizeSlug(e.target.value))
                    }}
                    onBlur={() => setSlugTouched(true)}
                    placeholder="email-response-agent"
                    aria-invalid={!!slugError}
                    aria-describedby="slug-error"
                  />
                  {slugError && (
                    <p id="slug-error" className="mt-1 text-xs text-red-500">
                      {slugError}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this agent do? What problem does it solve?" />
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} className="h-3.5 w-3.5 rounded-sm border-gray-300 text-[#3454cf] focus:ring-[#3454cf]" />
                <span>Enable agent immediately after creation</span>
              </label>
            </div>
          )}

          {/* Step 1: Type */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Agent Type</h4>
                <p className="text-xs text-gray-500">Choose a type to pre-configure tools and behavior. You can customize everything in the next steps.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(AGENT_TYPE_PRESETS).map(([key, preset]) => {
                  const meta = AGENT_TYPE_META[key] ?? AGENT_TYPE_META['custom']!
                  const isSelected = type === key
                  return (
                    <button
                      key={key}
                      onClick={() => setType(key)}
                      className={`rounded-sm border p-4 text-left transition-all ${isSelected ? 'border-[#3454cf] bg-[#3454cf]/5 ring-1 ring-[#3454cf]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <div className={`flex h-7 w-7 items-center justify-center rounded-sm ${meta.iconBg}`}>
                          {meta.icon}
                        </div>
                        <span className="text-sm font-semibold text-gray-900 capitalize">{preset.description.split('.')[0]}</span>
                      </div>
                      <p className="text-xs leading-relaxed text-gray-500">{preset.description}</p>
                      {preset.recommendedTools.length > 0 && (
                        <p className="mt-2 text-[10px] font-medium text-gray-400">
                          {preset.recommendedTools.length} recommended tools
                        </p>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 2: Capabilities & Tools */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-gray-900">Capabilities &amp; Tools</h4>
                  <p className="text-xs text-gray-500">Select which tools this agent can use. Each tool grants a specific capability.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedTools(new Set(Object.values(TOOL_CATEGORIES).flatMap((c) => c.tools).filter((t) => !FUTURE_TOOL_INFO[t])))}
                    className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedTools(new Set())}
                    className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {Object.entries(TOOL_CATEGORIES).map(([catKey, cat]) => (
                <div key={catKey} className="rounded-sm border border-gray-200">
                  <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                    <span className="text-gray-400">{cat.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{cat.label}</span>
                    <span className="ml-auto text-[10px] text-gray-400">
                      {cat.tools.filter((t) => selectedTools.has(t)).length}/{cat.tools.length} selected
                    </span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {cat.tools.map((toolName) => {
                      const isFuture = catKey === 'Future Capabilities'
                      const futureInfo = FUTURE_TOOL_INFO[toolName]
                      const catalogEntry = toolCatalog.find((t) => t.name === toolName)
                      const isSelected = selectedTools.has(toolName)
                      const description = futureInfo?.description ?? catalogEntry?.description ?? ''
                      return (
                        <label
                          key={toolName}
                          className={`flex cursor-pointer items-start gap-3 px-3 py-2 transition-colors ${isSelected ? 'bg-[#3454cf]/5' : 'hover:bg-gray-50'} ${isFuture ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isFuture}
                            onChange={() => !isFuture && toggleTool(toolName)}
                            className="mt-0.5 h-3.5 w-3.5 rounded-sm border-gray-300 text-[#3454cf] focus:ring-[#3454cf] disabled:opacity-30"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              {futureInfo ? futureInfo.icon : <Wrench className="h-3 w-3 text-gray-400" />}
                              <span className="text-xs font-medium text-gray-800">{toolName}</span>
                              {isFuture && (
                                <span className="rounded-sm bg-amber-50 px-1 py-0.5 text-[9px] font-medium text-amber-600">
                                  PLANNED
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">{description}</p>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Knowledge Collections */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-gray-900">Knowledge Collections</h4>
                  <p className="text-xs text-gray-500">Attach knowledge bases (FAQs, SOPs, product docs) that this agent can search for answers.</p>
                </div>
                {knowledgeCollections.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedKnowledge(new Set(knowledgeCollections.map((kc) => kc.id)))}
                      className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedKnowledge(new Set())}
                      className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              {knowledgeCollections.length === 0 ? (
                <div className="rounded-sm border border-gray-200 bg-gray-50 py-8 text-center">
                  <Database className="mx-auto mb-2 h-6 w-6 text-gray-300" />
                  <p className="text-xs text-gray-500">No knowledge collections available.</p>
                  <p className="mt-1 text-[11px] text-gray-400">Create collections in the Knowledge tab first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {knowledgeCollections.map((kc) => {
                    const isSelected = selectedKnowledge.has(kc.id)
                    return (
                      <label
                        key={kc.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-sm border p-3 transition-all ${isSelected ? 'border-[#3454cf] bg-[#3454cf]/5' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleKnowledge(kc.id)}
                          className="mt-0.5 h-3.5 w-3.5 rounded-sm border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Database className="h-3 w-3 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-800">{kc.name}</span>
                            {kc.is_system && (
                              <span className="rounded-sm bg-[#3454cf]/5 px-1 py-0.5 text-[9px] font-semibold text-[#3454cf]">SYSTEM</span>
                            )}
                            <span className="text-[10px] text-gray-400">{kc.document_count} docs</span>
                          </div>
                          {kc.description && (
                            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">{kc.description}</p>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 4: System Prompt */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">System Prompt</h4>
                <p className="text-xs text-gray-500">Define the agent's behavior, role, tone, and rules.</p>
              </div>

              {/* Prompt source toggle — identical to agent edit form and orchestrator form */}
              <div>
                <label className={labelClass}>System Prompt</label>
                <div
                  role="radiogroup"
                  aria-label="Prompt source"
                  className="mb-2 grid grid-cols-2 gap-1 rounded-sm bg-gray-100 p-1"
                >
                  {([
                    { value: 'inline', label: 'Write inline', icon: <FileText className="h-3 w-3" /> },
                    { value: 'template', label: 'Use a template', icon: <Bot className="h-3 w-3" /> },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={wizardPromptSource === opt.value}
                      onClick={() => handleWizardPromptSourceChange(opt.value)}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold transition-colors ${
                        wizardPromptSource === opt.value
                          ? 'bg-white text-[#3454cf] shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Inline prompt mode */}
                {wizardPromptSource === 'inline' ? (
                  <div>
                    <div className="mb-1 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setWizardShowGenerate(!wizardShowGenerate)}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
                      >
                        <Sparkles className="h-3 w-3" />
                        {wizardShowGenerate ? 'Hide' : 'Generate with AI'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleWizardPolish()}
                        disabled={wizardIsPolishing || !systemPrompt.trim()}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline disabled:opacity-40"
                      >
                        {wizardIsPolishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        Polish with AI
                      </button>
                    </div>
                    {wizardShowGenerate && (
                      <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                        <div className="space-y-3">
                          <div>
                            <label className={labelClass}>What should this prompt do?</label>
                            <textarea
                              value={wizardGenBrief.task}
                              onChange={(e) => setWizardGenBrief((p) => ({ ...p, task: e.target.value }))}
                              rows={2}
                              className={`${inputClass} resize-none`}
                              placeholder="e.g. Answer customer questions using the knowledge base"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={labelClass}>Audience</label>
                              <input
                                type="text"
                                value={wizardGenBrief.audience}
                                onChange={(e) => setWizardGenBrief((p) => ({ ...p, audience: e.target.value }))}
                                className={inputClass}
                                placeholder="e.g. Customer support"
                              />
                            </div>
                            <div>
                              <label className={labelClass}>Tone</label>
                                                            <Select
                                ariaLabel={"Tone"}
                                size="sm"
                                value={wizardGenBrief.tone}
                                onChange={(v) => setWizardGenBrief((p) => ({ ...p, tone: v as 'professional' | 'friendly' | 'concise' | 'detailed' }))}
                              >
                                <option value="professional">Professional</option>
                                <option value="friendly">Friendly</option>
                                <option value="concise">Concise</option>
                                <option value="detailed">Detailed</option>
                                                            </Select>
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setWizardShowGenerate(false)}
                              className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleWizardGenerate()}
                              disabled={wizardIsGenerating || !wizardGenBrief.task.trim()}
                              className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                            >
                              {wizardIsGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                              Generate
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Polish preview — before/after diff with accept/reject */}
                    {wizardPolishPreview && (
                      <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3454cf]">Polish preview</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setWizardPolishPreview(null)}
                              className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSystemPrompt(wizardPolishPreview.after)
                                setWizardPolishPreview(null)
                                toast.success('Prompt polished', 'The improved prompt has been applied.')
                              }}
                              className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                            >
                              <Check className="h-3 w-3" />
                              Accept
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Before</p>
                            <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-600 whitespace-pre-wrap">{wizardPolishPreview.before}</pre>
                          </div>
                          <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">After</p>
                            <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-900 whitespace-pre-wrap">{wizardPolishPreview.after}</pre>
                          </div>
                        </div>
                      </div>
                    )}
                    <textarea
                      className={`${inputClass} font-mono`}
                      rows={6}
                      value={systemPrompt}
                      onChange={(e) => {
                        setSystemPrompt(e.target.value)
                        if (promptId) {
                          setPromptId('')
                          setSelectedPromptId('')
                        }
                      }}
                      placeholder="You are a helpful assistant that..."
                    />
                    <div className="mt-1.5 flex items-center justify-between">
                      <p className="text-[10px] text-gray-400">
                        This prompt is sent to the AI model on every interaction. Define the agent's role, tone, and rules.
                      </p>
                      <button
                        type="button"
                        onClick={handleSaveAsPrompt}
                        disabled={isSavingAsPrompt || !systemPrompt.trim()}
                        className="inline-flex items-center gap-1 rounded bg-[#3454cf] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#2a45b0] disabled:opacity-50"
                      >
                        {isSavingAsPrompt ? 'Saving…' : 'Save to Prompt Studio'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {/* Template picker — same as agent edit form and orchestrator form */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Filter prompt templates..."
                        value={wizardPromptSearch}
                        onChange={(e) => setWizardPromptSearch(e.target.value)}
                        className="w-full rounded-sm border border-gray-200 bg-white px-2.5 py-1.5 pl-8 text-xs outline-none focus:border-[#3454cf]"
                      />
                      <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                                        <Select
                      ariaLabel={"Selected Prompt ID"}
                      size="sm"
                      value={selectedPromptId}
                      onChange={(v) => {
                        const id = v
                        setSelectedPromptId(id)
                        setPromptId(id)
                        const prompt = availablePrompts.find((p) => p.id === id)
                        if (prompt) setSystemPrompt(prompt.content)
                      }}
                    >
                      <option value="">-- Select a prompt template --</option>
                      {wizardFilteredPrompts.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.slug})
                        </option>
                      ))}
                                        </Select>
                    {wizardSelectedPrompt && (
                      <div className="rounded border border-blue-100 bg-blue-50/50 p-2">
                        <div className="flex items-center justify-between text-xs text-blue-900">
                          <span>
                            Using <strong>{wizardSelectedPrompt.name}</strong>
                          </span>
                          <span className="font-mono text-[10px] text-blue-700">{wizardSelectedPrompt.slug}</span>
                        </div>
                        <pre className="mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap border-t border-blue-100 pt-1.5 font-mono text-[10px] leading-snug text-blue-900/80">
                          {wizardSelectedPrompt.content || 'This template has no content yet.'}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Review</h4>
                <p className="text-xs text-gray-500">Review all settings before creating the agent.</p>
              </div>

              {/* Review summary */}
              <div className="rounded-sm border border-gray-200 bg-gray-50/50 p-4">
                <h5 className="mb-3 text-xs font-semibold text-gray-700">Review Summary</h5>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="font-medium text-gray-800">{name || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Slug</span>
                    <span className="font-mono text-gray-700">{slug || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium text-gray-800 capitalize">{type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tools</span>
                    <span className="font-medium text-gray-800">{selectedTools.size} selected</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Knowledge</span>
                    <span className="font-medium text-gray-800">{selectedKnowledge.size} collections</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Enabled</span>
                    <span className={`font-medium ${isEnabled ? 'text-emerald-600' : 'text-gray-400'}`}>{isEnabled ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Selected tools detail */}
                {selectedTools.size > 0 && (
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Selected Tools</p>
                    <div className="flex flex-wrap gap-1">
                      {[...selectedTools].map((t) => (
                        <span key={t} className="rounded-sm bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-gray-200">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer — navigation */}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
          <button
            className="rounded-sm border border-gray-200 px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30"
            onClick={() => step > 0 ? setStep((step - 1) as WizardStep) : onClose()}
            disabled={saving}
          >
            {step === 0 ? 'Cancel' : <span className="flex items-center gap-1"><ChevronLeft className="h-3 w-3" /> Back</span>}
          </button>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">Step {step + 1} of {WIZARD_STEPS.length}</span>
            {step < 5 ? (
              <button
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-30"
                onClick={() => setStep((step + 1) as WizardStep)}
                disabled={!canProceed()}
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </button>
            ) : (
              <button
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                onClick={handleSubmit}
                disabled={saving || !name.trim() || !slug.trim() || !!slugError}
              >
                {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                Create Agent
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Prompt Source Switch Confirmation */}
      {wizardPendingSourceChange && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">
                Switch to {wizardPendingSourceChange.source === 'template' ? 'a prompt template' : 'an inline prompt'}?
              </h3>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-gray-600">
              An agent can only use one prompt source, so switching will discard{' '}
              <strong>{wizardPendingSourceChange.discarding}</strong>.
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setWizardPendingSourceChange(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  wizardPendingSourceChange.apply()
                  setWizardPendingSourceChange(null)
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Yes, switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Training panel — Q&A entries for agent training
// ---------------------------------------------------------------------------

type TrainingScope = 'agent_assist' | 'client_portal' | 'both'

const TRAINING_SCOPE_OPTIONS: { value: TrainingScope; label: string; hint: string }[] = [
  { value: 'agent_assist', label: 'Agent', hint: 'Staff agents in the workspace' },
  { value: 'client_portal', label: 'Client', hint: 'Customer-facing portal chat' },
  { value: 'both', label: 'Both', hint: 'Staff and customers' },
]
const TRAINING_SCOPE_LABEL: Record<TrainingScope, string> = {
  agent_assist: 'Agent',
  client_portal: 'Client',
  both: 'Both',
}

interface TrainingEntry {
  id: string
  question: string
  answer: string
  category: string | null
  scope: TrainingScope
  enabled: boolean
  updatedAt: string | null
}

interface TrainingDraft {
  id: string | null
  question: string
  answer: string
  category: string
  scope: TrainingScope
}

const EMPTY_TRAINING_DRAFT: TrainingDraft = { id: null, question: '', answer: '', category: '', scope: 'agent_assist' }

/** Also mounted as Agent portal → AI Config → Training. */
export function TrainingPanel(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [entries, setEntries] = useState<TrainingEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<TrainingDraft | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async (q = '') => {
    setLoading(true)
    try {
      const res = await api.get<{ entries: TrainingEntry[] }>(
        `/admin/ai/training${q ? `?search=${encodeURIComponent(q)}` : ''}`,
      )
      setEntries(res.entries ?? [])
    } catch (err) {
      toast.error('Failed to load training entries:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function save() {
    if (!draft) return
    const question = draft.question.trim()
    const answer = draft.answer.trim()
    if (question.length < 3 || answer.length === 0) return
    setSaving(true)
    try {
      const body = { question, answer, category: draft.category.trim() || null, scope: draft.scope }
      if (draft.id) await api.patch(`/admin/ai/training/${draft.id}`, body)
      else await api.post('/admin/ai/training', body)
      setDraft(null)
      await load(search)
    } catch (err) {
      toast.error('Failed to save training entry:', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function toggleEnabled(entry: TrainingEntry) {
    try {
      await api.patch(`/admin/ai/training/${entry.id}`, { enabled: !entry.enabled })
      await load(search)
    } catch (err) {
      toast.error('Failed to toggle entry:', err instanceof Error ? err.message : String(err))
    }
  }

  function remove(entry: TrainingEntry) {
    confirm({
      title: 'Delete Training Entry',
      message: `Delete this entry?\n\n"${entry.question}"`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/ai/training/${entry.id}`)
          await load(search)
        } catch (err) {
          toast.error('Failed to delete entry:', err instanceof Error ? err.message : String(err))
        }
      },
    })
  }

  const inputClass = 'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
  const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Training Q&A Entries</h3>
          <p className="text-xs text-gray-500">
            Questions and answers the AI uses when replying. Changes apply to the next reply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
            onClick={() => setDraft({ ...EMPTY_TRAINING_DRAFT })}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Entry
          </button>
        </div>
      </div>

      {/* Draft editor */}
      {draft && (
        <div className="rounded-sm border border-gray-200 bg-white p-4 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="mb-3 flex items-center gap-2">
            <Pencil className="h-3.5 w-3.5 text-[#3454cf]" />
            <h4 className="text-xs font-semibold text-gray-900">{draft.id ? 'Edit entry' : 'New entry'}</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Question</label>
              <input
                type="text"
                className={inputClass}
                value={draft.question}
                onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                placeholder="What is our refund window?"
              />
            </div>
            <div>
              <label className={labelClass}>Answer the AI should give</label>
              <textarea
                className={inputClass}
                rows={4}
                value={draft.answer}
                onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
                placeholder="Refunds are accepted within 21 days of delivery..."
              />
            </div>
            <div>
              <label className={labelClass}>Category (optional)</label>
              <input
                type="text"
                className={inputClass}
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                placeholder="Billing"
              />
            </div>
            <div>
              <label className={labelClass}>Scope</label>
              <div className="flex flex-wrap gap-2">
                {TRAINING_SCOPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDraft({ ...draft, scope: opt.value })}
                    title={opt.hint}
                    className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                      draft.scope === opt.value
                        ? 'border-[#3454cf] bg-[#3454cf]/5 text-[#3454cf]'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-gray-400">
                {TRAINING_SCOPE_OPTIONS.find((o) => o.value === draft.scope)?.hint}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                onClick={save}
                disabled={saving || draft.question.trim().length < 3 || draft.answer.trim().length === 0}
              >
                {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                {draft.id ? 'Save Changes' : 'Add Entry'}
              </button>
              <button
                className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                onClick={() => setDraft(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="flex items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 py-2 focus-within:border-[#3454cf]">
        <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(search) }}
          placeholder="Search training entries..."
          className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-gray-400"
        />
        {search && (
          <button
            onClick={() => { setSearch(''); load() }}
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Entries list */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading entries...
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-sm border border-gray-200 bg-white py-10 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <BookOpen className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">
            {search ? 'No entries match that search' : 'No training data yet'}
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-gray-400">
            {search
              ? 'The AI would not find anything for this either — consider adding an entry.'
              : 'Add the questions your customers ask most, with the answer you want the AI to give.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className={`rounded-sm border bg-white p-3.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all ${
                entry.enabled ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900">{entry.question}</p>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-gray-600">{entry.answer}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-sm bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                      {TRAINING_SCOPE_LABEL[entry.scope] ?? 'Agent'}
                    </span>
                    {entry.category && (
                      <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        {entry.category}
                      </span>
                    )}
                    {!entry.enabled && (
                      <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        DISABLED
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => toggleEnabled(entry)}
                    className="rounded-sm px-2 py-1 text-[11px] text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    title={entry.enabled ? 'Disable' : 'Enable'}
                  >
                    {entry.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => setDraft({
                      id: entry.id,
                      question: entry.question,
                      answer: entry.answer,
                      category: entry.category ?? '',
                      scope: entry.scope ?? 'agent_assist',
                    })}
                    className="flex h-7 w-7 items-center justify-center rounded-sm text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => remove(entry)}
                    className="flex h-7 w-7 items-center justify-center rounded-sm text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Knowledge collections panel
// ---------------------------------------------------------------------------

/** Document version history drawer — shows all past versions with rollback. */
function DocumentVersionDrawer({
  doc,
  isOpen,
  onClose,
  onRefresh,
}: {
  doc: Document | null
  collectionId: string
  isOpen: boolean
  onClose: () => void
  onRefresh: () => void
}): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog: ConfirmDialog } = useConfirmDialog()
  const [versions, setVersions] = useState<DocumentVersion[]>([])
  const [loading, setLoading] = useState(false)
  const [rollingBack, setRollingBack] = useState<number | null>(null)
  const versionFileRef = useRef<HTMLInputElement>(null)
  const [uploadingVersion, setUploadingVersion] = useState(false)

  const loadVersions = useCallback(async () => {
    if (!doc) return
    setLoading(true)
    try {
      const data = await api.get<{ versions: DocumentVersion[] }>(`/admin/ai/documents/${doc.id}/versions`)
      setVersions(data.versions ?? [])
    } catch (err) {
      toast.error('Failed to load version history', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [doc, toast])

  useEffect(() => {
    if (isOpen && doc) {
      void loadVersions()
    }
  }, [isOpen, doc, loadVersions])

  const handleUploadVersion = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !doc) return
    const file = e.target.files[0]
    if (!file) return
    setUploadingVersion(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { csrfToken } = useAuthStore.getState()
      const res = await fetch(`/api/admin/ai/documents/${doc.id}/versions`, {
        method: 'POST',
        headers: csrfToken ? { 'x-csrf-token': csrfToken } : {},
        body: formData,
        credentials: 'include',
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        if (res.status === 422 && body.message) {
          throw new Error(body.message)
        }
        throw new Error(body.error ?? `Upload failed: ${file.name}`)
      }
      toast.success('New version uploaded', `Version is processing and will be ready shortly.`)
      await loadVersions()
      onRefresh()
    } catch (err) {
      toast.error('Version upload failed', err instanceof Error ? err.message : String(err))
    } finally {
      setUploadingVersion(false)
      if (versionFileRef.current) versionFileRef.current.value = ''
    }
  }

  const handleRollback = (version: number) => {
    confirm({
      title: 'Rollback Document',
      message: `Roll back to version ${version}? The current version will be saved to history, and the selected version will be re-processed.`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        if (!doc) return
        setRollingBack(version)
        try {
          await api.post(`/admin/ai/documents/${doc.id}/rollback`, { version })
          toast.success('Rollback started', `Restoring version ${version}. Re-processing in background.`)
          await loadVersions()
          onRefresh()
        } catch (err) {
          toast.error('Rollback failed', err instanceof Error ? err.message : String(err))
        } finally {
          setRollingBack(null)
        }
      },
    })
  }

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (iso: string): string => {
    try {
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString()
    } catch {
      return '—'
    }
  }

  if (!isOpen || !doc) return <></>

  return (
    <>
      <ConfirmDialog />
      {/* Hidden file input for version upload */}
      <input
        ref={versionFileRef}
        type="file"
        className="hidden"
        onChange={handleUploadVersion}
        accept=".txt,.md,.pdf,.docx,.json,.csv"
      />
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
        <div
          className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Version History</h3>
              <p className="truncate text-[11px] text-gray-500">{doc.filename}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Upload new version button — hidden for seeded built-in docs;
              their content is managed through platform seeding. */}
          {!doc?.is_builtin && (
          <div className="shrink-0 border-b border-gray-100 px-5 py-3">
            <button
              onClick={() => versionFileRef.current?.click()}
              disabled={uploadingVersion}
              className="flex w-full items-center justify-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-2 text-xs font-medium text-white hover:bg-[#2a44b8] disabled:opacity-50"
            >
              {uploadingVersion ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Upload New Version
            </button>
            <p className="mt-1.5 text-center text-[10px] text-gray-400">
              Replaces the current file. The old version is saved to history automatically.
            </p>
          </div>
          )}

          {/* Version list */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading versions...
              </div>
            ) : versions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <History className="mb-2 h-8 w-8 text-gray-300" />
                <p className="text-sm font-medium text-gray-600">No version history yet</p>
                <p className="mt-1 text-xs text-gray-400">
                  Upload a new version to start tracking changes to this document.
                </p>
              </div>
            ) : (
              <div className={`flex flex-col gap-3 ${versions.length > 5 ? 'max-h-full overflow-y-auto pr-1 thin-scrollbar' : ''}`}>
                {versions.map((v) => {
                  const isCurrent = v.version === doc.current_version
                  return (
                    <div
                      key={v.id}
                      className={`rounded-xl border p-3 text-xs ${
                        isCurrent ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">Version {v.version}</span>
                            {isCurrent && (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500">
                            <Clock className="h-3 w-3" />
                            {formatDate(v.created_at)}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-gray-400">
                            <span>{formatBytes(v.size_bytes)}</span>
                            <span>•</span>
                            <span>{v.chunk_count} chunks</span>
                            <span>•</span>
                            <span className="truncate">{v.filename}</span>
                          </div>
                          {v.notes && <p className="mt-1 text-gray-600">{v.notes}</p>}
                        </div>
                        {!isCurrent && (
                          <button
                            onClick={() => handleRollback(v.version)}
                            disabled={rollingBack !== null}
                            className="flex shrink-0 items-center gap-1 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[10px] font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
                          >
                            {rollingBack === v.version ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3" />
                            )}
                            Rollback
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-gray-200 px-5 py-3">
            <button
              onClick={onClose}
              className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

/** Also mounted as Agent portal → AI Config → Knowledge Base. */
export function KnowledgePanel(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog: ConfirmDialog } = useConfirmDialog()
  const [collections, setCollections] = useState<KnowledgeCollection[]>([])
  const [documents, setDocuments] = useState<Record<string, Document[]>>({})
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [expandedCollection, setExpandedCollection] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'system' | 'custom'>('all')
  const [sortBy, setSortBy] = useState<'name' | 'docs' | 'created'>('created')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null)
  const [reprocessingDoc, setReprocessingDoc] = useState<string | null>(null)
  const [deletingCollection, setDeletingCollection] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newCollection, setNewCollection] = useState({ name: '', slug: '', description: '' })
  const [creating, setCreating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadTarget, setUploadTarget] = useState<string | null>(null)
  const [versionDrawerDoc, setVersionDrawerDoc] = useState<Document | null>(null)
  const [versionDrawerCollection, setVersionDrawerCollection] = useState<string>('')
  const [docVersions, setDocVersions] = useState<Record<string, DocumentVersion[]>>({})
  const [inlineRollbackDoc, setInlineRollbackDoc] = useState<string | null>(null)
  const [viewingDoc, setViewingDoc] = useState<Document | null>(null)

  const loadCollections = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.get<{ collections: KnowledgeCollection[] }>('/admin/ai/knowledge-collections')
      setCollections(data.collections ?? [])
    } catch (err) {
      toast.error('Failed to load collections', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  const loadDocuments = useCallback(async (collectionId: string): Promise<Document[]> => {
    try {
      const data = await api.get<{ documents: Document[] }>(`/admin/ai/knowledge-collections/${collectionId}/documents`)
      const docs = data.documents ?? []
      setDocuments((prev) => ({ ...prev, [collectionId]: docs }))
      return docs
    } catch (err) {
      toast.error('Failed to load documents', err instanceof Error ? err.message : String(err))
      return []
    }
  }, [toast])

  const loadDocVersions = useCallback(async (docId: string) => {
    try {
      const data = await api.get<{ versions: DocumentVersion[] }>(`/admin/ai/documents/${docId}/versions`)
      setDocVersions((prev) => ({ ...prev, [docId]: data.versions ?? [] }))
    } catch (err) {
      toast.error('Failed to load versions', err instanceof Error ? err.message : String(err))
    }
  }, [toast])

  const handleInlineRollback = (docId: string, collectionId: string, version: number) => {
    confirm({
      title: 'Rollback Document',
      message: `Roll back to version ${version}? The current version will be saved to history, and the selected version will be re-processed.`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        setInlineRollbackDoc(docId)
        try {
          await api.post(`/admin/ai/documents/${docId}/rollback`, { version })
          toast.success('Rollback started', `Restoring version ${version}. Re-processing in background.`)
          await loadDocVersions(docId)
          await loadDocuments(collectionId)
          await loadCollections()
        } catch (err) {
          toast.error('Rollback failed', err instanceof Error ? err.message : String(err))
        } finally {
          setInlineRollbackDoc(null)
        }
      },
    })
  }

  const handleDeleteCollection = (kc: KnowledgeCollection) => {
    confirm({
      title: 'Delete Collection',
      message: `Delete "${kc.name}"? This permanently removes the collection and all ${kc.document_count} document${kc.document_count === 1 ? '' : 's'} inside it, including their files and version history. Agents using this collection will lose access to its knowledge.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        setDeletingCollection(kc.id)
        try {
          await api.delete(`/admin/ai/knowledge-collections/${kc.id}`)
          toast.success('Collection deleted', `"${kc.name}" was removed.`)
          if (expandedCollection === kc.id) setExpandedCollection(null)
          await loadCollections()
        } catch (err) {
          toast.error('Failed to delete collection', err instanceof Error ? err.message : String(err))
        } finally {
          setDeletingCollection(null)
        }
      },
    })
  }

  useEffect(() => {
    void loadCollections()
  }, [loadCollections])

  useEffect(() => {
    if (expandedCollection && documents[expandedCollection] === undefined) {
      void loadDocuments(expandedCollection)
    }
  }, [expandedCollection, documents, loadDocuments])

  // Load versions for all documents when a collection is expanded
  useEffect(() => {
    if (expandedCollection && documents[expandedCollection]) {
      for (const doc of documents[expandedCollection]) {
        if (docVersions[doc.id] === undefined) {
          void loadDocVersions(doc.id)
        }
      }
    }
  }, [expandedCollection, documents, docVersions, loadDocVersions])

  // Poll for status while any document in the expanded collection is still
  // being processed. Document processing (extract → chunk → embed) runs
  // async on the server, so without this the row stays on "processing" until
  // the user manually refreshes. Stops as soon as every doc is terminal
  // (ready / failed).
  useEffect(() => {
    if (!expandedCollection) return
    const docs = documents[expandedCollection]
    if (!docs) return
    const hasPending = docs.some(
      (d) => d.status === 'uploading' || d.status === 'processing',
    )
    if (!hasPending) return
    const timer = setInterval(() => {
      void loadDocuments(expandedCollection)
      void loadCollections() // keeps the collection-row doc count / version fresh
    }, 4000)
    return () => clearInterval(timer)
  }, [expandedCollection, documents, loadDocuments, loadCollections])

  const formatDate = (iso: string): string => {
    try {
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString()
    } catch {
      return '—'
    }
  }

  const filteredCollections = useMemo(() => {
    const q = search.trim().toLowerCase()
    let filtered = collections
    if (q) {
      filtered = filtered.filter(
        (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
      )
    }
    if (typeFilter === 'system') {
      filtered = filtered.filter((c) => c.is_system)
    } else if (typeFilter === 'custom') {
      filtered = filtered.filter((c) => !c.is_system)
    }
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'docs':
          return b.document_count - a.document_count
        case 'created':
          return b.created_at.localeCompare(a.created_at)
        case 'name':
        default:
          return a.name.localeCompare(b.name)
      }
    })
  }, [collections, search, typeFilter, sortBy])

  useEffect(() => {
    setPage(1)
  }, [search, typeFilter, sortBy])

  const totalCount = filteredCollections.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginatedCollections = filteredCollections.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endIdx = Math.min(currentPage * pageSize, totalCount)

  const systemCount = collections.filter((c) => c.is_system).length
  const customCount = collections.filter((c) => !c.is_system).length

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !uploadTarget) return
    setUploading(true)
    const rejectedFiles: string[] = []
    try {
      for (const file of Array.from(e.target.files)) {
        const formData = new FormData()
        formData.append('file', file)
        const { csrfToken } = useAuthStore.getState()
        const res = await fetch(`/api/admin/ai/knowledge-collections/${uploadTarget}/documents`, {
          method: 'POST',
          headers: csrfToken ? { 'x-csrf-token': csrfToken } : {},
          body: formData,
          credentials: 'include',
        })
        if (!res.ok) {
          // Parse the rejection response to get the specific, actionable message
          const body = await res.json().catch(() => ({}))
          // 415 = unsupported type, 422 = unreadable content, 400 = e.g.
          // system-collection restriction — all carry an actionable message.
          if ((res.status === 415 || res.status === 422 || res.status === 400) && body.message) {
            rejectedFiles.push(`${file.name}: ${body.message}`)
          } else {
            throw new Error(`Upload failed: ${file.name}`)
          }
        }
      }
      await loadDocuments(uploadTarget)
      await loadCollections()
      const succeeded = e.target.files.length - rejectedFiles.length
      if (succeeded > 0) {
        toast.success('Upload complete', `${succeeded} file(s) uploaded`)
      }
      if (rejectedFiles.length > 0) {
        toast.error(
          `${rejectedFiles.length} file(s) rejected`,
          rejectedFiles.join('\n'),
        )
      }
    } catch (err) {
      toast.error('Upload failed', err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
      setUploadTarget(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDeleteDoc = (docId: string, collectionId: string) => {
    confirm({
      title: 'Delete Document',
      message: 'Delete this document? This will remove all chunks from the vector store. This action cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        setDeletingDoc(docId)
        try {
          await api.delete(`/admin/ai/documents/${docId}`)
          await loadDocuments(collectionId)
          await loadCollections()
          toast.success('Document deleted')
        } catch (err) {
          toast.error('Delete failed', err instanceof Error ? err.message : String(err))
        } finally {
          setDeletingDoc(null)
        }
      },
    })
  }

  const handleReprocess = async (docId: string, collectionId: string) => {
    setReprocessingDoc(docId)
    try {
      await api.post(`/admin/ai/documents/${docId}/reprocess`, {})
      await loadDocuments(collectionId)
      toast.success('Reprocessing started', 'The document will be re-chunked and embedded.')
    } catch (err) {
      toast.error('Reprocess failed', err instanceof Error ? err.message : String(err))
    } finally {
      setReprocessingDoc(null)
    }
  }

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim() || !newCollection.slug.trim()) return
    setCreating(true)
    try {
      await api.post('/admin/ai/knowledge-collections', {
        name: newCollection.name.trim(),
        slug: newCollection.slug.trim(),
        description: newCollection.description.trim() || undefined,
      })
      toast.success('Collection created', `${newCollection.name} is ready for documents`)
      setShowCreate(false)
      setNewCollection({ name: '', slug: '', description: '' })
      await loadCollections()
    } catch (err) {
      toast.error('Failed to create collection', err instanceof Error ? err.message : String(err))
    } finally {
      setCreating(false)
    }
  }

  const statusIcon = (status: string): JSX.Element => {
    if (status === 'ready') return <CircleCheck className="h-3.5 w-3.5 text-emerald-500" />
    if (status === 'failed') return <CircleX className="h-3.5 w-3.5 text-red-500" />
    return <Clock className="h-3.5 w-3.5 text-amber-500" />
  }

  const statusBadge = (status: string): string => {
    if (status === 'ready') return 'bg-emerald-50 text-emerald-600'
    if (status === 'failed') return 'bg-red-50 text-red-600'
    return 'bg-amber-50 text-amber-600'
  }

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatTimestamp = (iso: string): string => {
    try {
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleDateString()
    } catch {
      return '—'
    }
  }

  return (
    <div className="space-y-4">
      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleUpload}
        // Extension hints for the OS picker. The server canonicalises the
        // type from the filename (browser MIME types are unreliable) and
        // rejects anything unsupported with a 415.
        accept=".txt,.text,.md,.markdown,.csv,.json,.pdf,.doc,.docx,.html,.htm,.xls,.xlsx"
      />

      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Knowledge Collections</h3>
          <p className="text-xs text-gray-500">
            {collections.length} collections ({systemCount} system, {customCount} custom). Upload documents to build searchable knowledge bases for AI agents.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a44b8]"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Collection
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 py-2 focus-within:border-[#3454cf]">
          <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search collections by name, slug, or description..."
            className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-gray-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
                <Select
          ariaLabel={"Type filter"}
          size="sm"
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as 'all' | 'system' | 'custom')}
        >
          <option value="all">All Types</option>
          <option value="system">System Only</option>
          <option value="custom">Custom Only</option>
                </Select>
                <Select
          ariaLabel={"Sort By"}
          size="sm"
          value={sortBy}
          onChange={(v) => setSortBy(v as 'name' | 'docs' | 'created')}
        >
          <option value="name">Sort: Name</option>
          <option value="docs">Sort: Document Count</option>
          <option value="created">Sort: Created</option>
                </Select>
        {(search || typeFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setTypeFilter('all') }}
            className="flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <X className="h-3 w-3" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Collections table */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading collections...
        </div>
      ) : filteredCollections.length === 0 ? (
        <div className="rounded-sm border border-gray-200 bg-white py-12 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          {collections.length === 0 ? (
            <>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#3454cf]/10">
                <Database className="h-6 w-6 text-[#3454cf]" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">No knowledge collections yet</h4>
              <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
                Collections group documents (FAQs, SOPs, product manuals) into searchable knowledge bases that AI agents can query during conversations.
              </p>
              <button
                onClick={() => setShowCreate(true)}
                className="mx-auto mt-4 flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-medium text-white hover:bg-[#2a44b8]"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Your First Collection
              </button>
            </>
          ) : (
            <>
              <Database className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No collections match your filters</p>
              <button
                onClick={() => { setSearch(''); setTypeFilter('all') }}
                className="mt-2 text-xs font-medium text-[#3454cf] hover:underline"
              >
                Clear all filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-gray-200 bg-gray-50/50">
              <tr>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy('name')} className="flex items-center gap-1 hover:text-gray-700">
                    Name
                    {sortBy === 'name' && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Description</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Type</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy('docs')} className="flex items-center gap-1 hover:text-gray-700">
                    Documents
                    {sortBy === 'docs' && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Versions</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy('created')} className="flex items-center gap-1 hover:text-gray-700">
                    Created
                    {sortBy === 'created' && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 text-right font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginatedCollections.map((kc) => {
                const isExpanded = expandedCollection === kc.id
                const docs = documents[kc.id] ?? []
                const readyCount = docs.filter((d) => d.status === 'ready').length
                return (
                  <React.Fragment key={kc.id}>
                    <tr className={`transition-colors ${isExpanded ? 'bg-gray-50/50' : 'hover:bg-gray-50/30'}`}>
                      {/* Name */}
                      <td className="px-4 py-3">
                        <button
                          className="flex items-center gap-1.5 text-left"
                          onClick={() => setExpandedCollection(isExpanded ? null : kc.id)}
                        >
                          <Database className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          <span className="font-semibold text-gray-900">{kc.name}</span>
                          {kc.is_system && (
                            <span className="rounded-sm bg-blue-50 px-1 py-0.5 text-[9px] font-medium text-blue-600">SYSTEM</span>
                          )}
                        </button>
                      </td>
                      {/* Description */}
                      <td className="max-w-xs px-4 py-3">
                        <p className="truncate text-[11px] leading-relaxed text-gray-500" title={kc.description}>
                          {kc.description || '—'}
                        </p>
                      </td>
                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${
                          kc.is_system ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                        }`}>
                          {kc.is_system ? 'System' : 'Custom'}
                        </span>
                      </td>
                      {/* Documents */}
                      <td className="px-4 py-3">
                        <span className="font-mono text-[11px] font-medium text-gray-700">{kc.document_count}</span>
                        {isExpanded && docs.length > 0 && (
                          <span className="ml-1.5 text-[10px] text-gray-400">
                            ({readyCount} ready)
                          </span>
                        )}
                      </td>
                      {/* Versions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600" title="Latest version across all documents in this collection">
                            v{kc.latest_version ?? 1}
                          </span>
                          {(kc.version_count ?? 0) > 0 && (
                            <span className="text-[10px] text-gray-400" title="Total version history entries across all documents">
                              ({kc.version_count} {kc.version_count === 1 ? 'version' : 'versions'})
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Created */}
                      <td className="px-4 py-3">
                        <span className="text-[10px] text-gray-400">{formatTimestamp(kc.created_at)}</span>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setUploadTarget(kc.id)
                              fileInputRef.current?.click()
                            }}
                            disabled={uploading}
                            className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-[#3454cf] disabled:cursor-not-allowed disabled:opacity-30"
                            title="Upload documents"
                          >
                            {uploading && uploadTarget === kc.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Upload className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => setExpandedCollection(isExpanded ? null : kc.id)}
                            className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            title={isExpanded ? 'Collapse' : 'Expand documents'}
                          >
                            {isExpanded ? <ChevronRight className="h-3.5 w-3.5 rotate-90" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                          {!kc.is_system && (
                            <button
                              onClick={() => handleDeleteCollection(kc)}
                              disabled={deletingCollection === kc.id}
                              className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                              title="Delete collection"
                            >
                              {deletingCollection === kc.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded documents row */}
                    {isExpanded && (
                      <tr className="bg-gray-50/50">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="ml-5 space-y-3 border-l border-gray-200 pl-4">
                            {/* Upload bar */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setUploadTarget(kc.id)
                                  fileInputRef.current?.click()
                                }}
                                disabled={uploading}
                                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a44b8] disabled:opacity-50"
                              >
                                {uploading && uploadTarget === kc.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Upload className="h-3 w-3" />
                                )}
                                {uploading && uploadTarget === kc.id ? 'Checking & Uploading…' : 'Upload Files'}
                              </button>
                              <span className="text-[11px] text-gray-400">TXT, MD, PDF, DOCX, JSON, CSV</span>
                            </div>

                            {/* Documents list */}
                            {docs.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-6 text-center">
                                <FileText className="mb-2 h-6 w-6 text-gray-200" />
                                <p className="text-[11px] text-gray-400">No documents yet. Upload files to build this knowledge base.</p>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                {docs.map((doc) => (
                                  <React.Fragment key={doc.id}>
                                  <div
                                    className="flex items-center justify-between rounded-sm border border-gray-100 bg-white px-3 py-2 text-xs transition-colors hover:border-gray-200"
                                  >
                                    <div className="flex items-center gap-2.5">
                                      {statusIcon(doc.status)}
                                      <span className="font-medium text-gray-900">{doc.filename}</span>
                                      {/* Version badge — always visible, like prompts */}
                                      <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600" title="Current version">
                                        v{doc.current_version ?? 1}
                                      </span>
                                      {(doc.version_count ?? 0) > 1 && (
                                        <span className="text-[10px] text-gray-400" title="Total versions in history">
                                          {doc.version_count} versions
                                        </span>
                                      )}
                                      <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${statusBadge(doc.status)}`}>
                                        {doc.status}
                                      </span>
                                      {doc.chunk_count > 0 && (
                                        <span className="text-gray-400">{doc.chunk_count} chunks</span>
                                      )}
                                      <span className="text-[10px] text-gray-400">{formatBytes(doc.size_bytes)}</span>
                                      {doc.processing_error && (
                                        <span className="text-[10px] text-red-500" title={doc.processing_error}>
                                          ⚠ {doc.processing_error.slice(0, 50)}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                      <button
                                        className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#3454cf]"
                                        onClick={() => setViewingDoc(doc)}
                                        title="View document"
                                      >
                                        <Eye className="h-3 w-3" />
                                      </button>
                                      <button
                                        className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#3454cf]"
                                        onClick={() => {
                                          setVersionDrawerDoc(doc)
                                          setVersionDrawerCollection(kc.id)
                                        }}
                                        title="Version history"
                                      >
                                        <History className="h-3 w-3" />
                                      </button>
                                      <button
                                        className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#3454cf] disabled:opacity-50"
                                        onClick={() => void handleReprocess(doc.id, kc.id)}
                                        disabled={reprocessingDoc === doc.id}
                                        title="Re-process"
                                      >
                                        {reprocessingDoc === doc.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <RefreshCw className="h-3 w-3" />
                                        )}
                                      </button>
                                      {/* Only the two SEEDED portal-FAQ docs are
                                          locked (server also rejects with 400).
                                          Anything an admin uploaded — including
                                          into a system collection — can be
                                          deleted by them. */}
                                      {!doc.is_builtin && (
                                      <button
                                        className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                        onClick={() => handleDeleteDoc(doc.id, kc.id)}
                                        disabled={deletingDoc === doc.id}
                                        title="Delete"
                                      >
                                        {deletingDoc === doc.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-3 w-3" />
                                        )}
                                      </button>
                                      )}
                                    </div>
                                  </div>
                                  {(() => {
                                    const versions = docVersions[doc.id]
                                    if (!versions || versions.length === 0) return null
                                    return (
                                      <div className="ml-9 mt-1 mb-1">
                                        <div className="mb-1 flex items-center gap-1">
                                          <Clock className="h-3 w-3 text-gray-400" />
                                          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                            Version History ({versions.length})
                                          </span>
                                        </div>
                                        <div className={`space-y-1 ${versions.length > 5 ? 'max-h-40 overflow-y-auto thin-scrollbar' : ''}`}>
                                          {versions
                                            .slice()
                                            .sort((a, b) => b.version - a.version)
                                            .map((v) => {
                                              const isCurrent = v.version === doc.current_version
                                              return (
                                                <div
                                                  key={v.id}
                                                  className="flex items-center gap-2 rounded-sm border border-gray-100 bg-white px-2 py-1.5"
                                                >
                                                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-600">
                                                    v{v.version}
                                                  </span>
                                                  <div className="min-w-0 flex-1">
                                                    <p className="truncate text-[10px] text-gray-600">
                                                      {v.notes || 'No notes'}
                                                    </p>
                                                    <p className="text-[9px] text-gray-400">
                                                      {formatDate(v.created_at)}
                                                    </p>
                                                  </div>
                                                  {isCurrent ? (
                                                    <span className="shrink-0 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600">
                                                      Active
                                                    </span>
                                                  ) : (
                                                    <button
                                                      onClick={() => handleInlineRollback(doc.id, kc.id, v.version)}
                                                      disabled={inlineRollbackDoc === doc.id}
                                                      className="shrink-0 rounded-sm border border-gray-200 px-2 py-0.5 text-[9px] font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
                                                    >
                                                      {inlineRollbackDoc === doc.id ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                      ) : (
                                                        'Rollback'
                                                      )}
                                                    </button>
                                                  )}
                                                </div>
                                              )
                                            })}
                                        </div>
                                      </div>
                                    )
                                  })()}
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between rounded-sm border border-gray-200 bg-white px-4 py-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500">
              Showing <span className="font-medium text-gray-700">{startIdx}-{endIdx}</span> of <span className="font-medium text-gray-700">{totalCount}</span> collection{totalCount !== 1 ? 's' : ''}
            </span>
                        <Select
              ariaLabel={"Page Size"}
              size="sm"
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
            >
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
                        </Select>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="First page"
              >
                «
              </button>
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-3 w-3" />
                Prev
              </button>
              {(() => {
                const pages: number[] = []
                const maxButtons = 5
                let start = Math.max(1, currentPage - Math.floor(maxButtons / 2))
                const end = Math.min(totalPages, start + maxButtons - 1)
                start = Math.max(1, end - maxButtons + 1)
                for (let i = start; i <= end; i++) pages.push(i)
                return pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`h-7 w-7 rounded-sm text-[11px] font-medium ${
                      p === currentPage
                        ? 'bg-[#3454cf] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))
              })()}
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="Last page"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create collection dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-sm font-bold text-gray-900">Create Knowledge Collection</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Name *</label>
                <input
                  value={newCollection.name}
                  onChange={(e) => setNewCollection((prev) => ({
                    ...prev,
                    name: e.target.value,
                    slug: prev.slug || e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, ''),
                  }))}
                  placeholder="e.g. Product Manuals"
                  className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Slug *</label>
                <input
                  value={newCollection.slug}
                  onChange={(e) => setNewCollection((prev) => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, ''),
                  }))}
                  placeholder="product-manuals"
                  className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-mono text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Description</label>
                <textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="What kind of documents will this collection contain?"
                  className="w-full resize-none rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-sm bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleCreateCollection()}
                disabled={creating || !newCollection.name.trim() || !newCollection.slug.trim()}
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2a44b8] disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Create Collection
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog />

      {/* Document version history drawer */}
      <DocumentVersionDrawer
        doc={versionDrawerDoc}
        collectionId={versionDrawerCollection}
        isOpen={versionDrawerDoc !== null}
        onClose={() => setVersionDrawerDoc(null)}
        onRefresh={() => {
          if (versionDrawerCollection) {
            void loadDocuments(versionDrawerCollection).then((docs) => {
              // Update the drawer's doc reference so the "Active" badge
              // reflects the new current_version after upload/rollback.
              if (versionDrawerDoc) {
                const updated = docs.find((d) => d.id === versionDrawerDoc.id)
                if (updated) setVersionDrawerDoc(updated)
              }
            })
          }
          void loadCollections()
        }}
      />

      {/* Document viewer — inline preview / download of the current file */}
      <DocumentViewer doc={viewingDoc} onClose={() => setViewingDoc(null)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chat panel
// ---------------------------------------------------------------------------

function ChatPanel(): JSX.Element {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [progress, setProgress] = useState<string[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const speechBaseRef = useRef('')
  const { listening, supported: micSupported, toggle: toggleMic, stop: stopMic } = useSpeechRecognition({
    onResult: (transcript, isFinal) => {
      if (isFinal) {
        speechBaseRef.current = speechBaseRef.current + transcript + ' '
        setInput(speechBaseRef.current)
      } else {
        setInput(speechBaseRef.current + transcript)
      }
    },
  })

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, progress])

  const handleSend = async () => {
    if (!input.trim() || streaming) return
    const userMsg: ChatMessage = { id: Date.now(), role: 'user', content: input.trim() }
    stopMic()
    speechBaseRef.current = ''
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setStreaming(true)
    setProgress([])

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const chatHistory = messages
        .filter((m) => m.content)
        .slice(-8)
        .map((m) => ({ role: m.role, content: m.content }))
      const res = await apiPostStream('/admin/ai/agents/chat/stream', {
        message: userMsg.content,
        ...(chatHistory.length > 0 ? { chatHistory } : {}),
      }, controller.signal)

      const reader = res.body?.getReader()
      if (!reader) return
      const decoder = new TextDecoder()
      let buffer = ''
      let assistantContent = ''
      let assistantIntent: string | undefined
      let assistantAgent: string | undefined
      let assistantFiles: ChatFile[] = []
      const progressSteps: string[] = []

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6)) as Record<string, unknown>
            if (eventType === 'planning' || eventType === 'tool_start' || eventType === 'tool_complete' || eventType === 'tool_error' || eventType === 'generating') {
              const msg = typeof data.message === 'string' ? data.message : eventType
              progressSteps.push(msg)
              setProgress([...progressSteps])
            } else if (eventType === 'file') {
              // File metadata event — actual content arrives in 'done'
            } else if (eventType === 'done') {
              assistantContent = typeof data.content === 'string' ? data.content : ''
              assistantIntent = typeof data.intent === 'string' ? data.intent : undefined
              assistantAgent = typeof data.selectedAgent === 'string' ? data.selectedAgent : undefined
              // Extract files from the done event
              if (Array.isArray(data.files)) {
                assistantFiles = (data.files as Array<Record<string, unknown>>).map((f) => ({
                  filename: String(f.filename ?? ''),
                  contentType: String(f.contentType ?? 'application/octet-stream'),
                  bufferBase64: String(f.bufferBase64 ?? ''),
                  sizeBytes: Number(f.sizeBytes ?? 0),
                })).filter((f) => f.filename && f.bufferBase64)
              }
            } else if (eventType === 'error') {
              assistantContent = `Error: ${typeof data.error === 'string' ? data.error : 'Unknown error'}`
            }
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: assistantContent || 'No response received.',
          ...(assistantIntent ? { intent: assistantIntent } : {}),
          ...(assistantAgent ? { selectedAgent: assistantAgent } : {}),
          ...(progressSteps.length > 0 ? { progress: progressSteps } : {}),
          ...(assistantFiles.length > 0 ? { files: assistantFiles } : {}),
        },
      ])
      setProgress([])
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: `Error: ${err instanceof Error ? err.message : String(err)}`, failed: true },
      ])
    } finally {
      setStreaming(false)
      abortRef.current = null
    }
  }

  const handleStop = () => {
    abortRef.current?.abort()
    setStreaming(false)
  }

  return (
    <div className="flex h-[28rem] flex-col rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <MessageSquare className="h-4 w-4 text-[#3454cf]" />
        <h3 className="text-sm font-semibold text-gray-900">Orchestrator Chat</h3>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-gray-50/30 p-4">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Bot className="mb-2 h-10 w-10 text-gray-200" />
            <p className="text-xs text-gray-400">Ask a question to test the multi-agent orchestrator...</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-sm px-3 py-2 text-xs ${msg.role === 'user' ? 'bg-[#3454cf] text-white' : 'border border-gray-200 bg-white text-gray-700'}`}>
              {msg.selectedAgent && (
                <div className="mb-1 flex items-center gap-1 text-[10px] opacity-70">
                  <Bot className="h-2.5 w-2.5" /> {msg.selectedAgent}
                </div>
              )}
              {msg.intent && (
                <div className="mb-1 text-[10px] opacity-70">Intent: {msg.intent}</div>
              )}
              <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
              {msg.files && msg.files.length > 0 && (
                <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
                  {msg.files.map((file, i) => {
                    const blob = new Blob(
                      [Uint8Array.from(atob(file.bufferBase64), (c) => c.charCodeAt(0))],
                      { type: file.contentType },
                    )
                    const url = URL.createObjectURL(blob)
                    return (
                      <a
                        key={i}
                        href={url}
                        download={file.filename}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[11px] font-medium text-[#3454cf] hover:bg-blue-50"
                      >
                        <Download className="h-3 w-3" />
                        {file.filename}
                        <span className="text-gray-400">
                          ({file.sizeBytes > 1024 ? `${(file.sizeBytes / 1024).toFixed(1)} KB` : `${file.sizeBytes} B`})
                        </span>
                      </a>
                    )
                  })}
                </div>
              )}
              {msg.progress && msg.progress.length > 0 && (
                <div className="mt-1.5 space-y-0.5 border-t border-gray-100 pt-1.5 text-[10px] opacity-60">
                  {msg.progress.map((p, i) => (
                    <div key={i}>• {p}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {streaming && progress.length > 0 && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-sm border border-gray-200 bg-white px-3 py-2 text-xs">
              {progress.map((p, i) => (
                <div key={i} className="flex items-center gap-1.5 text-gray-500">
                  <Loader2 className="h-2.5 w-2.5 animate-spin text-[#3454cf]" /> {p}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 p-3">
        <input
          className="flex-1 rounded-sm border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
          value={input}
          onChange={(e) => {
            setInput(e.target.value)
            if (e.target.value === '') speechBaseRef.current = ''
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={listening ? 'Listening…' : 'Ask the orchestrator...'}
          disabled={streaming}
        />
        {micSupported && !streaming && (
          <button
            onClick={toggleMic}
            className={`inline-flex items-center justify-center rounded-sm p-2 transition-colors ${
              listening
                ? 'bg-red-100 text-red-600 animate-pulse'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
            }`}
            title={listening ? 'Stop listening' : 'Voice input'}
          >
            <Mic className="h-3.5 w-3.5" />
          </button>
        )}
        {streaming ? (
          <button className="inline-flex items-center gap-1 rounded-sm bg-red-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-600" onClick={handleStop}>
            Stop
          </button>
        ) : (
          <button className="inline-flex items-center justify-center rounded-sm bg-[#3454cf] p-2 text-white transition-colors hover:bg-[#2a45b0]" onClick={handleSend}>
            <Send className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tools panel — tool catalog table with Create / Edit / Delete
// ---------------------------------------------------------------------------

/** Reverse-lookup map: tool name → list of agent slugs that have it attached. */
function buildToolUsageMap(agents: Agent[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const agent of agents) {
    for (const tool of agent.tools) {
      const existing = map.get(tool.tool_name)
      if (existing) existing.push(agent.slug)
      else map.set(tool.tool_name, [agent.slug])
    }
  }
  return map
}

/** Extract required property names from a JSON-schema input schema. */
function getRequiredParams(schema: Record<string, unknown>): string[] {
  const required = schema['required']
  return Array.isArray(required) ? required.filter((r): r is string => typeof r === 'string') : []
}

/** Extract all property names from a JSON-schema input schema. */
function getAllParams(schema: Record<string, unknown>): string[] {
  const props = schema['properties']
  if (!props || typeof props !== 'object') return []
  return Object.keys(props)
}

const HANDLER_TYPE_LABELS: Record<string, string> = {
  metadata_only: 'Metadata Only',
  database_read_template: 'DB Read',
  report_template: 'Report',
  internal_api: 'Internal API',
  knowledge_search: 'Knowledge Search',
  queue_export: 'Queue Export',
  lookup_query: 'Lookup Query',
  notification_send: 'Notification',
  conversation_action: 'Conversation Action',
  web_scraper: 'Web Scraper',
  custom_code: 'Custom Code',
}

// ── Allowed tables for DB read handler (from SCHEMA_CATALOG) ───────────────

// ── Schema catalog types (fetched dynamically from API) ───────────────────

interface CatalogColumn {
  name: string
  type: string
  description?: string
  filterable: boolean
  groupable: boolean
  aggregatable: boolean
}

interface CatalogTable {
  name: string
  label: string
  description: string
  columns: CatalogColumn[]
}

/** Hook: fetch the schema catalog from the API (tables + columns). */
function useSchemaCatalog(): { tables: CatalogTable[]; loading: boolean } {
  const toast = useToast()
  const [tables, setTables] = useState<CatalogTable[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api
      .get<{ tables: CatalogTable[] }>('/admin/ai/tools/schema-catalog')
      .then((res) => {
        if (!cancelled) setTables(res.tables ?? [])
      })
      .catch((err) => toast.error('Failed to load schema catalog:', err instanceof Error ? err.message : String(err)))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { tables, loading }
}

// ── Internal API route catalog types (fetched dynamically) ────────────────

interface InternalRoute {
  method: string
  route: string
  label: string
}

/** Hook: fetch the approved internal API routes from the API. */
function useInternalRoutes(): { routes: InternalRoute[]; loading: boolean } {
  const toast = useToast()
  const [routes, setRoutes] = useState<InternalRoute[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api
      .get<{ routes: InternalRoute[] }>('/admin/ai/tools/internal-routes')
      .then((res) => {
        if (!cancelled) setRoutes(res.routes ?? [])
      })
      .catch((err) => toast.error('Failed to load internal routes:', err instanceof Error ? err.message : String(err)))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { routes, loading }
}

// ── Reusable searchable table select (for 248+ table dropdowns) ───────────

function SearchableTableSelect({
  value,
  onChange,
  tables,
  loading,
  placeholder,
  required,
}: {
  value: string
  onChange: (name: string) => void
  tables: CatalogTable[]
  loading: boolean
  placeholder?: string
  required?: boolean
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const selected = tables.find((t) => t.name === value)
  const filtered = query
    ? tables.filter(
        (t) =>
          t.name.toLowerCase().includes(query.toLowerCase()) ||
          t.label.toLowerCase().includes(query.toLowerCase()),
      )
    : tables

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button showing selected table or placeholder */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100 disabled:opacity-50"
      >
        <span className={selected ? 'text-gray-700' : 'text-gray-400'}>
          {loading
            ? 'Loading tables...'
            : selected
              ? `${selected.label} (${selected.name})`
              : placeholder ?? '— Select a table —'}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown with search */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-sm border border-gray-200 bg-white shadow-lg">
          {/* Search input */}
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tables..."
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-3 text-xs text-gray-700 focus:border-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          {/* Table list */}
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                No tables found matching "{query}"
              </div>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => {
                    onChange(t.name)
                    setOpen(false)
                    setQuery('')
                  }}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-blue-50 ${
                    t.name === value ? 'bg-blue-50 font-medium text-[#3454cf]' : 'text-gray-700'
                  }`}
                >
                  <span>{t.label}</span>
                  <span className="ml-2 shrink-0 font-mono text-[10px] text-gray-400">{t.name}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer count */}
          {filtered.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-1 text-[10px] text-gray-400">
              {filtered.length} table{filtered.length !== 1 ? 's' : ''}
              {query && ` of ${tables.length}`}
            </div>
          )}
        </div>
      )}

      {/* Hidden required indicator */}
      {required && !value && <span className="text-red-500" />}
    </div>
  )
}

// ── Reusable searchable route select (for 300+ API route dropdowns) ────────

function SearchableRouteSelect({
  value,
  onChange,
  routes,
  loading,
  method,
}: {
  value: string
  onChange: (route: string) => void
  routes: InternalRoute[]
  loading: boolean
  method: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const filteredRoutes = routes.filter((r) => r.method === method)
  const selected = filteredRoutes.find((r) => r.route === value)
  const q = query.trim().toLowerCase()
  const filtered = q
    ? filteredRoutes.filter(
        (r) => r.route.toLowerCase().includes(q) || r.label.toLowerCase().includes(q),
      )
    : filteredRoutes

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100 disabled:opacity-50"
      >
        <span className={`truncate ${selected ? 'text-gray-700' : 'text-gray-400'}`}>
          {loading
            ? 'Loading routes...'
            : selected
              ? `${selected.label}`
              : '— Select a route —'}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-sm border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search routes..."
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-3 text-xs text-gray-700 focus:border-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                {filteredRoutes.length === 0
                  ? `No ${method} routes found`
                  : `No routes found matching "${query}"`}
              </div>
            ) : (
              filtered.map((r) => (
                <button
                  key={`${r.method} ${r.route}`}
                  type="button"
                  onClick={() => {
                    onChange(r.route)
                    setOpen(false)
                    setQuery('')
                  }}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-blue-50 ${
                    r.route === value ? 'bg-blue-50 font-medium text-[#3454cf]' : 'text-gray-700'
                  }`}
                >
                  <span className="truncate">{r.label}</span>
                  <span className="ml-2 shrink-0 font-mono text-[10px] text-gray-400">{r.route}</span>
                </button>
              ))
            )}
          </div>

          {filtered.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-1 text-[10px] text-gray-400">
              {filtered.length} route{filtered.length !== 1 ? 's' : ''}
              {query && ` of ${filteredRoutes.length}`}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Reusable searchable multi-select for API routes ────────────────────────

function _SearchableRouteMultiSelect({
  selected,
  onChange,
  routes,
  loading,
  method,
}: {
  selected: string[]
  onChange: (routes: string[]) => void
  routes: InternalRoute[]
  loading: boolean
  method: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const filteredRoutes = routes.filter((r) => r.method === method)
  const q = query.trim().toLowerCase()
  const filtered = q
    ? filteredRoutes.filter(
        (r) => r.route.toLowerCase().includes(q) || r.label.toLowerCase().includes(q),
      )
    : filteredRoutes

  const toggleRoute = (route: string): void => {
    onChange(selected.includes(route) ? selected.filter((r) => r !== route) : [...selected, route])
  }

  const selectedLabels = selected.map((route) => {
    const r = filteredRoutes.find((x) => x.route === route)
    return r ? r.label : route
  })

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:border-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100 disabled:opacity-50"
      >
        <span className={`truncate ${selected.length > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
          {loading
            ? 'Loading routes...'
            : selected.length === 0
              ? '— Select routes —'
              : selected.length === 1
                ? selectedLabels[0]
                : `${selected.length} routes selected`}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Selected route chips */}
      {selected.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selected.map((route) => {
            const r = filteredRoutes.find((x) => x.route === route)
            return (
              <span
                key={route}
                className="inline-flex items-center gap-1 rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] text-[#3454cf]"
              >
                <span className="truncate max-w-[200px]">{r ? r.label : route}</span>
                <button
                  type="button"
                  onClick={() => toggleRoute(route)}
                  className="text-[#3454cf] hover:text-red-500"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )
          })}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-sm border border-gray-200 bg-white shadow-lg">
          {/* Search + actions */}
          <div className="border-b border-gray-100 p-2">
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search routes..."
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-3 text-xs text-gray-700 focus:border-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-400">
                {selected.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onChange(filtered.map((r) => r.route))}
                  className="text-[10px] font-medium text-[#3454cf] hover:underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[10px] font-medium text-gray-500 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Route list with checkboxes */}
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                {filteredRoutes.length === 0
                  ? `No ${method} routes found`
                  : `No routes found matching "${query}"`}
              </div>
            ) : (
              filtered.map((r) => {
                const isSelected = selected.includes(r.route)
                return (
                  <button
                    key={`${r.method} ${r.route}`}
                    type="button"
                    onClick={() => toggleRoute(r.route)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-blue-50 ${
                      isSelected ? 'bg-blue-50/50' : 'text-gray-700'
                    }`}
                  >
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border ${
                      isSelected ? 'border-[#3454cf] bg-[#3454cf] text-white' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span className={`truncate ${isSelected ? 'font-medium text-[#3454cf]' : ''}`}>{r.label}</span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">{r.route}</span>
                  </button>
                )
              })
            )}
          </div>

          {/* Footer */}
          {filtered.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-1 text-[10px] text-gray-400">
              {filtered.length} route{filtered.length !== 1 ? 's' : ''}
              {query && ` of ${filteredRoutes.length}`}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const AGGREGATION_TYPES = [
  { value: 'count', label: 'Count (number of rows)' },
  { value: 'sum', label: 'Sum (total of a numeric column)' },
  { value: 'avg', label: 'Average (mean of a numeric column)' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
] as const

const REPORT_FORMATS = [
  { value: 'csv', label: 'CSV (spreadsheet)' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'pdf', label: 'PDF' },
  { value: 'json', label: 'JSON' },
] as const

const NOTIFICATION_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
] as const

const CONVERSATION_ACTIONS = [
  { value: 'assign', label: 'Assign to agent/team' },
  { value: 'close', label: 'Close conversation' },
  { value: 'set_priority', label: 'Set priority' },
  { value: 'add_tag', label: 'Add tag' },
  { value: 'set_status', label: 'Set status' },
] as const

const PARAM_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Yes/No (Boolean)' },
  { value: 'array', label: 'List (Array)' },
] as const

/**
 * Common parameter presets per handler type. Lets admins bootstrap a tool's
 * input schema with sensible defaults instead of starting from a blank slate.
 * Only handler types that benefit from templated inputs define presets.
 */
const HANDLER_PARAM_PRESETS: Record<string, SchemaParam[]> = {
  database_read_template: [
    { name: 'dateFrom', type: 'string', description: 'Start date for the query (ISO 8601, e.g. 2024-01-01). The AI derives this from the user\'s request.', required: false, enumValues: '' },
    { name: 'dateTo', type: 'string', description: 'End date for the query (ISO 8601). Defaults to today if omitted.', required: false, enumValues: '' },
    { name: 'status', type: 'string', description: 'Filter by status value (e.g. open, closed, pending).', required: false, enumValues: '' },
  ],
  report_template: [
    { name: 'dateFrom', type: 'string', description: 'Start date for the report period (ISO 8601).', required: false, enumValues: '' },
    { name: 'dateTo', type: 'string', description: 'End date for the report period (ISO 8601).', required: false, enumValues: '' },
  ],
  queue_export: [
    { name: 'dateFrom', type: 'string', description: 'Start date for the export (ISO 8601).', required: false, enumValues: '' },
    { name: 'dateTo', type: 'string', description: 'End date for the export (ISO 8601).', required: false, enumValues: '' },
    { name: 'status', type: 'string', description: 'Filter rows by status.', required: false, enumValues: '' },
  ],
  lookup_query: [
    { name: 'query', type: 'string', description: 'The search term the AI wants to look up in the reference table.', required: true, enumValues: '' },
  ],
  knowledge_search: [
    { name: 'query', type: 'string', description: 'The question or search phrase to look up in the knowledge base.', required: true, enumValues: '' },
    { name: 'limit', type: 'integer', description: 'Maximum number of results to return (default: 10).', required: false, enumValues: '' },
  ],
  notification_send: [
    { name: 'recipient', type: 'string', description: 'Recipient identifier (email address, phone number, or user ID depending on channel).', required: true, enumValues: '' },
    { name: 'subject', type: 'string', description: 'Optional subject line for email notifications.', required: false, enumValues: '' },
  ],
  web_scraper: [
    { name: 'url', type: 'string', description: 'The full URL to fetch and extract data from.', required: true, enumValues: '' },
  ],
  conversation_action: [
    { name: 'conversationId', type: 'string', description: 'The ID of the conversation to act on.', required: true, enumValues: '' },
    { name: 'assigneeId', type: 'string', description: 'User/team ID to assign (used by the "assign" action).', required: false, enumValues: '' },
    { name: 'priority', type: 'string', description: 'Priority value for the "set_priority" action.', required: false, enumValues: 'low,medium,high,urgent' },
    { name: 'tag', type: 'string', description: 'Tag to add for the "add_tag" action.', required: false, enumValues: '' },
  ],
  internal_api: [
    { name: 'body', type: 'string', description: 'Optional request body (JSON string) for POST endpoints.', required: false, enumValues: '' },
  ],
  custom_code: [
    { name: 'input', type: 'string', description: 'Input passed to your execute function as the first argument.', required: false, enumValues: '' },
  ],
}

// ── Visual Schema Builder types ────────────────────────────────────────────

interface SchemaParam {
  name: string
  type: string
  description: string
  required: boolean
  enumValues: string
}

interface DbReadHandlerConfig {
  table: string
  groupBy: string
  aggregation: string
  aggregationColumn: string
  maxRows: string
  whereTemplate: string
}

interface ReportHandlerConfig {
  format: string
  table: string
  columns: string
  filters: string
}

interface InternalApiHandlerConfig {
  method: string
  routes: string[]
  timeoutMs: string
}

interface KnowledgeSearchHandlerConfig {
  collectionSlug: string
  limit: string
}

interface QueueExportHandlerConfig {
  table: string
  format: string
  maxRows: string
  filters: string
}

interface LookupQueryHandlerConfig {
  table: string
  searchColumn: string
}

interface NotificationHandlerConfig {
  channel: string
  templateId: string
}

interface ConversationActionHandlerConfig {
  action: string
}

interface WebScraperPresetField {
  name: string
  selector: string
  engine: 'css' | 'xpath'
  extract: string
}

interface WebScraperPreset {
  id: string
  name: string
  mode: 'single' | 'list' | 'record' | 'full'
  containerSelector: string
  containerEngine: 'css' | 'xpath'
  fields: WebScraperPresetField[]
}

interface WebScraperHandlerConfig {
  allowedDomains: string
  blockedDomains: string
  presets: WebScraperPreset[]
  renderJs: boolean
  renderJsFallback: 'fail' | 'plain_fetch'
  ignoreRobotsTxt: boolean
  rateLimitMs: string
  timeoutMs: string
  maxResponseBytes: string
  maxRedirects: string
  maxRecords: string
}

interface CustomCodeHandlerConfig {
  language: 'javascript' | 'python'
  source: string
  timeoutMs: string
  memoryLimitMb: string
  aiGenerated: boolean
  generationPrompt: string
  /** Advisory warnings from the last generate-code call (empty when clean). */
  warnings: { code: string; message: string }[]
}

// ── Convert visual form state to JSON Schema ───────────────────────────────

function paramsToJsonSchema(params: SchemaParam[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  const required: string[] = []

  for (const p of params) {
    if (!p.name.trim()) continue

    const prop: Record<string, unknown> = {
      type: p.type,
      description: p.description || undefined,
    }

    // Parse enum values (comma-separated) → add to schema
    if (p.enumValues.trim()) {
      const enums = p.enumValues.split(',').map((v) => v.trim()).filter(Boolean)
      if (enums.length > 0) {
        prop['enum'] = enums
      }
    }

    // Remove undefined description to keep schema clean
    if (prop['description'] === undefined) {
      delete prop['description']
    }

    properties[p.name] = prop
    if (p.required) {
      required.push(p.name)
    }
  }

  return {
    type: 'object',
    properties,
    required,
  }
}

// ── Convert JSON Schema back to visual form params ─────────────────────────

function jsonSchemaToParams(schema: Record<string, unknown>): SchemaParam[] {
  const props = schema['properties'] as Record<string, Record<string, unknown>> | undefined
  if (!props || typeof props !== 'object') return []

  const requiredArr = Array.isArray(schema['required']) ? schema['required'] as string[] : []

  return Object.entries(props).map(([name, def]) => {
    const enumArr = Array.isArray(def['enum']) ? def['enum'] as string[] : []
    return {
      name,
      type: (def['type'] as string) || 'string',
      description: (def['description'] as string) || '',
      required: requiredArr.includes(name),
      enumValues: enumArr.join(', '),
    }
  })
}

// ── Convert handler config objects ─────────────────────────────────────────

function parseDbReadConfig(config: Record<string, unknown> | null): DbReadHandlerConfig {
  return {
    table: (config?.['table'] as string) || '',
    groupBy: (config?.['groupBy'] as string) || '',
    aggregation: (config?.['aggregation'] as string) || 'count',
    aggregationColumn: (config?.['aggregationColumn'] as string) || '',
    maxRows: String(config?.['maxRows'] ?? '1000'),
    whereTemplate: (config?.['whereTemplate'] as string) || '',
  }
}

function parseReportConfig(config: Record<string, unknown> | null): ReportHandlerConfig {
  return {
    format: (config?.['format'] as string) || 'csv',
    table: (config?.['table'] as string) || '',
    columns: (config?.['columns'] as string) || '',
    filters: (config?.['filters'] as string) || '',
  }
}

function parseInternalApiConfig(config: Record<string, unknown> | null): InternalApiHandlerConfig {
  // Support both old single "route" field and new "routes" array for backward compat
  const routesArr = Array.isArray(config?.['routes']) ? (config!['routes'] as string[]) : []
  const legacyRoute = (config?.['route'] as string) || ''
  const routes = routesArr.length > 0 ? routesArr : legacyRoute ? [legacyRoute] : []
  return {
    method: (config?.['method'] as string) || 'GET',
    routes,
    timeoutMs: String(config?.['timeoutMs'] ?? '30000'),
  }
}

function parseKnowledgeSearchConfig(config: Record<string, unknown> | null): KnowledgeSearchHandlerConfig {
  return {
    collectionSlug: (config?.['collectionSlug'] as string) || '',
    limit: String(config?.['limit'] ?? '10'),
  }
}

function parseQueueExportConfig(config: Record<string, unknown> | null): QueueExportHandlerConfig {
  return {
    table: (config?.['table'] as string) || '',
    format: (config?.['format'] as string) || 'csv',
    maxRows: String(config?.['maxRows'] ?? '10000'),
    filters: (config?.['filters'] as string) || '',
  }
}

function parseLookupQueryConfig(config: Record<string, unknown> | null): LookupQueryHandlerConfig {
  return {
    table: (config?.['table'] as string) || '',
    searchColumn: (config?.['searchColumn'] as string) || 'name',
  }
}

function parseNotificationConfig(config: Record<string, unknown> | null): NotificationHandlerConfig {
  return {
    channel: (config?.['channel'] as string) || 'email',
    templateId: (config?.['templateId'] as string) || '',
  }
}

function parseConversationActionConfig(config: Record<string, unknown> | null): ConversationActionHandlerConfig {
  return {
    action: (config?.['action'] as string) || 'assign',
  }
}

function parseWebScraperConfig(config: Record<string, unknown> | null): WebScraperHandlerConfig {
  const presetsRaw = Array.isArray(config?.['presets']) ? (config!['presets'] as Record<string, unknown>[]) : []
  const presets: WebScraperPreset[] = presetsRaw.map((p) => ({
    id: (p['id'] as string) || '',
    name: (p['name'] as string) || '',
    mode: (p['mode'] as 'single' | 'list' | 'record') || 'single',
    containerSelector: (p['containerSelector'] as string) || '',
    containerEngine: (p['containerEngine'] as 'css' | 'xpath') || 'css',
    fields: Array.isArray(p['fields'])
      ? (p['fields'] as Record<string, unknown>[]).map((f) => ({
          name: (f['name'] as string) || '',
          selector: (f['selector'] as string) || '',
          engine: (f['engine'] as 'css' | 'xpath') || 'css',
          extract: (f['extract'] as string) || 'text',
        }))
      : [],
  }))
  return {
    allowedDomains: Array.isArray(config?.['allowedDomains']) ? (config!['allowedDomains'] as string[]).join(', ') : '',
    blockedDomains: Array.isArray(config?.['blockedDomains']) ? (config!['blockedDomains'] as string[]).join(', ') : '',
    presets,
    renderJs: Boolean(config?.['renderJs'] ?? false),
    renderJsFallback: (config?.['renderJsFallback'] as 'fail' | 'plain_fetch') ?? 'fail',
    ignoreRobotsTxt: Boolean(config?.['ignoreRobotsTxt'] ?? false),
    rateLimitMs: String(config?.['rateLimitMs'] ?? '2000'),
    timeoutMs: String(config?.['timeoutMs'] ?? '10000'),
    maxResponseBytes: String(config?.['maxResponseBytes'] ?? '5242880'),
    maxRedirects: String(config?.['maxRedirects'] ?? '5'),
    maxRecords: String(config?.['maxRecords'] ?? '200'),
  }
}

/**
 * Parse names out of a destructuring pattern's inner content, e.g. the
 * `a, b: c, d = 1` in `const { a, b: c, d = 1 } = input`. For a renamed
 * binding (`b: c`), the SOURCE field name (`b`) is what's returned, since
 * that's the actual input key — `c` is just the local variable name.
 */
function parseDestructureNames(inner: string): string[] {
  const result: string[] = []
  for (const part of inner.split(',')) {
    const trimmed = part.trim()
    if (!trimmed) continue
    const nameMatch = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*:?/.exec(trimmed)
    if (nameMatch) result.push(nameMatch[1]!)
  }
  return result
}

/**
 * Extract the input field names a custom_code function actually reads, by
 * scanning its source for:
 *   - `input.foo` / `input['foo']` / `input["foo"]` (property access)
 *   - `const { foo, bar: baz } = input` (destructuring assignment)
 *   - `function execute({ foo, bar }, ctx)` (destructuring in the signature)
 *   - `input.get('foo')` (Python dict access)
 * Deterministic and doesn't depend on the model separately reporting a
 * schema — it reads what the generated code itself references.
 */
function extractInputFieldNames(source: string, language: 'javascript' | 'python'): string[] {
  const names = new Set<string>()
  const dotPattern = /\binput\.([a-zA-Z_][a-zA-Z0-9_]*)/g
  const bracketPattern = /\binput\[['"]([a-zA-Z_][a-zA-Z0-9_]*)['"]\]/g
  for (const m of source.matchAll(dotPattern)) names.add(m[1]!)
  for (const m of source.matchAll(bracketPattern)) names.add(m[1]!)

  const varDestructure = /(?:const|let|var)\s*\{\s*([^}]+?)\s*\}\s*=\s*input\b/g
  for (const m of source.matchAll(varDestructure)) {
    for (const name of parseDestructureNames(m[1]!)) names.add(name)
  }

  const paramDestructure = /function\s+execute\s*\(\s*\{\s*([^}]+?)\s*\}/.exec(source)
  if (paramDestructure) {
    for (const name of parseDestructureNames(paramDestructure[1]!)) names.add(name)
  }

  if (language === 'python') {
    const getPattern = /\binput\.get\(['"]([a-zA-Z_][a-zA-Z0-9_]*)['"]/g
    for (const m of source.matchAll(getPattern)) names.add(m[1]!)
  }
  return [...names]
}

function parseCustomCodeConfig(config: Record<string, unknown> | null): CustomCodeHandlerConfig {
  return {
    language: (config?.['language'] as 'javascript' | 'python') ?? 'javascript',
    source: (config?.['source'] as string) ?? '',
    timeoutMs: String(config?.['timeoutMs'] ?? '5000'),
    memoryLimitMb: String(config?.['memoryLimitMb'] ?? '128'),
    aiGenerated: Boolean(config?.['aiGenerated'] ?? false),
    generationPrompt: (config?.['generationPrompt'] as string) ?? '',
    warnings: [],
  }
}

// ── Tool Editor State (visual form) ────────────────────────────────────────

interface ToolEditorState {
  name: string
  description: string
  params: SchemaParam[]
  accessesTenantData: boolean
  handlerType: string
  dbReadConfig: DbReadHandlerConfig
  reportConfig: ReportHandlerConfig
  apiConfig: InternalApiHandlerConfig
  knowledgeSearchConfig: KnowledgeSearchHandlerConfig
  queueExportConfig: QueueExportHandlerConfig
  lookupQueryConfig: LookupQueryHandlerConfig
  notificationConfig: NotificationHandlerConfig
  conversationActionConfig: ConversationActionHandlerConfig
  webScraperConfig: WebScraperHandlerConfig
  customCodeConfig: CustomCodeHandlerConfig
  isEnabled: boolean
}

function buildEmptyTool(): ToolEditorState {
  return {
    name: '',
    description: '',
    params: [],
    accessesTenantData: false,
    handlerType: 'metadata_only',
    dbReadConfig: { table: '', groupBy: '', aggregation: 'count', aggregationColumn: '', maxRows: '1000', whereTemplate: '' },
    reportConfig: { format: 'csv', table: '', columns: '', filters: '' },
    apiConfig: { method: 'GET', routes: [], timeoutMs: '30000' },
    knowledgeSearchConfig: { collectionSlug: '', limit: '10' },
    queueExportConfig: { table: '', format: 'csv', maxRows: '10000', filters: '' },
    lookupQueryConfig: { table: '', searchColumn: 'name' },
    notificationConfig: { channel: 'email', templateId: '' },
    conversationActionConfig: { action: 'assign' },
    webScraperConfig: {
      allowedDomains: '',
      blockedDomains: '',
      presets: [],
      renderJs: false,
      renderJsFallback: 'fail',
      ignoreRobotsTxt: false,
      rateLimitMs: '2000',
      timeoutMs: '10000',
      maxResponseBytes: '5242880',
      maxRedirects: '5',
      maxRecords: '200',
    },
    customCodeConfig: {
      language: 'javascript',
      source: '',
      timeoutMs: '5000',
      memoryLimitMb: '128',
      aiGenerated: false,
      generationPrompt: '',
      warnings: [],
    },
    isEnabled: true,
  }
}

function toolToEditorState(tool: ToolCatalogEntry): ToolEditorState {
  const handlerType = tool.handlerType ?? 'metadata_only'
  const config = tool.handlerConfig ?? null

  return {
    name: tool.name,
    description: tool.description,
    params: jsonSchemaToParams(tool.inputSchema),
    accessesTenantData: tool.accessesTenantData,
    handlerType,
    dbReadConfig: handlerType === 'database_read_template' ? parseDbReadConfig(config) : parseDbReadConfig(null),
    reportConfig: handlerType === 'report_template' ? parseReportConfig(config) : parseReportConfig(null),
    apiConfig: handlerType === 'internal_api' ? parseInternalApiConfig(config) : parseInternalApiConfig(null),
    knowledgeSearchConfig: handlerType === 'knowledge_search' ? parseKnowledgeSearchConfig(config) : parseKnowledgeSearchConfig(null),
    queueExportConfig: handlerType === 'queue_export' ? parseQueueExportConfig(config) : parseQueueExportConfig(null),
    lookupQueryConfig: handlerType === 'lookup_query' ? parseLookupQueryConfig(config) : parseLookupQueryConfig(null),
    notificationConfig: handlerType === 'notification_send' ? parseNotificationConfig(config) : parseNotificationConfig(null),
    conversationActionConfig: handlerType === 'conversation_action' ? parseConversationActionConfig(config) : parseConversationActionConfig(null),
    webScraperConfig: handlerType === 'web_scraper' ? parseWebScraperConfig(config) : parseWebScraperConfig(null),
    customCodeConfig: handlerType === 'custom_code' ? parseCustomCodeConfig(config) : parseCustomCodeConfig(null),
    isEnabled: tool.isEnabled ?? true,
  }
}

// ── Param Placeholders Hint ────────────────────────────────────────────────

/**
 * Renders the list of available `{{paramName}}` placeholders derived from the
 * tool's current parameters. Shown under handler config fields that support
 * templating (e.g. SQL WHERE, report filters) so the user knows exactly which
 * placeholders they can reference.
 */
/** Renders web-scraper test result data in a readable format based on extraction mode. */
function ScrapeDataPreview({ data, mode }: { data: unknown; mode?: string }): JSX.Element {
  if (data === null || data === undefined) {
    return <p className="text-[10px] text-gray-400">No data extracted.</p>
  }

  // Full mode: { title, text, charCount } — show readable text
  if (mode === 'full' && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as { title?: string; text?: string; charCount?: number }
    return (
      <div className="max-h-56 overflow-auto rounded-sm border border-gray-200 bg-white p-2">
        {d.title && <p className="mb-1 text-[11px] font-semibold text-gray-800">{d.title}</p>}
        <p className="whitespace-pre-wrap text-[10px] leading-relaxed text-gray-600">
          {d.text && d.text.length > 3000 ? d.text.slice(0, 3000) + '…' : d.text || '(empty page)'}
        </p>
        {typeof d.charCount === 'number' && (
          <p className="mt-1 text-[9px] text-gray-400">{d.charCount.toLocaleString()} characters extracted</p>
        )}
      </div>
    )
  }

  // List mode: array of strings — show as a list
  if (mode === 'list' && Array.isArray(data)) {
    const items = data as string[]
    if (items.length === 0) return <p className="text-[10px] text-gray-400">No items matched.</p>
    return (
      <div className="max-h-56 overflow-auto rounded-sm border border-gray-200 bg-white">
        <ul className="divide-y divide-gray-100">
          {items.slice(0, 50).map((item, i) => (
            <li key={i} className="px-2 py-1 text-[10px] text-gray-700">
              <span className="mr-1.5 text-gray-400">{i + 1}.</span>
              {String(item)}
            </li>
          ))}
          {items.length > 50 && (
            <li className="px-2 py-1 text-[9px] text-gray-400">… and {items.length - 50} more</li>
          )}
        </ul>
        <p className="border-t border-gray-100 bg-gray-50 px-2 py-1 text-[9px] text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</p>
      </div>
    )
  }

  // Record mode: array of objects — show as a table
  if (mode === 'record' && Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    const rows = data as Record<string, unknown>[]
    const columns = Object.keys(rows[0] ?? {})
    return (
      <div className="max-h-56 overflow-auto rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 bg-gray-100 text-gray-600">
            <tr>
              <th className="px-2 py-1 text-left font-medium">#</th>
              {columns.map((col) => (
                <th key={col} className="px-2 py-1 text-left font-medium">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((row, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                {columns.map((col) => (
                  <td key={col} className="px-2 py-1 text-gray-700">{String(row[col] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 50 && (
          <p className="border-t border-gray-100 bg-gray-50 px-2 py-1 text-[9px] text-gray-400">Showing first 50 of {rows.length} records</p>
        )}
      </div>
    )
  }

  // Single mode or fallback: show the raw value
  if (typeof data === 'string') {
    return (
      <div className="max-h-40 overflow-auto rounded-sm border border-gray-200 bg-white p-2">
        <p className="whitespace-pre-wrap text-[11px] text-gray-700">{data}</p>
      </div>
    )
  }

  // Fallback: formatted JSON
  return (
    <pre className="max-h-48 overflow-auto rounded-sm border border-gray-200 bg-white p-2 text-[10px] text-gray-700">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function ParamPlaceholdersHint({ params }: { params: SchemaParam[] }): JSX.Element {

  const named = params.filter((p) => p.name.trim().length > 0)
  if (named.length === 0) {
    return (
      <p className="mt-1 text-[10px] text-gray-400">
        No parameters defined yet — add some in the Parameters section below to reference them here.
      </p>
    )
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <span className="text-[10px] text-gray-400">Available placeholders:</span>
      {named.map((p) => (
        <code
          key={p.name}
          className="rounded-sm bg-gray-100 px-1 py-0.5 font-mono text-[10px] text-gray-600"
          title={p.description || undefined}
        >
          {'{{' + p.name + '}}'}
        </code>
      ))}
    </div>
  )
}

// ── Tool Editor Dialog (visual form) ───────────────────────────────────────

function ToolEditorDialog({
  mode,
  tool,
  catalogTables,
  catalogLoading,
  internalRoutes,
  routesLoading,
  onClose,
  onSave,
}: {
  mode: 'create' | 'edit'
  tool: ToolEditorState | null
  catalogTables: CatalogTable[]
  catalogLoading: boolean
  internalRoutes: InternalRoute[]
  routesLoading: boolean
  onClose: () => void
  onSave: (data: Record<string, unknown>) => Promise<void>
}): JSX.Element {
  const toast = useToast()
  const [form, setForm] = useState<ToolEditorState>(tool ?? buildEmptyTool())
  const [saving, setSaving] = useState(false)
  const [showJsonPreview, setShowJsonPreview] = useState(false)
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ error?: string; simulated?: boolean; message?: string; rowCount?: number; columns?: string[]; sampleRows?: Record<string, unknown>[]; truncated?: boolean; matchedCount?: number; url?: string; finalUrl?: string; status?: number; mode?: string; modeSource?: string; data?: unknown; robotsPolicy?: string; contentType?: string; bytes?: number; robotsOverride?: { url: string; rule: string } | null; renderJsFallbackUsed?: boolean; success?: boolean; customResult?: unknown; executionTimeMs?: number; timedOut?: boolean } | null>(null)

  const [showAiPreview, setShowAiPreview] = useState(false)
  const [testInputs, setTestInputs] = useState<Record<string, string>>({})
  const [pastedHtmlMode, setPastedHtmlMode] = useState(false)
  const [pastedHtml, setPastedHtml] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [activeTab, setActiveTab] = useState<'basic' | 'parameters'>('basic')
  const [pythonAvailable, setPythonAvailable] = useState(true)
  const [showCodeGenerate, setShowCodeGenerate] = useState(false)
  const [codeGenerateDescription, setCodeGenerateDescription] = useState('')
  const [codeGenerating, setCodeGenerating] = useState(false)
  const lastHandlerTypeRef = useRef<string>(form.handlerType === 'custom_code' ? 'metadata_only' : form.handlerType)
  const TOOL_TAB_ORDER = ['basic', 'parameters'] as const

  // Fetch custom_code language availability (Python gated by deployment config).
  useEffect(() => {
    api
      .get<{ availableLanguages: { value: 'javascript' | 'python'; available: boolean }[] }>(
        '/admin/ai/tools/custom-code/meta',
      )
      .then((res) => {
        const py = res.availableLanguages?.find((l) => l.value === 'python')
        setPythonAvailable(py?.available ?? false)
      })
      .catch(() => {
        // If the meta endpoint fails, default to Python unavailable.
        setPythonAvailable(false)
      })
  }, [])

  /** Smart defaults: auto-set accessesTenantData when handler type changes. */
  const handlerTypeDefaults: Record<string, { accessesTenantData: boolean }> = {
    database_read_template: { accessesTenantData: true },
    report_template: { accessesTenantData: true },
    internal_api: { accessesTenantData: false },
    knowledge_search: { accessesTenantData: true },
    queue_export: { accessesTenantData: true },
    lookup_query: { accessesTenantData: true },
    notification_send: { accessesTenantData: false },
    conversation_action: { accessesTenantData: true },
    web_scraper: { accessesTenantData: false },
    custom_code: { accessesTenantData: false },
    metadata_only: { accessesTenantData: false },
  }

  const handleHandlerTypeChange = (newType: string): void => {
    if (newType !== 'custom_code') {
      lastHandlerTypeRef.current = newType
    }
    const defaults = handlerTypeDefaults[newType]
    setForm((prev) => ({
      ...prev,
      handlerType: newType,
      // Auto-set tenant data access based on handler type (only if user hasn't manually overridden)
      accessesTenantData: defaults?.accessesTenantData ?? prev.accessesTenantData,
    }))
  }

  const inputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-gray-400 focus:ring-2 focus:ring-gray-100'
  const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

  // Fetch templates when notification_send handler is selected
  useEffect(() => {
    if (form.handlerType !== 'notification_send') {
      setTemplates([])
      return
    }
    let cancelled = false
    setTemplatesLoading(true)
    const channel = form.notificationConfig.channel
    const endpoint =
      channel === 'email'
        ? '/admin/template-management/email-templates/dropdown'
        : channel === 'sms'
          ? '/admin/template-management/sms-templates/dropdown'
          : channel === 'whatsapp'
            ? '/admin/template-management/whatsapp-templates/dropdown'
            : null
    if (!endpoint) {
      setTemplates([])
      setTemplatesLoading(false)
      return
    }
    const responseKey =
      channel === 'email'
        ? 'emailTemplates'
        : channel === 'sms'
          ? 'smsTemplates'
          : 'whatsappTemplates'
    api
      .get<Record<string, { id: string; name: string }[]>>(endpoint)
      .then((res) => {
        if (!cancelled) setTemplates(res[responseKey] ?? [])
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [form.handlerType, form.notificationConfig.channel])

  const updateParam = (index: number, patch: Partial<SchemaParam>): void => {
    setForm((prev) => ({
      ...prev,
      params: prev.params.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }))
  }

  const addParam = (): void => {
    setForm((prev) => ({
      ...prev,
      params: [...prev.params, { name: '', type: 'string', description: '', required: false, enumValues: '' }],
    }))
  }

  /**
   * Add the preset parameters for the current handler type, skipping any whose
   * name already exists (so the button is idempotent and never clobbers
   * user-edited params).
   */
  const addPresetParams = (): void => {
    const presets = HANDLER_PARAM_PRESETS[form.handlerType]
    if (!presets || presets.length === 0) return
    setForm((prev) => {
      const existing = new Set(prev.params.map((p) => p.name))
      const toAdd = presets.filter((p) => !existing.has(p.name))
      return { ...prev, params: [...prev.params, ...toAdd] }
    })
  }

  const removeParam = (index: number): void => {
    setForm((prev) => ({
      ...prev,
      params: prev.params.filter((_, i) => i !== index),
    }))
  }

  const updateDbReadConfig = (patch: Partial<DbReadHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, dbReadConfig: { ...prev.dbReadConfig, ...patch } }))
  }

  const updateReportConfig = (patch: Partial<ReportHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, reportConfig: { ...prev.reportConfig, ...patch } }))
  }

  const updateApiConfig = (patch: Partial<InternalApiHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, apiConfig: { ...prev.apiConfig, ...patch } }))
  }

  const updateKnowledgeSearchConfig = (patch: Partial<KnowledgeSearchHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, knowledgeSearchConfig: { ...prev.knowledgeSearchConfig, ...patch } }))
  }

  const updateQueueExportConfig = (patch: Partial<QueueExportHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, queueExportConfig: { ...prev.queueExportConfig, ...patch } }))
  }

  const updateLookupQueryConfig = (patch: Partial<LookupQueryHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, lookupQueryConfig: { ...prev.lookupQueryConfig, ...patch } }))
  }

  const updateNotificationConfig = (patch: Partial<NotificationHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, notificationConfig: { ...prev.notificationConfig, ...patch } }))
  }

  const updateConversationActionConfig = (patch: Partial<ConversationActionHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, conversationActionConfig: { ...prev.conversationActionConfig, ...patch } }))
  }

  const updateWebScraperConfig = (patch: Partial<WebScraperHandlerConfig>): void => {
    setForm((prev) => ({ ...prev, webScraperConfig: { ...prev.webScraperConfig, ...patch } }))
  }

  // Build the handler config object from the visual form
  const buildHandlerConfig = (): Record<string, unknown> => {
    switch (form.handlerType) {
      case 'database_read_template': {
        const c = form.dbReadConfig
        const config: Record<string, unknown> = {
          table: c.table,
          aggregation: c.aggregation,
          maxRows: parseInt(c.maxRows, 10) || 1000,
        }
        if (c.groupBy.trim()) config['groupBy'] = c.groupBy.trim()
        if (c.aggregationColumn.trim()) config['aggregationColumn'] = c.aggregationColumn.trim()
        if (c.whereTemplate.trim()) config['whereTemplate'] = c.whereTemplate.trim()
        return config
      }
      case 'report_template': {
        const c = form.reportConfig
        const config: Record<string, unknown> = {
          format: c.format,
          table: c.table,
        }
        if (c.columns.trim()) config['columns'] = c.columns.split(',').map((s) => s.trim()).filter(Boolean)
        if (c.filters.trim()) config['filters'] = c.filters.trim()
        return config
      }
      case 'internal_api': {
        const c = form.apiConfig
        return {
          method: c.method,
          routes: c.routes,
          timeoutMs: parseInt(c.timeoutMs, 10) || 30000,
        }
      }
      case 'knowledge_search': {
        const c = form.knowledgeSearchConfig
        const config: Record<string, unknown> = {
          limit: parseInt(c.limit, 10) || 10,
        }
        if (c.collectionSlug.trim()) config['collectionSlug'] = c.collectionSlug.trim()
        return config
      }
      case 'queue_export': {
        const c = form.queueExportConfig
        const config: Record<string, unknown> = {
          table: c.table,
          format: c.format,
          maxRows: parseInt(c.maxRows, 10) || 10000,
        }
        if (c.filters.trim()) config['filters'] = c.filters.trim()
        return config
      }
      case 'lookup_query': {
        const c = form.lookupQueryConfig
        return {
          table: c.table,
          searchColumn: c.searchColumn,
        }
      }
      case 'notification_send': {
        const c = form.notificationConfig
        return {
          channel: c.channel,
          templateId: c.templateId,
        }
      }
      case 'conversation_action': {
        const c = form.conversationActionConfig
        return {
          action: c.action,
        }
      }
      case 'web_scraper': {
        const c = form.webScraperConfig
        // Only include presets that have at least an id and name — incomplete
        // presets are filtered out so the backend never sees a half-filled row.
        const cleanPresets = c.presets
          .filter((p) => p.id.trim() && p.name.trim())
          .map((p) => ({
            id: p.id.trim(),
            name: p.name.trim(),
            mode: p.mode,
            ...(p.mode === 'record' && p.containerSelector.trim() ? { containerSelector: p.containerSelector.trim() } : {}),
            fields: p.fields
              .filter((f) => f.name.trim() && f.selector.trim())
              .map((f) => ({
                name: f.name.trim(),
                selector: f.selector.trim(),
                engine: f.engine,
                extract: f.extract,
              })),
          }))
        const config: Record<string, unknown> = {
          renderJs: c.renderJs,
          renderJsFallback: c.renderJsFallback,
          ignoreRobotsTxt: c.ignoreRobotsTxt,
          rateLimitMs: parseInt(c.rateLimitMs, 10) || 2000,
          timeoutMs: parseInt(c.timeoutMs, 10) || 10000,
          maxResponseBytes: parseInt(c.maxResponseBytes, 10) || 5242880,
          maxRedirects: parseInt(c.maxRedirects, 10) || 5,
          maxRecords: parseInt(c.maxRecords, 10) || 200,
          presets: cleanPresets,
        }
        const allowed = c.allowedDomains.split(',').map((s) => s.trim()).filter(Boolean)
        const blocked = c.blockedDomains.split(',').map((s) => s.trim()).filter(Boolean)
        if (allowed.length > 0) config['allowedDomains'] = allowed
        if (blocked.length > 0) config['blockedDomains'] = blocked
        return config
      }
      case 'custom_code': {
        const c = form.customCodeConfig
        return {
          language: c.language,
          source: c.source,
          timeoutMs: parseInt(c.timeoutMs, 10) || 5000,
          memoryLimitMb: parseInt(c.memoryLimitMb, 10) || 128,
          accessesTenantData: form.accessesTenantData,
          aiGenerated: c.aiGenerated,
          ...(c.aiGenerated && c.generationPrompt ? { generationPrompt: c.generationPrompt } : {}),
        }
      }
      default:
        return {}
    }
  }

  const generatedSchema = useMemo(() => paramsToJsonSchema(form.params), [form.params])

  /** Generate a plain-English summary of what the tool will do. */
  const handlerSummary = useMemo((): string => {
    const tableName = (name: string): string =>
      catalogTables.find((t) => t.name === name)?.label ?? name

    switch (form.handlerType) {
      case 'metadata_only':
        return 'This tool provides metadata only — no data is queried or modified.'

      case 'database_read_template': {
        const cfg = form.dbReadConfig
        const parts: string[] = []
        parts.push(`Queries the ${tableName(cfg.table) || '— no table selected —'} table`)
        if (cfg.aggregation !== 'count') {
          parts.push(`computing ${cfg.aggregation.toUpperCase()} of ${cfg.aggregationColumn || '—'}`)
        } else {
          parts.push('counting rows')
        }
        if (cfg.groupBy) parts.push(`grouped by ${cfg.groupBy}`)
        parts.push(`with a max of ${cfg.maxRows || '1000'} results`)
        if (cfg.whereTemplate) parts.push(`filtered by: ${cfg.whereTemplate}`)
        return parts.join(', ') + '.'
      }

      case 'report_template': {
        const cfg = form.reportConfig
        return `Generates a ${cfg.format.toUpperCase()} report from the ${tableName(cfg.table) || '— no table selected —'} table${cfg.columns ? ` with columns: ${cfg.columns}` : ''}${cfg.filters ? `, filtered by: ${cfg.filters}` : ''}.`
      }

      case 'internal_api': {
        const cfg = form.apiConfig
        const route = cfg.routes[0]
        if (!route) return `Calls the internal API: ${cfg.method} — no route selected —`
        return `Calls the internal API: ${cfg.method} ${route} with a ${cfg.timeoutMs}ms timeout.`
      }

      case 'knowledge_search': {
        const cfg = form.knowledgeSearchConfig
        return `Searches the knowledge base collection "${cfg.collectionSlug || 'default'}" and returns up to ${cfg.limit || '10'} relevant documents.`
      }

      case 'queue_export': {
        const cfg = form.queueExportConfig
        return `Exports up to ${cfg.maxRows || '10000'} rows from the ${tableName(cfg.table) || '— no table selected —'} table as ${cfg.format.toUpperCase()}${cfg.filters ? `, filtered by: ${cfg.filters}` : ''}.`
      }

      case 'lookup_query': {
        const cfg = form.lookupQueryConfig
        return `Looks up reference data from the ${tableName(cfg.table) || '— no table selected —'} table, searching by ${cfg.searchColumn || 'name'}.`
      }

      case 'notification_send': {
        const cfg = form.notificationConfig
        const tplName = templates.find((t) => t.id === cfg.templateId)?.name ?? cfg.templateId
        return `Sends a ${cfg.channel.toUpperCase()} notification using template "${tplName || '— no template selected —'}".`
      }

      case 'conversation_action': {
        const cfg = form.conversationActionConfig
        return `Performs the "${cfg.action}" action on a conversation.`
      }

      case 'web_scraper': {
        const cfg = form.webScraperConfig
        const parts: string[] = [`Scrapes web pages with a ${cfg.timeoutMs || '10000'}ms timeout`]
        if (cfg.allowedDomains.trim()) parts.push(`restricted to: ${cfg.allowedDomains}`)
        if (cfg.blockedDomains.trim()) parts.push(`excluding: ${cfg.blockedDomains}`)
        parts.push(`${cfg.presets.length} selector preset${cfg.presets.length !== 1 ? 's' : ''}`)
        if (cfg.ignoreRobotsTxt) parts.push('robots.txt override enabled')
        return parts.join(', ') + '.'
      }

      case 'custom_code': {
        const cfg = form.customCodeConfig
        const lines = cfg.source.trim() ? cfg.source.trim().split('\n').length : 0
        return `Executes a custom ${cfg.language} function (${lines} line${lines !== 1 ? 's' : ''}) with a ${cfg.timeoutMs || '5000'}ms timeout and ${cfg.memoryLimitMb || '128'}MB memory limit.`
      }

      default:
        return 'Configuration unknown.'
    }
  }, [form, catalogTables, templates])

  /** Section 5: Test selectors against pasted HTML (no network fetch). */
  const handleTestSelectors = async (): Promise<void> => {
    if (!pastedHtml.trim()) {
      toast.error('Validation error', 'Paste some HTML to test selectors against')
      return
    }
    setTestResult(null)
    setTesting(true)
    try {
      const c = form.webScraperConfig
      // Resolve which preset to use: first selected preset, else first configured preset
      const selectedPreset = c.presets[0]
      const mode = selectedPreset?.mode ?? 'full'
      const fields = (selectedPreset?.fields ?? []).map((f) => ({
        name: f.name,
        selector: f.selector,
        engine: f.engine,
        extract: f.extract,
      }))
      const res = await api.post<{
        success?: boolean
        error?: string
        result?: {
          error?: string
          message?: string
          mode?: string
          data?: unknown
          truncated?: boolean
          matchedCount?: number
        }
      }>('/admin/ai/tools/test-selectors', {
        html: pastedHtml,
        mode,
        fields,
        containerSelector: selectedPreset?.containerSelector,
        containerEngine: selectedPreset?.containerEngine,
        maxRecords: parseInt(c.maxRecords, 10) || 200,
      })
      if (res.error) {
        setTestResult({ error: res.error })
      } else if (res.result) {
        const r = res.result
        if (!r.error) {
          setTestResult({
            data: r.data ?? null,
            truncated: r.truncated ?? false,
            matchedCount: r.matchedCount ?? 0,
            ...(r.mode ? { mode: r.mode } : {}),
          })
        } else {
          setTestResult({ error: r.error, ...(r.message ? { message: r.message } : {}) })
        }
      }
    } catch (err) {
      setTestResult({ error: (err as Error).message || 'Selector test failed' })
    } finally {
      setTesting(false)
    }
  }

  const handleTest = async (): Promise<void> => {
    if (pastedHtmlMode) {
      return handleTestSelectors()
    }
    setTestResult(null)
    setTesting(true)
    try {
      const handlerConfig = buildHandlerConfig()
      // Convert test input strings to typed values
      const typedTestInput: Record<string, unknown> = {}
      for (const [key, val] of Object.entries(testInputs)) {
        if (val.trim()) {
          const param = form.params.find((p) => p.name === key)
          if (param?.type === 'number') {
            typedTestInput[key] = Number(val)
          } else if (param?.type === 'boolean') {
            typedTestInput[key] = val === 'true'
          } else {
            typedTestInput[key] = val
          }
        }
      }
      const res = await api.post<{
        success?: boolean
        error?: string
        message?: string
        result?: {
          simulated?: boolean
          message?: string
          rowCount?: number
          columns?: string[]
          sampleRows?: Record<string, unknown>[]
          truncated?: boolean
          matchedCount?: number
          url?: string
          finalUrl?: string
          status?: number
          mode?: string
          modeSource?: string
          data?: unknown
          robotsPolicy?: string
          contentType?: string
          bytes?: number
          robotsOverride?: { url: string; rule: string } | null
          renderJsFallbackUsed?: boolean
        }
        executionTimeMs?: number
        timedOut?: boolean
      }>('/admin/ai/tools/test', {
        handlerType: form.handlerType,
        handlerConfig,
        inputSchema: generatedSchema,
        testInput: typedTestInput,
        accessesTenantData: form.accessesTenantData,
      })
      if (res.error) {
        setTestResult({ error: res.error })
      } else if (form.handlerType === 'custom_code') {
        setTestResult({
          success: true,
          ...(res.result !== undefined ? { customResult: res.result } : {}),
          ...(res.executionTimeMs !== undefined ? { executionTimeMs: res.executionTimeMs } : {}),
          ...(res.timedOut !== undefined ? { timedOut: res.timedOut } : {}),
          ...(res.message ? { message: res.message } : {}),
        })
      } else if (res.result) {
        setTestResult(res.result)
      }
    } catch (err) {
      setTestResult({ error: (err as Error).message || 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  /** Generate custom_code source from a natural-language description via the tenant's code model (e.g. Ollama codellama:7b). */
  const handleGenerateCode = async (): Promise<void> => {
    if (!codeGenerateDescription.trim()) {
      toast.error('Nothing to generate from', 'Describe what the function should do first.')
      return
    }
    setCodeGenerating(true)
    try {
      const res = await api.post<{
        source: string
        provider: string
        model: string
        warnings: { code: string; message: string }[]
      }>('/admin/ai/tools/generate-code', {
        language: form.customCodeConfig.language,
        description: codeGenerateDescription.trim(),
        inputSchema: generatedSchema,
        accessesTenantData: form.accessesTenantData,
      })
      const inferredFields = extractInputFieldNames(res.source, form.customCodeConfig.language)
      const warnings = res.warnings ?? []
      setForm((prev) => {
        const existingNames = new Set(prev.params.map((p) => p.name))
        const newParams: SchemaParam[] = inferredFields
          .filter((name) => !existingNames.has(name))
          .map((name) => ({ name, type: 'string', description: '', required: false, enumValues: '' }))
        return {
          ...prev,
          customCodeConfig: {
            ...prev.customCodeConfig,
            source: res.source,
            aiGenerated: true,
            generationPrompt: codeGenerateDescription.trim(),
            warnings,
          },
          params: [...prev.params, ...newParams],
        }
      })
      setShowCodeGenerate(false)
      const fieldNote = inferredFields.length > 0
        ? ` Detected input fields: ${inferredFields.join(', ')} — added to Parameters, review the types.`
        : ''
      if (warnings.length > 0) {
        toast.warning(
          'Generated with issues',
          `Generated with ${res.model} but ${warnings.length} issue(s) — review before saving.${fieldNote}`,
        )
      } else {
        toast.success('Code generated', `Generated with ${res.model}. Review the code carefully before saving.${fieldNote}`)
      }
    } catch (err) {
      toast.error('Failed to generate code', err instanceof Error ? err.message : String(err))
    } finally {
      setCodeGenerating(false)
    }
  }

  const handleSave = async (): Promise<void> => {
    // Validate name
    if (mode === 'create' && !form.name.trim()) {
      toast.error('Validation error', 'Tool name is required')
      return
    }
    if (mode === 'create' && !/^[a-z][a-z0-9_]*$/.test(form.name)) {
      toast.error('Validation error', 'Tool name must be snake_case (lowercase, start with letter, only a-z 0-9 _)')
      return
    }

    // Validate description
    if (form.description.trim().length < 10) {
      toast.error('Validation error', 'Description must be at least 10 characters')
      return
    }

    // Validate params — each must have a name if any field is filled
    for (let i = 0; i < form.params.length; i++) {
      const p = form.params[i]!
      if (!p.name.trim() && (p.description.trim() || p.enumValues.trim())) {
        toast.error('Validation error', `Parameter #${i + 1} has a description/enum but no name`)
        return
      }
      if (p.name.trim() && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(p.name)) {
        toast.error('Validation error', `Parameter "${p.name}" must be a valid identifier (letters, numbers, underscore)`)
        return
      }
    }

    // Validate handler-specific fields
    if (form.handlerType === 'database_read_template' && !form.dbReadConfig.table) {
      toast.error('Validation error', 'Please select a table for the database read handler')
      return
    }
    if (form.handlerType === 'report_template' && !form.reportConfig.table) {
      toast.error('Validation error', 'Please select a table for the report handler')
      return
    }
    if (form.handlerType === 'internal_api' && form.apiConfig.routes.length === 0) {
      toast.error('Validation error', 'Please select an API route for the internal API handler')
      return
    }
    if (form.handlerType === 'queue_export' && !form.queueExportConfig.table) {
      toast.error('Validation error', 'Please select a table for the queue export handler')
      return
    }
    if (form.handlerType === 'lookup_query' && !form.lookupQueryConfig.table) {
      toast.error('Validation error', 'Please select a lookup table')
      return
    }
    if (form.handlerType === 'notification_send' && !form.notificationConfig.templateId.trim()) {
      toast.error('Validation error', 'Please select a template for the notification handler')
      return
    }
    if (form.handlerType === 'web_scraper') {
      // Presets are optional — if none are configured, the scraper defaults
      // to `full` mode (extract all text from the page).
      // Validate each preset has the required fields the backend expects
      for (let i = 0; i < form.webScraperConfig.presets.length; i++) {
        const preset = form.webScraperConfig.presets[i]!
        if (!preset.id.trim()) {
          toast.error('Validation error', `Preset #${i + 1} is missing an ID`)
          return
        }
        if (!preset.name.trim()) {
          toast.error('Validation error', `Preset "${preset.id}" is missing a name`)
          return
        }
        if (preset.mode !== 'single' && preset.mode !== 'list' && preset.mode !== 'record' && preset.mode !== 'full') {
          toast.error('Validation error', `Preset "${preset.id}" has an invalid mode`)
          return
        }
        // Full mode doesn't need fields or selectors
        if (preset.mode === 'full') continue
        if (preset.fields.length === 0) {
          toast.error('Validation error', `Preset "${preset.id}" must have at least one field`)
          return
        }
        for (let j = 0; j < preset.fields.length; j++) {
          const field = preset.fields[j]!
          if (!field.name.trim()) {
            toast.error('Validation error', `Preset "${preset.id}" field #${j + 1} is missing a name`)
            return
          }
          if (!field.selector.trim()) {
            toast.error('Validation error', `Preset "${preset.id}" field "${field.name}" is missing a selector`)
            return
          }
          if (field.engine !== 'css' && field.engine !== 'xpath') {
            toast.error('Validation error', `Preset "${preset.id}" field "${field.name}" has an invalid engine`)
            return
          }
        }
        if (preset.mode === 'record' && !preset.containerSelector.trim()) {
          toast.error('Validation error', `Preset "${preset.id}" (record mode) requires a container selector`)
          return
        }
      }
    }

    // Validate custom_code fields
    if (form.handlerType === 'custom_code') {
      if (!form.customCodeConfig.source.trim()) {
        toast.error('Validation error', 'Please write the function code')
        return
      }
      if (form.customCodeConfig.source.trim().length < 10) {
        toast.error('Validation error', 'Source code must be at least 10 characters')
        return
      }
      if (form.customCodeConfig.language === 'python' && !pythonAvailable) {
        toast.error('Validation error', 'Python is not available in this deployment')
        return
      }
    }

    const handlerConfig = buildHandlerConfig()

    setSaving(true)
    try {
      await onSave({
        ...(mode === 'create' ? { name: form.name } : {}),
        description: form.description,
        inputSchema: generatedSchema,
        accessesTenantData: form.accessesTenantData,
        handlerType: form.handlerType,
        handlerConfig,
        isEnabled: form.isEnabled,
      })
      onClose()
    } catch (err) {
      toast.error('Failed to save tool', (err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col border-l border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900">
              {mode === 'create' ? 'Create Custom Tool' : `Edit: ${form.name}`}
            </h2>
          </div>
          <button
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex border-b border-gray-200 px-4">
          {([
            { key: 'basic' as const, label: 'Basic Info', icon: <Settings className="h-3.5 w-3.5" /> },
            { key: 'parameters' as const, label: 'Parameters', icon: <Cpu className="h-3.5 w-3.5" /> },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-[#3454cf] text-[#3454cf]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.key === 'parameters' && form.params.filter((p) => p.name.trim()).length > 0 && (
                <span className="ml-0.5 rounded-full bg-[#3454cf]/10 px-1.5 text-[9px] font-bold text-[#3454cf]">
                  {form.params.filter((p) => p.name.trim()).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-28">
          {activeTab === 'basic' && (
          <>
          {/* ── Section: Basics ── */}

          {/* Name (only on create) */}
          {mode === 'create' && (
            <div>
              <label className={labelClass}>Tool Name <span className="text-red-500">*</span></label>
              <input
                className={`${inputClass} ${form.name && !/^[a-z][a-z0-9_]*$/.test(form.name) ? 'border-red-300' : form.name && /^[a-z][a-z0-9_]*$/.test(form.name) ? 'border-green-300' : ''}`}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., get_ticket_stats"
              />
              {form.name && !/^[a-z][a-z0-9_]*$/.test(form.name) && (
                <p className="mt-1 text-[10px] text-red-500">Must be snake_case (lowercase, start with letter)</p>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className={labelClass}>Description <span className="text-red-500">*</span></label>
            <textarea
              className={`${inputClass} ${form.description && form.description.trim().length < 10 ? 'border-red-300' : form.description && form.description.trim().length >= 10 ? 'border-green-300' : ''}`}
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe what this tool does — the AI uses this to decide when to call it."
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                className="h-3.5 w-3.5"
              />
              Enabled
            </label>
          </div>

          {/* Handler Type */}
          <div className="space-y-3">
            <label className={labelClass}>Execution Handler</label>
            <div
              role="radiogroup"
              aria-label="Execution handler type"
              className="mb-2 grid grid-cols-2 gap-1 rounded-sm bg-gray-100 p-1"
            >
              {([
                { value: 'handler', label: 'Execution Handler', icon: <Settings className="h-3 w-3" /> },
                { value: 'custom', label: 'Custom Code', icon: <Cpu className="h-3 w-3" /> },
              ] as const).map((opt) => {
                const isActive = form.handlerType === 'custom_code' ? opt.value === 'custom' : opt.value === 'handler'
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => {
                      if (opt.value === 'custom') {
                        if (form.handlerType !== 'custom_code') {
                          lastHandlerTypeRef.current = form.handlerType
                        }
                        handleHandlerTypeChange('custom_code')
                      } else if (form.handlerType === 'custom_code') {
                        handleHandlerTypeChange(lastHandlerTypeRef.current)
                      }
                    }}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold transition-colors ${
                      isActive ? 'bg-white text-[#3454cf] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {form.handlerType !== 'custom_code' && (
                            <Select
                ariaLabel={"Handler Type"}
                size="sm"
                value={form.handlerType}
                onChange={(v) => handleHandlerTypeChange(v)}
              >
                <option value="metadata_only">Metadata Only (informational tool)</option>
                <option value="database_read_template">Database Read (query tenant data)</option>
                <option value="report_template">Report Generator (export data)</option>
                <option value="internal_api">Internal API (call approved route)</option>
                <option value="knowledge_search">Knowledge Search (search FAQs, SOPs, docs)</option>
                <option value="queue_export">Queue Export (async background export)</option>
                <option value="lookup_query">Lookup Query (reference/lookup data)</option>
                <option value="notification_send">Notification (send email/SMS/WhatsApp)</option>
                <option value="conversation_action">Conversation Action (assign, close, tag)</option>
                <option value="web_scraper">Web Scraper (fetch & extract data from a URL)</option>
                            </Select>
            )}

            {form.handlerType !== 'custom_code' && (
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={form.accessesTenantData}
                  onChange={(e) => setForm({ ...form, accessesTenantData: e.target.checked })}
                  className="h-3.5 w-3.5"
                />
                Accesses Tenant Data
              </label>
            )}
          </div>

          {/* ── Section: Handler Configuration ── */}

          {/* ── Handler-specific config fields ── */}

          {form.handlerType === 'database_read_template' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Database Read Configuration</div>

              {/* Table selector — essential */}
              <div>
                <label className={labelClass}>Table <span className="text-red-500">*</span></label>
                <SearchableTableSelect
                  value={form.dbReadConfig.table}
                  onChange={(name) => updateDbReadConfig({ table: name })}
                  tables={catalogTables}
                  loading={catalogLoading}
                  required
                />
              </div>

              {/* Aggregation — essential */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Aggregation</label>
                                    <Select
                    ariaLabel={"Aggregation"}
                    size="sm"
                    value={form.dbReadConfig.aggregation}
                    onChange={(v) => updateDbReadConfig({ aggregation: v })}
                  >
                    {AGGREGATION_TYPES.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                                    </Select>
                </div>
                <div>
                  <label className={labelClass}>Aggregation Column</label>
                                    <Select
                    ariaLabel={"Aggregation Column"}
                    size="sm"
                    value={form.dbReadConfig.aggregationColumn}
                    onChange={(v) => updateDbReadConfig({ aggregationColumn: v })}
                    disabled={form.dbReadConfig.aggregation === 'count' || !form.dbReadConfig.table}
                  >
                    <option value="">
                      {form.dbReadConfig.aggregation === 'count'
                        ? 'Not needed for count'
                        : !form.dbReadConfig.table
                          ? 'Select a table first'
                          : '— Select column —'}
                    </option>
                    {(catalogTables.find((t) => t.name === form.dbReadConfig.table)?.columns ?? [])
                      .filter((c) => c.aggregatable)
                      .map((c) => (
                        <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                      ))}
                                    </Select>
                </div>
              </div>

              {/* Advanced fields */}
              {showAdvanced && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Group By Column</label>
                                            <Select
                        ariaLabel={"Group By Column"}
                        size="sm"
                        value={form.dbReadConfig.groupBy}
                        onChange={(v) => updateDbReadConfig({ groupBy: v })}
                        disabled={!form.dbReadConfig.table}
                      >
                        <option value="">— None —</option>
                        {(catalogTables.find((t) => t.name === form.dbReadConfig.table)?.columns ?? [])
                          .filter((c) => c.groupable)
                          .map((c) => (
                            <option key={c.name} value={c.name}>{c.name}</option>
                          ))}
                                            </Select>
                    </div>
                    <div>
                      <label className={labelClass}>Max Rows</label>
                      <input
                        type="number"
                        className={inputClass}
                        value={form.dbReadConfig.maxRows}
                        onChange={(e) => updateDbReadConfig({ maxRows: e.target.value })}
                        min={1}
                        max={10000}
                      />
                      <p className="mt-1 text-[10px] text-gray-400">Safety limit. Default: 1000.</p>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Filter Template (optional)</label>
                    <input
                      className={inputClass}
                      value={form.dbReadConfig.whereTemplate}
                      onChange={(e) => updateDbReadConfig({ whereTemplate: e.target.value })}
                      placeholder="e.g., status = 'open' AND opened_at >= {{dateFrom}}"
                    />
                    <p className="mt-1 text-[10px] text-gray-400">
                      Use {'{{paramName}}'} placeholders that match input parameters. Tenant ID is auto-applied.
                    </p>
                    <ParamPlaceholdersHint params={form.params} />
                  </div>
                </>
              )}
            </div>
          )}

          {form.handlerType === 'report_template' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Report Configuration</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Format <span className="text-red-500">*</span></label>
                                    <Select
                    ariaLabel={"Format"}
                    size="sm"
                    value={form.reportConfig.format}
                    onChange={(v) => updateReportConfig({ format: v })}
                  >
                    {REPORT_FORMATS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                                    </Select>
                </div>
                <div>
                  <label className={labelClass}>Table <span className="text-red-500">*</span></label>
                  <SearchableTableSelect
                    value={form.reportConfig.table}
                    onChange={(name) => updateReportConfig({ table: name })}
                    tables={catalogTables}
                    loading={catalogLoading}
                    required
                  />
                </div>
              </div>

              {showAdvanced && (
                <>
                  <div>
                    <label className={labelClass}>Columns (comma-separated)</label>
                    <input
                      className={inputClass}
                      value={form.reportConfig.columns}
                      onChange={(e) => updateReportConfig({ columns: e.target.value })}
                      placeholder="e.g., id, status, opened_at, assigned_agent_id"
                    />
                    <p className="mt-1 text-[10px] text-gray-400">Leave empty to include all columns.</p>
                  </div>

                  <div>
                    <label className={labelClass}>Filters (optional)</label>
                    <input
                      className={inputClass}
                      value={form.reportConfig.filters}
                      onChange={(e) => updateReportConfig({ filters: e.target.value })}
                      placeholder="e.g., status = 'open' AND created_at >= {{dateFrom}}"
                    />
                    <ParamPlaceholdersHint params={form.params} />
                  </div>
                </>
              )}
            </div>
          )}

          {form.handlerType === 'internal_api' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Internal API Configuration</div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Method</label>
                                    <Select
                    ariaLabel={"Method"}
                    size="sm"
                    value={form.apiConfig.method}
                    onChange={(v) => {
                      updateApiConfig({ method: v, routes: [] })
                    }}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                                    </Select>
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>API Route <span className="text-red-500">*</span></label>
                  <SearchableRouteSelect
                    value={form.apiConfig.routes[0] ?? ''}
                    onChange={(route) => updateApiConfig({ routes: route ? [route] : [] })}
                    routes={internalRoutes}
                    loading={routesLoading}
                    method={form.apiConfig.method}
                  />
                  <p className="mt-1 text-[11px] text-gray-500">
                    An internal_api tool calls one route. Create a separate tool for each endpoint.
                  </p>
                </div>
              </div>

              {showAdvanced && (
                <div>
                  <label className={labelClass}>Timeout (ms)</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.apiConfig.timeoutMs}
                    onChange={(e) => updateApiConfig({ timeoutMs: e.target.value })}
                    min={1000}
                    max={120000}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Knowledge Search handler config ── */}
          {form.handlerType === 'knowledge_search' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Knowledge Search Configuration</div>

              <div>
                <label className={labelClass}>Collection Slug (optional)</label>
                <input
                  className={inputClass}
                  value={form.knowledgeSearchConfig.collectionSlug}
                  onChange={(e) => updateKnowledgeSearchConfig({ collectionSlug: e.target.value })}
                  placeholder="e.g., faqs, sops, product-docs (leave empty to search all)"
                />
              </div>

              {showAdvanced && (
                <div>
                  <label className={labelClass}>Max Results</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.knowledgeSearchConfig.limit}
                    onChange={(e) => updateKnowledgeSearchConfig({ limit: e.target.value })}
                    min={1}
                    max={20}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Queue Export handler config ── */}
          {form.handlerType === 'queue_export' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Queue Export Configuration</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Table <span className="text-red-500">*</span></label>
                  <SearchableTableSelect
                    value={form.queueExportConfig.table}
                    onChange={(name) => updateQueueExportConfig({ table: name })}
                    tables={catalogTables}
                    loading={catalogLoading}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Format</label>
                                    <Select
                    ariaLabel={"Format"}
                    size="sm"
                    value={form.queueExportConfig.format}
                    onChange={(v) => updateQueueExportConfig({ format: v })}
                  >
                    {REPORT_FORMATS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                                    </Select>
                </div>
              </div>

              {showAdvanced && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Max Rows</label>
                    <input
                      type="number"
                      className={inputClass}
                      value={form.queueExportConfig.maxRows}
                      onChange={(e) => updateQueueExportConfig({ maxRows: e.target.value })}
                      min={1}
                      max={1000000}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Filters (optional)</label>
                    <input
                      className={inputClass}
                      value={form.queueExportConfig.filters}
                      onChange={(e) => updateQueueExportConfig({ filters: e.target.value })}
                      placeholder="e.g., status = 'open' AND created_at >= {{dateFrom}}"
                    />
                    <ParamPlaceholdersHint params={form.params} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Lookup Query handler config ── */}
          {form.handlerType === 'lookup_query' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Lookup Query Configuration</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Lookup Table <span className="text-red-500">*</span></label>
                  <SearchableTableSelect
                    value={form.lookupQueryConfig.table}
                    onChange={(name) => updateLookupQueryConfig({ table: name })}
                    tables={catalogTables}
                    loading={catalogLoading}
                    placeholder="— Select lookup table —"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Search Column</label>
                                    <Select
                    ariaLabel={"Search Column"}
                    size="sm"
                    value={form.lookupQueryConfig.searchColumn}
                    onChange={(v) => updateLookupQueryConfig({ searchColumn: v })}
                    disabled={!form.lookupQueryConfig.table}
                  >
                    <option value="">— Select column —</option>
                    {(catalogTables.find((t) => t.name === form.lookupQueryConfig.table)?.columns ?? [])
                      .filter((c) => c.filterable && (c.type === 'text' || c.type === 'uuid'))
                      .map((c) => (
                        <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                      ))}
                                    </Select>
                </div>
              </div>
            </div>
          )}

          {/* ── Notification handler config ── */}

          {/* ── Notification handler config ── */}
          {form.handlerType === 'notification_send' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Notification Configuration</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Channel <span className="text-red-500">*</span></label>
                                    <Select
                    ariaLabel={"Channel"}
                    size="sm"
                    value={form.notificationConfig.channel}
                    onChange={(v) => updateNotificationConfig({ channel: v })}
                  >
                    {NOTIFICATION_CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                                    </Select>
                </div>
                <div>
                  <label className={labelClass}>Template <span className="text-red-500">*</span></label>
                                    <Select
                    ariaLabel={"Template"}
                    size="sm"
                    value={form.notificationConfig.templateId}
                    onChange={(v) => updateNotificationConfig({ templateId: v })}
                    disabled={templatesLoading}
                  >
                    <option value="">
                      {templatesLoading ? 'Loading templates...' : '— Select a template —'}
                    </option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                                    </Select>
                  {templates.length === 0 && !templatesLoading && (
                    <p className="mt-1 text-[10px] text-amber-600">
                      No active {form.notificationConfig.channel} templates found.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Conversation Action handler config ── */}
          {form.handlerType === 'conversation_action' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Conversation Action Configuration</div>

              <div>
                <label className={labelClass}>Action <span className="text-red-500">*</span></label>
                                <Select
                  ariaLabel={"Action"}
                  size="sm"
                  value={form.conversationActionConfig.action}
                  onChange={(v) => updateConversationActionConfig({ action: v })}
                >
                  {CONVERSATION_ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                                </Select>
              </div>
            </div>
          )}

          {/* ── Metadata Only handler guidance ── */}
          {form.handlerType === 'metadata_only' && (
            <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-900">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">Metadata-Only Reference Tool</div>
              <p className="leading-relaxed">
                This tool acts as an informational reference. When invoked by an AI agent, it returns the description and metadata defined below without querying external systems or running backend queries.
              </p>
            </div>
          )}

          {/* ── Web Scraper handler config ── */}
          {form.handlerType === 'web_scraper' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Web Scraper Configuration</div>

              {/* Rendering options */}
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2" title="Render JavaScript using headless Chrome (needed for SPAs, JS cookie checks, etc.)">
                  <input
                    type="checkbox"
                    checked={form.webScraperConfig.renderJs}
                    onChange={(e) => updateWebScraperConfig({ renderJs: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <div>
                    <span className="text-xs font-medium text-gray-700">Render JavaScript</span>
                    <p className="text-[10px] text-gray-400">Use headless Chrome for JS-rendered pages</p>
                  </div>
                </label>
                <div>
                  <label className="mb-0.5 block text-[10px] font-medium text-gray-500">JS Render Fallback</label>
                                    <Select
                    ariaLabel={"JS Render Fallback"}
                    size="sm"
                    value={form.webScraperConfig.renderJsFallback}
                    onChange={(v) => updateWebScraperConfig({ renderJsFallback: v as 'fail' | 'plain_fetch' })}
                    className="w-full"
                  >
                    <option value="fail">Fail with error (safest)</option>
                    <option value="plain_fetch">Fall back to plain HTTP fetch</option>
                                    </Select>
                </div>
              </div>

              {/* Domain controls */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Allowed Domains (comma-separated)</label>
                  <input
                    className={inputClass}
                    value={form.webScraperConfig.allowedDomains}
                    onChange={(e) => updateWebScraperConfig({ allowedDomains: e.target.value })}
                    placeholder="example.com, news.org (empty = any)"
                  />
                </div>
                <div>
                  <label className={labelClass}>Blocked Domains (comma-separated)</label>
                  <input
                    className={inputClass}
                    value={form.webScraperConfig.blockedDomains}
                    onChange={(e) => updateWebScraperConfig({ blockedDomains: e.target.value })}
                    placeholder="facebook.com, twitter.com"
                  />
                </div>
              </div>

              {/* Selector presets */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className={labelClass}>Selector Presets</label>
                  <button
                    type="button"
                    onClick={() => updateWebScraperConfig({
                      presets: [...form.webScraperConfig.presets, {
                        id: `preset_${Date.now()}`, name: '', mode: 'single',
                        containerSelector: '', containerEngine: 'css', fields: [{ name: 'value', selector: '', engine: 'css', extract: 'text' }],
                      }],
                    })}
                    className="flex items-center gap-1 rounded-sm bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-200"
                  >
                    <Plus className="h-3 w-3" /> Add Preset
                  </button>
                </div>
                {form.webScraperConfig.presets.length === 0 ? (
                  <p className="text-[11px] text-gray-400">No presets yet. Add one to define reusable selector sets for specific sites.</p>
                ) : (
                  <div className="space-y-2">
                    {form.webScraperConfig.presets.map((preset, pi) => (
                      <div key={pi} className="rounded-lg border border-gray-200 bg-white p-2.5 space-y-2">
                        <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
                          <div>
                            <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Preset Name <span className="text-red-500">*</span></label>
                            <input
                              className={inputClass}
                              value={preset.name}
                              onChange={(e) => {
                                const presets = [...form.webScraperConfig.presets]
                                presets[pi] = { ...preset, name: e.target.value }
                                updateWebScraperConfig({ presets })
                              }}
                              placeholder="e.g. page_info, product_list"
                            />
                          </div>
                          <div>
                            <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Mode</label>
                                                        <Select
                              ariaLabel={"Mode"}
                              size="sm"
                              value={preset.mode}
                              onChange={(v) => {
                                const presets = [...form.webScraperConfig.presets]
                                presets[pi] = { ...preset, mode: v as 'single' | 'list' | 'record' }
                                updateWebScraperConfig({ presets })
                              }}
                              className="w-28"
                            >
                              <option value="single">Single</option>
                              <option value="list">List</option>
                              <option value="record">Record</option>
                              <option value="full">Full Page</option>
                                                        </Select>
                          </div>
                          <button
                            type="button"
                            onClick={() => updateWebScraperConfig({ presets: form.webScraperConfig.presets.filter((_, j) => j !== pi) })}
                            className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {preset.mode === 'record' && (
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              className={inputClass}
                              value={preset.containerSelector}
                              onChange={(e) => {
                                const presets = [...form.webScraperConfig.presets]
                                presets[pi] = { ...preset, containerSelector: e.target.value }
                                updateWebScraperConfig({ presets })
                              }}
                              placeholder="Container selector (e.g. .product-card)"
                            />
                                                        <Select
                              ariaLabel={"Container Engine"}
                              size="sm"
                              value={preset.containerEngine}
                              onChange={(v) => {
                                const presets = [...form.webScraperConfig.presets]
                                presets[pi] = { ...preset, containerEngine: v as 'css' | 'xpath' }
                                updateWebScraperConfig({ presets })
                              }}
                            >
                              <option value="css">CSS</option>
                              <option value="xpath">XPath</option>
                                                        </Select>
                          </div>
                        )}
                        {preset.mode === 'full' && (
                          <p className="rounded-sm bg-blue-50 px-2 py-1.5 text-[10px] text-blue-600">
                            Full Page mode extracts all visible text content from the page — no selectors needed.
                          </p>
                        )}
                        {preset.mode !== 'full' && (
                        <div className="space-y-1.5">
                          {/* Column labels for field rows */}
                          <div className="grid grid-cols-[120px_1fr_70px_80px_24px] items-center gap-1.5 text-[9px] font-medium uppercase tracking-wide text-gray-400">
                            <span>Field Name</span>
                            <span>CSS/XPath Selector</span>
                            <span>Engine</span>
                            <span>Extract</span>
                            <span />
                          </div>
                          {preset.fields.map((field, fi) => (
                            <div key={fi} className="grid grid-cols-[120px_1fr_70px_80px_24px] items-center gap-1.5">
                              <input
                                className={inputClass}
                                value={field.name}
                                onChange={(e) => {
                                  const presets = [...form.webScraperConfig.presets]
                                  const fields = [...preset.fields]
                                  fields[fi] = { ...field, name: e.target.value }
                                  presets[pi] = { ...preset, fields }
                                  updateWebScraperConfig({ presets })
                                }}
                                placeholder="title"
                              />
                              <input
                                className={inputClass}
                                value={field.selector}
                                onChange={(e) => {
                                  const presets = [...form.webScraperConfig.presets]
                                  const fields = [...preset.fields]
                                  fields[fi] = { ...field, selector: e.target.value }
                                  presets[pi] = { ...preset, fields }
                                  updateWebScraperConfig({ presets })
                                }}
                                placeholder="e.g. h1, .title, a@href"
                              />
                                                            <Select
                                ariaLabel={"Engine"}
                                size="sm"
                                value={field.engine}
                                onChange={(v) => {
                                  const presets = [...form.webScraperConfig.presets]
                                  const fields = [...preset.fields]
                                  fields[fi] = { ...field, engine: v as 'css' | 'xpath' }
                                  presets[pi] = { ...preset, fields }
                                  updateWebScraperConfig({ presets })
                                }}
                              >
                                <option value="css">CSS</option>
                                <option value="xpath">XPath</option>
                                                            </Select>
                              <input
                                className={inputClass}
                                value={field.extract}
                                onChange={(e) => {
                                  const presets = [...form.webScraperConfig.presets]
                                  const fields = [...preset.fields]
                                  fields[fi] = { ...field, extract: e.target.value }
                                  presets[pi] = { ...preset, fields }
                                  updateWebScraperConfig({ presets })
                                }}
                                placeholder="text"
                              />
                              {preset.fields.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const presets = [...form.webScraperConfig.presets]
                                    presets[pi] = { ...preset, fields: preset.fields.filter((_, j) => j !== fi) }
                                    updateWebScraperConfig({ presets })
                                  }}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const presets = [...form.webScraperConfig.presets]
                              presets[pi] = { ...preset, fields: [...preset.fields, { name: '', selector: '', engine: 'css', extract: 'text' }] }
                              updateWebScraperConfig({ presets })
                            }}
                            className="text-[10px] font-medium text-[#3454cf] hover:underline"
                          >
                            + Add field
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* robots.txt override warning */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.webScraperConfig.ignoreRobotsTxt}
                    onChange={(e) => updateWebScraperConfig({ ignoreRobotsTxt: e.target.checked })}
                    className="h-3.5 w-3.5"
                  />
                  Override robots.txt
                </label>
                {form.webScraperConfig.ignoreRobotsTxt && (
                  <span className="text-[10px] font-medium text-amber-600">
                    ⚠ Overriding robots.txt is logged to the audit trail. Only enable if you have explicit permission to scrape disallowed paths.
                  </span>
                )}
              </div>

              {/* Advanced numeric fields */}
              {showAdvanced && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Rate Limit (ms between requests per domain)</label>
                    <input
                      className={inputClass}
                      type="number"
                      value={form.webScraperConfig.rateLimitMs}
                      onChange={(e) => updateWebScraperConfig({ rateLimitMs: e.target.value })}
                      min={0}
                      max={60000}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Timeout (ms, max 30000)</label>
                    <input
                      className={inputClass}
                      type="number"
                      value={form.webScraperConfig.timeoutMs}
                      onChange={(e) => updateWebScraperConfig({ timeoutMs: e.target.value })}
                      min={1000}
                      max={30000}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Max Response Size (bytes, max 5MB)</label>
                    <input
                      className={inputClass}
                      type="number"
                      value={form.webScraperConfig.maxResponseBytes}
                      onChange={(e) => updateWebScraperConfig({ maxResponseBytes: e.target.value })}
                      min={1024}
                      max={5242880}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Max Redirects</label>
                    <input
                      className={inputClass}
                      type="number"
                      value={form.webScraperConfig.maxRedirects}
                      onChange={(e) => updateWebScraperConfig({ maxRedirects: e.target.value })}
                      min={0}
                      max={10}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Max Records (list/record modes, max 2000)</label>
                    <input
                      className={inputClass}
                      type="number"
                      value={form.webScraperConfig.maxRecords}
                      onChange={(e) => updateWebScraperConfig({ maxRecords: e.target.value })}
                      min={1}
                      max={2000}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Custom Code handler config ── */}
          {form.handlerType === 'custom_code' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Custom Code Configuration</div>

              {/* Persistent warning notice */}
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                Custom code tools execute automatically when assigned agents call them. Review code carefully before saving.
              </div>

              {/* Language selector */}
              <div>
                <label className={labelClass}>Language</label>
                                <Select
                  ariaLabel={"Language"}
                  size="sm"
                  value={form.customCodeConfig.language}
                  onChange={(v) => setForm((prev) => ({
                    ...prev,
                    customCodeConfig: { ...prev.customCodeConfig, language: v as 'javascript' | 'python' },
                  }))}
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python" disabled={!pythonAvailable}>
                    {pythonAvailable ? 'Python' : 'Python (unavailable in this deployment)'}
                  </option>
                                </Select>
                {!pythonAvailable && (
                  <p className="mt-1 text-[10px] text-gray-400">
                    Python is not available in this deployment. Set <code>AI_CUSTOM_CODE_PYTHON_ISOLATION=true</code> and ensure <code>python3</code> is installed to enable it.
                  </p>
                )}
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.accessesTenantData}
                    onChange={(e) => setForm({ ...form, accessesTenantData: e.target.checked })}
                    className="h-3.5 w-3.5"
                  />
                  Accesses Tenant Data
                </label>
              </div>

              {/* Generate with AI */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowCodeGenerate(!showCodeGenerate)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#3454cf] hover:underline"
                >
                  {showCodeGenerate ? 'Hide' : '✨ Generate with AI'}
                </button>
                {showCodeGenerate && (
                  <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-white p-3">
                    <label className={labelClass}>Describe what the function should do</label>
                    <textarea
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs"
                      rows={3}
                      value={codeGenerateDescription}
                      onChange={(e) => setCodeGenerateDescription(e.target.value)}
                      placeholder="e.g. Take a list of order totals and return the sum, average, and max."
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleGenerateCode}
                        disabled={codeGenerating}
                        className="rounded-md bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a44b0] disabled:opacity-50"
                      >
                        {codeGenerating ? 'Generating...' : 'Generate'}
                      </button>
                      <span className="text-[10px] text-gray-400">
                        Uses the tenant's configured code model. Generated code replaces the editor below — review it before saving.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Code editor */}
              <div>
                <div className="flex items-center justify-between">
                  <label className={labelClass}>Function code</label>
                  {form.customCodeConfig.aiGenerated && (
                    <span className="rounded-sm bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                      AI-generated
                    </span>
                  )}
                </div>
                <p className="mb-1 text-[11px] text-gray-400">
                  Write a function called <code>execute(input, ctx)</code> that returns a JSON object.
                  Example: <code>function execute(input, ctx) &#123; return &#123; result: input.value * 2 &#125; &#125;</code>
                </p>
                {form.customCodeConfig.warnings.length > 0 && (
                  <div className="mb-2 rounded-md border border-red-300 bg-red-50 px-3 py-2">
                    <p className="text-[11px] font-semibold text-red-800">
                      {form.customCodeConfig.warnings.length} issue(s) flagged in generated code — review before saving:
                    </p>
                    <ul className="mt-1 list-disc pl-5">
                      {form.customCodeConfig.warnings.map((w, i) => (
                        <li key={i} className="text-[11px] text-red-700">{w.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <textarea
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-xs"
                  rows={12}
                  value={form.customCodeConfig.source}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    customCodeConfig: { ...prev.customCodeConfig, source: e.target.value, aiGenerated: false, warnings: [] },
                  }))}
                  placeholder={form.customCodeConfig.language === 'javascript'
                    ? 'function execute(input, ctx) {\n  return { result: input.value * 2 }\n}'
                    : 'def execute(input, ctx):\n    return {"result": input["value"] * 2}'}
                />
                {form.customCodeConfig.source.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      const inferredFields = extractInputFieldNames(form.customCodeConfig.source, form.customCodeConfig.language)
                      if (inferredFields.length === 0) {
                        toast.error('Nothing detected', 'No input.xxx references or destructured fields found in the code.')
                        return
                      }
                      setForm((prev) => {
                        const existingNames = new Set(prev.params.map((p) => p.name))
                        const newParams: SchemaParam[] = inferredFields
                          .filter((name) => !existingNames.has(name))
                          .map((name) => ({ name, type: 'string', description: '', required: false, enumValues: '' }))
                        return { ...prev, params: [...prev.params, ...newParams] }
                      })
                      toast.success('Fields detected', `Added: ${inferredFields.join(', ')} — review the types on the Parameters tab.`)
                    }}
                    className="mt-1.5 rounded-md border border-gray-200 px-2 py-1 text-[10px] font-medium text-[#3454cf] hover:bg-gray-50"
                  >
                    Detect fields from code
                  </button>
                )}
              </div>

              {/* Timeout and memory limit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Timeout (ms, 100–30000)</label>
                  <input
                    className={inputClass}
                    type="number"
                    value={form.customCodeConfig.timeoutMs}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      customCodeConfig: { ...prev.customCodeConfig, timeoutMs: e.target.value },
                    }))}
                    min={100}
                    max={30000}
                  />
                </div>
                <div>
                  <label className={labelClass}>Memory limit (MB, 16–512)</label>
                  <input
                    className={inputClass}
                    type="number"
                    value={form.customCodeConfig.memoryLimitMb}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      customCodeConfig: { ...prev.customCodeConfig, memoryLimitMb: e.target.value },
                    }))}
                    min={16}
                    max={512}
                  />
                </div>
              </div>
            </div>
          )}
          {form.handlerType !== 'metadata_only' && form.handlerType !== 'conversation_action' && (
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              {showAdvanced ? 'Hide' : 'Show'} advanced options
            </button>
          )}

          </>
          )}

          {activeTab === 'parameters' && (
          <>
          {/* ── Section: Parameters ── */}

          {/* Explanatory helper — what parameters are and how they connect to the handler */}
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-[11px] leading-relaxed text-blue-900">
            <div className="mb-1 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">What are parameters?</span>
            </div>
            <p>
              Parameters are the inputs the AI agent can fill in when it decides to call this tool.
              The agent sees each parameter's <strong>name</strong> and <strong>description</strong>, then
              supplies values based on the user's request.
            </p>
            <p className="mt-1.5">
              For handlers that support templating (e.g. Database Read, Report, Queue Export), reference
              these parameters in your handler config using <code className="rounded-sm bg-blue-100 px-1 font-mono text-[10px]">{'{{paramName}}'}</code> placeholders.
              For example, a parameter named <code className="rounded-sm bg-blue-100 px-1 font-mono text-[10px]">dateFrom</code> is used as <code className="rounded-sm bg-blue-100 px-1 font-mono text-[10px]">{'{{dateFrom}}'}</code>.
            </p>
            {form.handlerType === 'metadata_only' && (
              <p className="mt-1.5 italic text-blue-700">
                The Metadata-Only handler returns its description without running a query, so parameters are optional — use them only if the AI needs to pass context that shapes the returned metadata.
              </p>
            )}
          </div>

          {/* ── Input Parameters (visual schema builder) ── */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className={labelClass}>Input Parameters</label>
              <div className="flex items-center gap-1.5">
                {(HANDLER_PARAM_PRESETS[form.handlerType]?.length ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={addPresetParams}
                    className="flex items-center gap-1 rounded-lg bg-[#3454cf]/10 px-2 py-1 text-[10px] font-medium text-[#3454cf] hover:bg-[#3454cf]/20"
                    title={`Add common parameters for ${HANDLER_TYPE_LABELS[form.handlerType] ?? form.handlerType}`}
                  >
                    <Lightbulb className="h-3 w-3" /> Add common params
                  </button>
                )}
                <button
                  type="button"
                  onClick={addParam}
                  className="flex items-center gap-1 rounded-sm bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-200"
                >
                  <Plus className="h-3 w-3" /> Add Parameter
                </button>
              </div>
            </div>

            {form.params.length === 0 ? (
              <div className="rounded-sm border border-dashed border-gray-300 py-6 text-center">
                <p className="text-[11px] text-gray-400">
                  No parameters yet. This tool takes no input — the AI will call it as-is.
                </p>
                <p className="mt-1 text-[11px] text-gray-400">
                  Add a parameter when the AI needs to pass dynamic values (e.g. a date range, search query, or recipient).
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {form.params.map((param, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 bg-white p-2.5">
                    {/* Row 1: name + type + required + delete */}
                    <div className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2">
                      <div>
                        <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Name <span className="text-red-500">*</span></label>
                        <input
                          className={inputClass}
                          value={param.name}
                          onChange={(e) => updateParam(index, { name: e.target.value })}
                          placeholder="e.g. url, dateRange"
                        />
                      </div>
                      <div>
                        <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Type</label>
                                                <Select
                          ariaLabel={"Type"}
                          size="sm"
                          value={param.type}
                          onChange={(v) => updateParam(index, { type: v })}
                          className="w-32"
                        >
                          {PARAM_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                                                </Select>
                      </div>
                      <label className="flex shrink-0 items-center gap-1 pb-2 text-[10px] text-gray-600" title="Mark as required">
                        <input
                          type="checkbox"
                          checked={param.required}
                          onChange={(e) => updateParam(index, { required: e.target.checked })}
                          className="h-3.5 w-3.5"
                        />
                        Req
                      </label>
                      <button
                        type="button"
                        onClick={() => removeParam(index)}
                        className="shrink-0 rounded-sm p-1.5 pb-2 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        title="Remove parameter"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Row 2: description */}
                    <div className="mt-2">
                      <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Description</label>
                      <input
                        className={inputClass}
                        value={param.description}
                        onChange={(e) => updateParam(index, { description: e.target.value })}
                        placeholder="What this parameter means (shown to the AI)"
                      />
                    </div>
                    {/* Row 3: enum values (optional, advanced) */}
                    {showAdvanced && (
                      <input
                        className={`${inputClass} mt-2`}
                        value={param.enumValues}
                        onChange={(e) => updateParam(index, { enumValues: e.target.value })}
                        placeholder="Allowed values (comma-separated, optional) e.g., today, this_week, this_month"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Advanced toggle for parameters step */}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="mt-2 flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              {showAdvanced ? 'Hide' : 'Show'} advanced options
            </button>

            {/* JSON preview toggle — only in advanced mode */}
            {showAdvanced && (
              <button
                type="button"
                onClick={() => setShowJsonPreview(!showJsonPreview)}
                className="mt-2 flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
              >
                <ChevronRight className={`h-3 w-3 transition-transform ${showJsonPreview ? 'rotate-90' : ''}`} />
                {showJsonPreview ? 'Hide' : 'Show'} generated JSON Schema
              </button>
            )}
            {showJsonPreview && (
              <pre className="mt-1 max-h-40 overflow-auto rounded-sm bg-gray-900 p-3 text-[10px] text-gray-100">
                {JSON.stringify(generatedSchema, null, 2)}
              </pre>
            )}
          </div>
          </>
          )}



          {/* ── Compact summary (always visible) ── */}
          <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2">
            <p className="text-xs text-gray-700">
              <Sparkles className="mr-1 inline h-3 w-3 text-blue-500" />
              {handlerSummary}
            </p>
          </div>

          {/* ── AI-facing preview + JSON schema (advanced only) ── */}
          {showAdvanced && (
            <div className="rounded-sm border border-gray-200 bg-gray-900 p-3">
              <button
                type="button"
                onClick={() => setShowAiPreview(!showAiPreview)}
                className="flex w-full items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-300"
              >
                <Eye className="h-3 w-3" />
                AI-facing preview
                <ChevronDown className={`ml-auto h-3 w-3 transition-transform ${showAiPreview ? 'rotate-180' : ''}`} />
              </button>
              {showAiPreview && (
                <div className="mt-2 space-y-2 text-[10px] text-gray-300">
                  <div>
                    <span className="text-gray-500">name: </span>
                    <span className="font-mono text-blue-300">{form.name || '<unnamed>'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">description: </span>
                    <span className="text-gray-200">{form.description || '<no description>'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">handler: </span>
                    <span className="font-mono text-green-300">{form.handlerType}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">parameters:</span>
                    {form.params.length === 0 ? (
                      <span className="ml-1 text-gray-500">none</span>
                    ) : (
                      <div className="ml-3 mt-1 space-y-1 border-l border-gray-700 pl-2">
                        {form.params.filter((p) => p.name).map((p) => (
                          <div key={p.name} className="flex items-baseline gap-1">
                            <span className="font-mono text-yellow-300">{p.name}</span>
                            <span className="text-gray-500">:</span>
                            <span className="font-mono text-purple-300">{p.type}</span>
                            {p.required && <span className="text-red-400">*required</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">json_schema:</span>
                    <pre className="mt-1 max-h-32 overflow-auto rounded-sm bg-black/40 p-2 text-[9px] text-gray-400">
                      {JSON.stringify(generatedSchema, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Test inputs — sample parameter values */}
        {form.params.some((p) => p.name) && !pastedHtmlMode && (
          <div className="shrink-0 mx-4 mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Test Inputs (optional)</div>
            <div className="grid grid-cols-2 gap-2">
              {form.params.filter((p) => p.name).map((p) => (
                <div key={p.name}>
                  <label className="mb-0.5 block text-[10px] font-mono text-gray-600">
                    {p.name}
                    {p.required && <span className="text-red-500"> *</span>}
                    <span className="ml-1 text-gray-400">({p.type})</span>
                  </label>
                  <input
                    type={p.type === 'number' ? 'number' : p.type === 'boolean' ? 'text' : 'text'}
                    value={testInputs[p.name] ?? ''}
                    onChange={(e) => setTestInputs({ ...testInputs, [p.name]: e.target.value })}
                    placeholder={p.type === 'boolean' ? 'true or false' : p.description || `Enter ${p.name}`}
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                  />
                </div>
              ))}
            </div>
            {form.params.some((p) => p.enumValues) && (
              <p className="mt-1.5 text-[9px] text-gray-400">Enum params: {form.params.filter((p) => p.enumValues).map((p) => `${p.name}: ${p.enumValues}`).join(', ')}</p>
            )}
          </div>
        )}

        {/* Section 5: Pasted-HTML selector testing toggle (web_scraper only) */}
        {form.handlerType === 'web_scraper' && (
          <div className="shrink-0 mx-4 mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={pastedHtmlMode}
                onChange={(e) => { setPastedHtmlMode(e.target.checked); setTestResult(null) }}
                className="h-4 w-4"
              />
              <span className="text-xs font-medium text-gray-700">Test against pasted HTML</span>
              <span className="text-[10px] text-gray-400">— skip network fetch, test selectors directly</span>
            </label>
            {pastedHtmlMode && (
              <div className="mt-2">
                <label className="mb-0.5 block text-[10px] font-medium text-gray-500">Paste HTML to test selectors against</label>
                <textarea
                  value={pastedHtml}
                  onChange={(e) => setPastedHtml(e.target.value)}
                  placeholder="<html><body>...paste page HTML here to test your selectors...</body></html>"
                  rows={8}
                  className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 font-mono text-[10px] text-gray-700 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                />
                <p className="mt-1 text-[10px] text-gray-400">
                  Uses the first preset's mode and fields. No network request is made — SSRF, robots.txt, and rate limiting are skipped.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Test result display */}
        {testResult && (
          <div className="shrink-0 mx-4 mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                <Play className="h-3 w-3" />
                Test Result
              </span>
              <button onClick={() => setTestResult(null)} className="text-gray-400 hover:text-gray-700">
                <X className="h-3 w-3" />
              </button>
            </div>
            {testResult.error ? (
              <div className="rounded-sm border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-600">
                {testResult.error}
              </div>
            ) : testResult.success !== undefined ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-[10px] text-gray-500">
                  <span className={`font-medium ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                    {testResult.success ? '✓ Success' : '✗ Failed'}
                  </span>
                  {testResult.executionTimeMs !== undefined && (
                    <span>{testResult.executionTimeMs}ms</span>
                  )}
                  {testResult.timedOut && (
                    <span className="font-medium text-amber-600">⏱ Timed out</span>
                  )}
                </div>
                {testResult.message && (
                  <p className="text-[10px] text-gray-600">{testResult.message}</p>
                )}
                {testResult.customResult !== undefined && (
                  <pre className="max-h-48 overflow-auto rounded-md bg-gray-900 p-2 text-[10px] text-gray-100">
                    {JSON.stringify(testResult.customResult, null, 2)}
                  </pre>
                )}
              </div>
            ) : testResult.data !== undefined ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-[10px] text-gray-500">
                  <span className="font-medium text-green-600">HTTP {testResult.status}</span>
                  <span>{testResult.mode} mode</span>
                  <span>{testResult.bytes} bytes</span>
                  {testResult.robotsPolicy && <span>robots: {testResult.robotsPolicy}</span>}
                </div>
                {testResult.modeSource === 'defaulted_full_no_selectors' && (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-700">
                    ⚠ No selector preset or field config was provided — this ran in Full Page mode and returned raw page text. Add a preset or inline selectors to extract structured data.
                  </div>
                )}
                {testResult.renderJsFallbackUsed && (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-700">
                    ⚠ JavaScript rendering was requested but Chrome was unavailable. Fell back to plain HTTP fetch — JS-rendered content may be missing.
                  </div>
                )}
                {testResult.truncated && (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-700">
                    ⚠ Results truncated: found {testResult.matchedCount} matches but only returned the first {Array.isArray(testResult.data) ? testResult.data.length : testResult.matchedCount}. Increase maxRecords to see more.
                  </div>
                )}
                {testResult.finalUrl && testResult.finalUrl !== testResult.url && (
                  <p className="text-[10px] text-gray-400">Redirected to: {testResult.finalUrl}</p>
                )}
                {testResult.robotsOverride && (
                  <p className="text-[10px] font-medium text-amber-600">⚠ robots.txt override: {testResult.robotsOverride.rule}</p>
                )}
                <ScrapeDataPreview data={testResult.data ?? null} {...(testResult.mode ? { mode: testResult.mode } : {})} />
              </div>
            ) : (
              <div className="space-y-2">
                {testResult.simulated ? (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 p-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Simulated result</p>
                    <p className="mt-1 text-xs text-gray-600">{testResult.message}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 text-[10px] text-gray-500">
                      <span className="font-medium text-green-600">{testResult.rowCount} row{testResult.rowCount !== 1 ? 's' : ''} returned</span>
                      <span>Columns: {testResult.columns?.join(', ')}</span>
                    </div>
                    {testResult.sampleRows && testResult.sampleRows.length > 0 && (
                      <div className="max-h-40 overflow-auto rounded-sm border border-gray-200 bg-white">
                        <table className="w-full text-[10px]">
                          <thead className="bg-gray-100 text-gray-600">
                            <tr>
                              {(testResult.columns ?? []).slice(0, 6).map((col: string) => (
                                <th key={col} className="px-2 py-1 text-left font-medium">{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {testResult.sampleRows.map((row: Record<string, unknown>, i: number) => (
                              <tr key={i} className="border-t border-gray-100">
                                {(testResult.columns ?? []).slice(0, 6).map((col: string) => (
                                  <td key={col} className="px-2 py-1 text-gray-700">
                                    {String(row[col] ?? '').slice(0, 40)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {testResult.truncated && (
                      <p className="text-[10px] text-gray-400">Showing first 5 rows. More results available.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer — fixed at bottom */}
        <div className="shrink-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white p-4">
          {/* Left: Back (when not on first tab) + Test */}
          <div className="flex items-center gap-2">
            {activeTab !== TOOL_TAB_ORDER[0] && (
              <button
                type="button"
                onClick={() => {
                  const idx = TOOL_TAB_ORDER.indexOf(activeTab)
                  if (idx > 0) setActiveTab(TOOL_TAB_ORDER[idx - 1]!)
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#3454cf] px-4 py-2 text-sm font-medium text-[#3454cf] transition-colors hover:bg-blue-50 disabled:opacity-50"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {pastedHtmlMode ? 'Test Selectors' : 'Test'}
            </button>
          </div>
          {/* Right: Cancel + Next/Save */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            {activeTab !== TOOL_TAB_ORDER[TOOL_TAB_ORDER.length - 1] ? (
              <button
                type="button"
                onClick={() => {
                  const idx = TOOL_TAB_ORDER.indexOf(activeTab)
                  if (idx < TOOL_TAB_ORDER.length - 1) setActiveTab(TOOL_TAB_ORDER[idx + 1]!)
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#3454cf] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2a45b0]"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#3454cf] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {mode === 'create' ? 'Create Tool' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tools Panel (table view) ───────────────────────────────────────────────

function ToolsPanel({ agents }: { agents: Agent[] }): JSX.Element {
  const toast = useToast()
  const { tables: catalogTables, loading: catalogLoading } = useSchemaCatalog()
  const { routes: internalRoutes, loading: routesLoading } = useInternalRoutes()
  const [tools, setTools] = useState<ToolCatalogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'builtin' | 'custom'>('all')
  const [handlerFilter, setHandlerFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [sortBy, setSortBy] = useState<'updated' | 'name' | 'created' | 'handler'>('updated')
  const [expandedTool, setExpandedTool] = useState<string | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create')
  const [editTool, setEditTool] = useState<ToolEditorState | null>(null)
  const [editToolName, setEditToolName] = useState<string>('')
  const [deleting, setDeleting] = useState<string | null>(null)
  const [toolVersions, setToolVersions] = useState<Record<string, Array<{ version: number; changed_at: string; changed_by: string | null; description: string; handler_type: string }>>>({})
  const [loadingVersions, setLoadingVersions] = useState<string | null>(null)
  const [rollingBack, setRollingBack] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const loadTools = useCallback(() => {
    setLoading(true)
    api
      .get<{ tools: ToolCatalogEntry[] }>('/admin/ai/tools')
      .then((res) => setTools(res.tools ?? []))
      .catch((err) => {
        toast.error('Failed to load tools:', err instanceof Error ? err.message : String(err))
        toast.error('Failed to load tools', (err as Error).message)
      })
      .finally(() => setLoading(false))
  }, [toast])

  useEffect(() => {
    loadTools()
  }, [loadTools])

  const usageMap = useMemo(() => buildToolUsageMap(agents), [agents])

  const filteredTools = useMemo(() => {
    const q = search.trim().toLowerCase()
    let filtered = tools

    // Text search
    if (q) {
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q),
      )
    }

    // Type filter
    if (typeFilter === 'builtin') {
      filtered = filtered.filter((t) => t.isCustom !== true)
    } else if (typeFilter === 'custom') {
      filtered = filtered.filter((t) => t.isCustom === true)
    }

    // Handler filter
    if (handlerFilter !== 'all') {
      filtered = filtered.filter((t) => (t.handlerType ?? 'metadata_only') === handlerFilter)
    }

    // Status filter
    if (statusFilter === 'enabled') {
      filtered = filtered.filter((t) => t.isCustom !== true || t.isEnabled !== false)
    } else if (statusFilter === 'disabled') {
      filtered = filtered.filter((t) => t.isCustom === true && t.isEnabled === false)
    }

    // Sort
    return [...filtered].sort((a, b) => {
      // Custom tools always come first
      const aCustom = a.isCustom === true
      const bCustom = b.isCustom === true
      if (aCustom && !bCustom) return -1
      if (!aCustom && bCustom) return 1

      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'created': {
          const aDate = a.createdAt ?? ''
          const bDate = b.createdAt ?? ''
          return bDate.localeCompare(aDate)
        }
        case 'handler':
          return (a.handlerType ?? '').localeCompare(b.handlerType ?? '')
        case 'updated':
        default: {
          if (aCustom && bCustom) {
            const aDate = a.updatedAt ?? a.createdAt ?? ''
            const bDate = b.updatedAt ?? b.createdAt ?? ''
            if (aDate !== bDate) return bDate.localeCompare(aDate)
            return a.name.localeCompare(b.name)
          }
          return a.name.localeCompare(b.name)
        }
      }
    })
  }, [tools, search, typeFilter, handlerFilter, statusFilter, sortBy])

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [search, typeFilter, handlerFilter, statusFilter, sortBy])

  const builtinCount = tools.filter((t) => !t.isCustom).length
  const customCount = tools.filter((t) => t.isCustom).length

  // Pagination
  const totalCount = filteredTools.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginatedTools = filteredTools.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endIdx = Math.min(currentPage * pageSize, totalCount)

  const handleCreate = (): void => {
    setEditMode('create')
    setEditTool(null)
    setEditToolName('')
    setShowEditor(true)
  }

  const handleEdit = (tool: ToolCatalogEntry): void => {
    setEditMode('edit')
    setEditToolName(tool.name)
    setEditTool(toolToEditorState(tool))
    setShowEditor(true)
  }

  const handleClone = (tool: ToolCatalogEntry): void => {
    const cloned = toolToEditorState(tool)
    cloned.name = `${tool.name}_copy`
    cloned.description = tool.description
    setEditMode('create')
    setEditToolName('')
    setEditTool(cloned)
    setShowEditor(true)
  }

  const handleSave = async (data: Record<string, unknown>): Promise<void> => {
    if (editMode === 'create') {
      await api.post('/admin/ai/tools', data)
      toast.success('Tool created', `Tool "${data['name']}" has been created successfully`)
    } else {
      await api.put(`/admin/ai/tools/${editToolName}`, data)
      toast.success('Tool updated', `Tool "${editToolName}" has been updated successfully`)
    }
    loadTools()
  }

  const handleDelete = (tool: ToolCatalogEntry): void => {
    const usedBy = usageMap.get(tool.name) ?? []
    const agentNames = usedBy.map((slug) => agents.find((a) => a.slug === slug)?.name ?? slug)
    const hasAgents = agentNames.length > 0

    confirmDialog({
      message: hasAgents
        ? `Delete tool "${tool.name}"?\n\n⚠️ This tool is currently used by ${agentNames.length} agent${agentNames.length !== 1 ? 's' : ''}: ${agentNames.join(', ')}.\n\nThose agents will lose access to this tool. This action cannot be undone.`
        : `Delete tool "${tool.name}"?\n\nThis tool is not used by any agent. This action cannot be undone.`,
      header: 'Delete Custom Tool',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setDeleting(tool.name)
        try {
          await api.delete(`/admin/ai/tools/${tool.name}`)
          toast.success('Tool deleted', `Tool "${tool.name}" has been deleted${hasAgents ? ` and removed from ${agentNames.length} agent${agentNames.length !== 1 ? 's' : ''}` : ''}`)
          loadTools()
        } catch (err) {
          const msg = (err as Error).message || 'Unknown error'
          toast.error('Failed to delete tool', msg)
        } finally {
          setDeleting(null)
        }
      },
    })
  }

  const loadVersions = useCallback(async (toolName: string): Promise<void> => {
    setLoadingVersions(toolName)
    try {
      const res = await api.get<{ versions: Array<{ version: number; changed_at: string; changed_by: string | null; description: string; handler_type: string }> }>(`/admin/ai/tools/${toolName}/versions`)
      setToolVersions((prev) => ({ ...prev, [toolName]: res.versions ?? [] }))
    } catch (err) {
      toast.error('Failed to load versions:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoadingVersions(null)
    }
  }, [])

  const handleRollback = useCallback(async (toolName: string, version: number): Promise<void> => {
    confirmDialog({
      message: `Rollback "${toolName}" to version ${version}?\n\nThe current configuration will be saved as a new version before rolling back.`,
      header: 'Rollback Tool',
      icon: 'pi pi-history',
      accept: async () => {
        setRollingBack(`${toolName}:${version}`)
        try {
          await api.post(`/admin/ai/tools/${toolName}/rollback/${version}`)
          toast.success('Tool rolled back', `Tool "${toolName}" has been rolled back to version ${version}`)
          loadTools()
          loadVersions(toolName)
        } catch (err) {
          const msg = (err as Error).message || 'Unknown error'
          toast.error('Failed to rollback', msg)
        } finally {
          setRollingBack(null)
        }
      },
    })
  }, [confirmDialog, toast, loadTools, loadVersions])

  /** Format an ISO timestamp as a short relative date string. */
  const formatTimestamp = (iso?: string): string => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return '—'
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHr / 24)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 30) return `${diffDay}d ago`
    return d.toLocaleDateString()
  }

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Tool Catalog</h3>
          <p className="text-xs text-gray-500">
            {tools.length} tools ({builtinCount} built-in, {customCount} custom). Built-in tools are registered in code; custom tools are managed from this UI.
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a44a8]"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Tool
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Text search */}
        <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 py-2 focus-within:border-[#3454cf]">
          <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tools by name or description..."
            className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-gray-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* Type filter */}
                <Select
          ariaLabel={"Type filter"}
          size="sm"
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as 'all' | 'builtin' | 'custom')}
        >
          <option value="all">All Types</option>
          <option value="custom">Custom Only</option>
          <option value="builtin">Built-in Only</option>
                </Select>
        {/* Handler filter */}
                <Select
          ariaLabel={"Handler filter"}
          size="sm"
          value={handlerFilter}
          onChange={(v) => setHandlerFilter(v)}
        >
          <option value="all">All Handlers</option>
          <option value="metadata_only">Metadata Only</option>
          <option value="database_read_template">DB Read</option>
          <option value="report_template">Report</option>
          <option value="internal_api">Internal API</option>
          <option value="knowledge_search">Knowledge Search</option>
          <option value="queue_export">Queue Export</option>
          <option value="lookup_query">Lookup Query</option>
          <option value="notification_send">Notification</option>
          <option value="conversation_action">Conversation Action</option>
          <option value="web_scraper">Web Scraper</option>
          <option value="custom_code">Custom Code</option>
                </Select>
        {/* Status filter */}
                <Select
          ariaLabel={"Status filter"}
          size="sm"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as 'all' | 'enabled' | 'disabled')}
          disabled={true}
        >
          <option value="all">All Status</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
                </Select>
        {/* Clear filters button — shown when any filter is active */}
        {(search || typeFilter !== 'all' || handlerFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => {
              setSearch('')
              setTypeFilter('all')
              setHandlerFilter('all')
              setStatusFilter('all')
            }}
            className="flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <X className="h-3 w-3" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Tool table */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tools...
        </div>
      ) : filteredTools.length === 0 ? (
        <div className="rounded-sm border border-gray-200 bg-white py-12 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          {tools.length === 0 ? (
            /* No tools at all — getting started guide */
            <>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#3454cf]/10">
                <Wrench className="h-6 w-6 text-[#3454cf]" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">No custom tools yet</h4>
              <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
                Custom tools let the AI agent query your data, search knowledge bases, send notifications, and more — all with tenant-scoped security.
              </p>
              <div className="mx-auto mt-4 max-w-md rounded-sm border border-gray-100 bg-gray-50/50 p-3 text-left">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Quick start:</p>
                <ol className="space-y-1.5 text-[11px] text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">1</span>
                    Click <span className="font-medium text-[#3454cf]">"Create Tool"</span> above
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">2</span>
                    Pick a template or start from scratch
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">3</span>
                    Configure the handler, parameters, and permissions
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">4</span>
                    Test it before saving, then attach it to an agent
                  </li>
                </ol>
              </div>
              <button
                onClick={handleCreate}
                className="mx-auto mt-4 flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-medium text-white hover:bg-[#2a44a8]"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Your First Tool
              </button>
            </>
          ) : (
            /* Tools exist but filtered out */
            <>
              <Wrench className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No tools match your filters</p>
              <button
                onClick={() => { setSearch(''); setTypeFilter('all'); setHandlerFilter('all'); setStatusFilter('all') }}
                className="mt-2 text-xs font-medium text-[#3454cf] hover:underline"
              >
                Clear all filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <table className="w-full text-left text-xs">
            {/* Table header */}
            <thead className="border-b border-gray-200 bg-gray-50/50">
              <tr>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy('name')} className="flex items-center gap-1 hover:text-gray-700">
                    Name
                    {sortBy === 'name' && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Description</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Type</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy('handler')} className="flex items-center gap-1 hover:text-gray-700">
                    Handler
                    {sortBy === 'handler' && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Permissions</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Status</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">Agents</th>
                <th className="px-4 py-2.5 font-semibold text-gray-500">
                  <button onClick={() => setSortBy(sortBy === 'created' ? 'updated' : 'created')} className="flex items-center gap-1 hover:text-gray-700">
                    {sortBy === 'created' ? 'Created' : 'Updated'}
                    {(sortBy === 'updated' || sortBy === 'created') && <ChevronDown className="h-3 w-3" />}
                  </button>
                </th>
                <th className="px-4 py-2.5 text-right font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="divide-y divide-gray-50">
              {paginatedTools.map((tool) => {
                const isExpanded = expandedTool === tool.name
                const usedBy = usageMap.get(tool.name) ?? []
                const requiredParams = getRequiredParams(tool.inputSchema)
                const allParams = getAllParams(tool.inputSchema)
                const optionalParams = allParams.filter((p) => !requiredParams.includes(p))
                const isCustom = tool.isCustom === true

                return (
                  <React.Fragment key={tool.name}>
                    <tr className={`transition-colors ${isExpanded ? 'bg-gray-50/50' : 'hover:bg-gray-50/30'}`}>
                      {/* Name */}
                      <td className="px-4 py-3">
                        <button
                          className="flex items-center gap-1.5 text-left"
                          onClick={() => setExpandedTool(isExpanded ? null : tool.name)}
                        >
                          <Wrench className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          <span className="font-mono font-semibold text-gray-900">{tool.name}</span>
                          {tool.category && (
                            <span className={`rounded-sm px-1 py-0.5 text-[9px] font-medium ${
                              tool.category === 'routing' ? 'bg-orange-50 text-orange-600' :
                              tool.category === 'knowledge' ? 'bg-green-50 text-green-600' :
                              tool.category === 'data' ? 'bg-blue-50 text-blue-600' :
                              tool.category === 'file-generation' ? 'bg-yellow-50 text-yellow-700' :
                              tool.category === 'custom' ? 'bg-purple-50 text-purple-600' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {tool.category.toUpperCase()}
                            </span>
                          )}
                          {isCustom && !tool.category && (
                            <span className="rounded-sm bg-purple-50 px-1 py-0.5 text-[9px] font-medium text-purple-600">CUSTOM</span>
                          )}
                          {tool.accessesTenantData && (
                            <span className="flex items-center gap-0.5 rounded-sm bg-blue-50 px-1 py-0.5 text-[9px] font-medium text-blue-600" title="Accesses tenant data">
                              <Shield className="h-2.5 w-2.5" />
                            </span>
                          )}
                          {isCustom && tool.isEnabled === false && (
                            <span className="rounded-sm bg-gray-100 px-1 py-0.5 text-[9px] font-medium text-gray-500">DISABLED</span>
                          )}
                        </button>
                      </td>
                      {/* Description */}
                      <td className="max-w-xs px-4 py-3">
                        <p className="truncate text-[11px] leading-relaxed text-gray-500" title={tool.description}>
                          {tool.description}
                        </p>
                      </td>
                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${
                          isCustom ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {isCustom ? 'Custom' : 'Built-in'}
                        </span>
                      </td>
                      {/* Handler */}
                      <td className="px-4 py-3">
                        {isCustom ? (
                          <span className="text-[10px] text-gray-500">{HANDLER_TYPE_LABELS[tool.handlerType ?? 'metadata_only'] ?? tool.handlerType}</span>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      {/* Permissions */}
                      <td className="px-4 py-3">
                        {tool.requiredPermissions.length === 0 ? (
                          <span className="text-[10px] text-gray-400">None</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {tool.requiredPermissions.slice(0, 2).map((perm) => (
                              <span key={perm} className="rounded-sm bg-gray-100 px-1 py-0.5 font-mono text-[9px] text-gray-600">
                                {perm}
                              </span>
                            ))}
                            {tool.requiredPermissions.length > 2 && (
                              <span className="text-[9px] text-gray-400">+{tool.requiredPermissions.length - 2}</span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* Status / Health */}
                      <td className="px-4 py-3">
                        {isCustom ? (
                          <div className="flex flex-col gap-1">
                            <span className={`flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${
                              tool.isEnabled === false
                                ? 'bg-gray-100 text-gray-500'
                                : 'bg-green-50 text-green-600'
                            }`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${tool.isEnabled === false ? 'bg-gray-400' : 'bg-green-500'}`} />
                              {tool.isEnabled === false ? 'Disabled' : 'Active'}
                            </span>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1 rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                            Built-in
                          </span>
                        )}
                      </td>
                      {/* Agents */}
                      <td className="px-4 py-3">
                        {usedBy.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1" title={`Used by: ${usedBy.join(', ')}`}>
                            {usedBy.slice(0, 2).map((slug) => {
                              const agent = agents.find((a) => a.slug === slug)
                              return (
                                <span key={slug} className="flex items-center gap-0.5 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600">
                                  <Bot className="h-2.5 w-2.5" />
                                  {agent?.name ?? slug}
                                </span>
                              )
                            })}
                            {usedBy.length > 2 && (
                              <span className="text-[9px] text-gray-400">+{usedBy.length - 2} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">Unused</span>
                        )}
                      </td>
                      {/* Updated */}
                      <td className="px-4 py-3">
                        <span className="text-[10px] text-gray-400" title={tool.updatedAt ?? tool.createdAt ?? ''}>
                          {formatTimestamp(tool.updatedAt ?? tool.createdAt)}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isCustom && (
                            <>
                              <button
                                onClick={() => handleEdit(tool)}
                                className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-[#3454cf]"
                                title="Edit tool"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleClone(tool)}
                                className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-green-600"
                                title="Duplicate tool"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(tool)}
                                disabled={deleting === tool.name}
                                className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                title="Delete tool"
                              >
                                {deleting === tool.name ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setExpandedTool(isExpanded ? null : tool.name)}
                            className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            title={isExpanded ? 'Collapse' : 'Expand details'}
                          >
                            {isExpanded ? <ChevronRight className="h-3.5 w-3.5 rotate-90" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded details row */}
                    {isExpanded && (
                      <tr className="bg-gray-50/50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="ml-5 space-y-3 border-l border-gray-200 pl-4">
                            {/* Full description */}
                            <div>
                              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Description</div>
                              <p className="text-[11px] leading-relaxed text-gray-600">{tool.description}</p>
                            </div>

                            {/* Input parameters */}
                            <div>
                              <div className="mb-1 flex items-center gap-1">
                                <Settings className="h-3 w-3 text-gray-400" />
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Input Parameters</span>
                              </div>
                              {allParams.length === 0 ? (
                                <p className="text-[10px] text-gray-400">No parameters — this tool takes no input.</p>
                              ) : (
                                <div className="space-y-1">
                                  {requiredParams.map((param) => {
                                    const prop = (tool.inputSchema['properties'] as Record<string, { type?: string; description?: string }>)?.[param]
                                    return (
                                      <div key={param} className="flex items-baseline gap-2 text-[11px]">
                                        <span className="font-mono font-medium text-gray-700">{param}</span>
                                        <span className="rounded-sm bg-red-50 px-1 py-0.5 text-[9px] font-medium text-red-500">REQUIRED</span>
                                        {prop?.type && <span className="font-mono text-[10px] text-gray-400">{prop.type}</span>}
                                        {prop?.description && <span className="text-gray-500">{prop.description}</span>}
                                      </div>
                                    )
                                  })}
                                  {optionalParams.map((param) => {
                                    const prop = (tool.inputSchema['properties'] as Record<string, { type?: string; description?: string }>)?.[param]
                                    return (
                                      <div key={param} className="flex items-baseline gap-2 text-[11px]">
                                        <span className="font-mono font-medium text-gray-600">{param}</span>
                                        <span className="rounded-sm bg-gray-100 px-1 py-0.5 text-[9px] font-medium text-gray-400">OPTIONAL</span>
                                        {prop?.type && <span className="font-mono text-[10px] text-gray-400">{prop.type}</span>}
                                        {prop?.description && <span className="text-gray-500">{prop.description}</span>}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Used by agents */}
                            <div>
                              <div className="mb-1 flex items-center gap-1">
                                <Bot className="h-3 w-3 text-gray-400" />
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Used By Agents</span>
                              </div>
                              {usedBy.length === 0 ? (
                                <p className="text-[10px] text-gray-400">Not attached to any agent yet.</p>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {usedBy.map((slug) => {
                                    const agent = agents.find((a) => a.slug === slug)
                                    return (
                                      <span key={slug} className="rounded-sm bg-[#3454cf]/5 px-1.5 py-0.5 text-[10px] font-medium text-[#3454cf]">
                                        {agent?.name ?? slug}
                                      </span>
                                    )
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Version history (custom tools only) */}
                            {isCustom && (
                              <div>
                                <div className="mb-1 flex items-center gap-1">
                                  <History className="h-3 w-3 text-gray-400" />
                                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Version History</span>
                                  {toolVersions[tool.name] === undefined && (
                                    <button
                                      onClick={() => loadVersions(tool.name)}
                                      disabled={loadingVersions === tool.name}
                                      className="ml-1 text-[10px] font-medium text-[#3454cf] hover:underline disabled:opacity-50"
                                    >
                                      {loadingVersions === tool.name ? 'Loading...' : 'Load history'}
                                    </button>
                                  )}
                                </div>
                                {(() => {
                                  const versions = toolVersions[tool.name]
                                  if (versions === undefined) {
                                    return <p className="text-[10px] text-gray-400">Click "Load history" to view past versions.</p>
                                  }
                                  if (versions.length === 0) {
                                    return <p className="text-[10px] text-gray-400">No version history yet. Versions are saved when you edit this tool.</p>
                                  }
                                  return (
                                  <div className="space-y-1">
                                    {versions.map((v) => (
                                      <div key={v.version} className="flex items-center gap-2 rounded-sm border border-gray-100 bg-white px-2 py-1.5">
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-600">v{v.version}</span>
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-[10px] text-gray-600">{v.description}</p>
                                          <p className="text-[9px] text-gray-400">{formatTimestamp(v.changed_at)} · {v.handler_type}</p>
                                        </div>
                                        <button
                                          onClick={() => handleRollback(tool.name, v.version)}
                                          disabled={rollingBack === `${tool.name}:${v.version}`}
                                          className="shrink-0 rounded-sm border border-gray-200 px-2 py-0.5 text-[9px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                          {rollingBack === `${tool.name}:${v.version}` ? 'Rolling back...' : 'Rollback'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  )
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between rounded-sm border border-gray-200 bg-white px-4 py-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          {/* Left: results info + page size */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500">
              Showing <span className="font-medium text-gray-700">{startIdx}-{endIdx}</span> of <span className="font-medium text-gray-700">{totalCount}</span> tool{totalCount !== 1 ? 's' : ''}
            </span>
                        <Select
              ariaLabel={"Page Size"}
              size="sm"
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
            >
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
                        </Select>
          </div>

          {/* Right: page navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="First page"
              >
                «
              </button>
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-3 w-3" />
                Prev
              </button>

              {/* Page number buttons */}
              {(() => {
                const pages: number[] = []
                const maxButtons = 5
                let start = Math.max(1, currentPage - Math.floor(maxButtons / 2))
                const end = Math.min(totalPages, start + maxButtons - 1)
                start = Math.max(1, end - maxButtons + 1)
                for (let i = start; i <= end; i++) pages.push(i)
                return pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`h-7 w-7 rounded-sm text-[11px] font-medium ${
                      p === currentPage
                        ? 'bg-[#3454cf] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))
              })()}

              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="Last page"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tool editor dialog */}
      {showEditor && (
        <ToolEditorDialog
          mode={editMode}
          tool={editTool}
          catalogTables={catalogTables}
          catalogLoading={catalogLoading}
          internalRoutes={internalRoutes}
          routesLoading={routesLoading}
          onClose={() => setShowEditor(false)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Agent View Dialog — read-only detail view opened from the agent table
// ---------------------------------------------------------------------------

interface AgentHistoryEntry {
  id: string
  agentId: string
  action: string
  changedBy: string
  changedAt: string
  changeNote?: string | null
  diff: Record<string, { before: unknown; after: unknown }>
}

function AgentViewDialog({ agent, onClose, onRestored }: { agent: Agent | null; onClose: () => void; onRestored?: () => void }): JSX.Element | null {
  const toast = useToast()
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState<AgentHistoryEntry[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [restoring, setRestoring] = useState<string | null>(null)

  const fetchHistory = useCallback(async () => {
    if (!agent) return
    setLoadingHistory(true)
    try {
      const res = await api.get<{ history: AgentHistoryEntry[] }>(`/admin/ai/agents/${agent.slug}/history`)
      setHistory(res.history ?? [])
    } catch (err) {
      toast.error('Failed to load history:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoadingHistory(false)
    }
  }, [agent, toast])

  useEffect(() => {
    if (showHistory && agent) void fetchHistory()
  }, [showHistory, agent, fetchHistory])

  const handleRestore = async (historyId: string) => {
    if (!agent) return
    setRestoring(historyId)
    try {
      await api.post(`/admin/ai/agents/${agent.slug}/history/${historyId}/restore`, {})
      toast.success('Agent state restored from history snapshot')
      onRestored?.()
      void fetchHistory()
    } catch (err) {
      toast.error('Failed to restore:', err instanceof Error ? err.message : String(err))
    } finally {
      setRestoring(null)
    }
  }

  if (!agent) return null
  const meta = AGENT_TYPE_META[agent.type] ?? AGENT_TYPE_META['custom']!

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-8 w-8 items-center justify-center rounded-sm ${meta.iconBg}`}>
              {meta.icon}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{agent.name}</h3>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-gray-400">{agent.slug}</span>
                {agent.is_system && (
                  <span className="rounded-sm bg-[#3454cf]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#3454cf]">SYSTEM</span>
                )}
                <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${agent.is_enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                  {agent.is_enabled ? 'ENABLED' : 'DISABLED'}
                </span>
                <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${meta.text} opacity-70`}>{meta.label}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-sm p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {/* Description */}
          <div>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Description</h4>
            <p className="text-xs leading-relaxed text-gray-600">{agent.description || 'No description provided.'}</p>
          </div>

          {/* Tools */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <Wrench className="h-3 w-3 text-gray-400" />
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Tools ({agent.tools.length})</h4>
            </div>
            {agent.tools.length === 0 ? (
              <p className="text-[11px] text-gray-400">No tools attached.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {agent.tools.map((tool) => (
                  <span
                    key={tool.tool_name}
                    className={`rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${tool.is_enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}
                  >
                    {tool.tool_name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Knowledge Collections */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <Database className="h-3 w-3 text-gray-400" />
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Knowledge ({agent.knowledge_collections.length})</h4>
            </div>
            {agent.knowledge_collections.length === 0 ? (
              <p className="text-[11px] text-gray-400">No knowledge collections attached.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {agent.knowledge_collections.map((kc) => (
                  <span key={kc.id} className="rounded-sm bg-purple-50 px-1.5 py-0.5 text-[11px] font-medium text-purple-600">
                    {kc.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* System Prompt */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <Settings className="h-3 w-3 text-gray-400" />
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">System Prompt</h4>
            </div>
            {agent.system_prompt ? (
              <pre className="max-h-64 overflow-y-auto rounded-sm border border-gray-100 bg-gray-50/50 p-3 font-mono text-[10px] leading-relaxed text-gray-600 whitespace-pre-wrap">
                {agent.system_prompt}
              </pre>
            ) : agent.prompt_id ? (
              <p className="text-[11px] text-[#3454cf]">Uses a Prompt Studio template (ID: {agent.prompt_id})</p>
            ) : (
              <p className="text-[11px] text-gray-400">No system prompt configured.</p>
            )}
          </div>

          {/* Audit History */}
          <div className="border-t border-gray-100 pt-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex w-full items-center justify-between text-left"
            >
              <div className="flex items-center gap-1.5">
                <History className="h-3 w-3 text-gray-400" />
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Audit History</h4>
              </div>
              <span className="text-[10px] text-gray-400">{showHistory ? 'Hide' : 'Show'}</span>
            </button>

            {showHistory && (
              <div className="mt-2 space-y-2">
                {loadingHistory ? (
                  <p className="py-3 text-center text-[11px] text-gray-400">Loading audit history...</p>
                ) : history.length === 0 ? (
                  <p className="py-3 text-center text-[11px] text-gray-400">No audit history recorded yet.</p>
                ) : (
                  history.map((entry) => {
                    const isExpanded = expandedId === entry.id
                    return (
                      <div key={entry.id} className="rounded border border-gray-200 bg-white p-2.5 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                                entry.action === 'create'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : entry.action === 'delete'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {entry.action}
                            </span>
                            <span className="font-mono text-[10px] text-gray-500">{entry.changedBy.slice(0, 8)}</span>
                          </div>
                          <span className="text-[9px] text-gray-400">{new Date(entry.changedAt).toLocaleString()}</span>
                        </div>

                        {entry.changeNote && (
                          <p className="text-gray-600 bg-gray-50 p-1.5 rounded italic text-[10px]">
                            "{entry.changeNote}"
                          </p>
                        )}

                        <div className="flex items-center justify-between pt-0.5">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                            className="text-[10px] font-medium text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <FileText className="h-2.5 w-2.5" />
                            {isExpanded ? 'Hide Diff' : 'View Diff'}
                          </button>
                          {entry.action !== 'create' && entry.action !== 'delete' && (
                            <button
                              onClick={() => void handleRestore(entry.id)}
                              disabled={restoring === entry.id}
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded hover:bg-purple-100 disabled:opacity-50"
                            >
                              <RotateCcw className="h-2.5 w-2.5" />
                              {restoring === entry.id ? 'Restoring...' : 'Restore'}
                            </button>
                          )}
                        </div>

                        {isExpanded && (
                          <div className="mt-1 overflow-x-auto rounded border border-gray-200 bg-gray-50/60 p-2">
                            <AgentDiffViewer diff={entry.diff} />
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-xs">
            <div>
              <span className="text-gray-400">Created</span>
              <p className="font-medium text-gray-700">{new Date(agent.created_at).toLocaleString()}</p>
            </div>
            <div>
              <span className="text-gray-400">Last Updated</span>
              <p className="font-medium text-gray-700">{new Date(agent.updated_at).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

/** Renders a field-level diff for agent history entries. */
function AgentDiffViewer({ diff }: { diff: Record<string, { before: unknown; after: unknown }> }): JSX.Element {
  const entries = Object.entries(diff ?? {})
  if (entries.length === 0) {
    return <p className="py-1.5 text-[10px] italic text-gray-400">No field changes recorded.</p>
  }

  const formatValue = (v: unknown): string => {
    if (v === null || v === undefined) return '—'
    if (typeof v === 'boolean') return v ? 'true' : 'false'
    if (typeof v === 'string') return v.length > 80 ? v.slice(0, 80) + '...' : v
    return JSON.stringify(v, null, 0).slice(0, 120)
  }

  return (
    <div className="text-[10px]">
      <div className="grid grid-cols-[100px_1fr_1fr] gap-1.5 pb-1 text-[9px] font-semibold uppercase text-gray-400">
        <span>Field</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {entries.map(([field, value]) => (
        <div key={field} className="grid grid-cols-[100px_1fr_1fr] gap-1.5 py-0.5 border-t border-gray-100">
          <span className="font-mono text-gray-600">{field}</span>
          <span className="text-red-600 break-words">{formatValue(value?.before)}</span>
          <span className="text-emerald-600 break-words">{formatValue(value?.after)}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Agent Table — list view matching the orchestrator panel pattern
// ---------------------------------------------------------------------------

type AgentStatusFilter = 'all' | 'enabled' | 'disabled'
type AgentSortField = 'name' | 'type' | 'updatedAt'
type AgentSortOrder = 'asc' | 'desc'

function AgentTable({ agents, loading, onUpdate }: { agents: Agent[]; loading: boolean; onUpdate: () => void }): JSX.Element {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<AgentStatusFilter>('all')
  const [sortBy, setSortBy] = useState<AgentSortField>('name')
  const [sortOrder, setSortOrder] = useState<AgentSortOrder>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [viewingAgent, setViewingAgent] = useState<Agent | null>(null)
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [drawerTab, setDrawerTab] = useState<'basic' | 'tools' | 'knowledge' | 'prompt'>('basic')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [_drawerSaving, setDrawerSaving] = useState(false)
  const [toolCatalog, setToolCatalog] = useState<ToolCatalogEntry[]>([])
  const [knowledgeCollections, setKnowledgeCollections] = useState<KnowledgeCollection[]>([])

  // Load tool and knowledge catalogs when the drawer opens
  useEffect(() => {
    if (drawerOpen) {
      if (toolCatalog.length === 0) {
        api.get<{ tools: ToolCatalogEntry[] }>('/admin/ai/tools').then((res) => {
          setToolCatalog(res.tools ?? [])
        }).catch((err) => toast.error('Failed to load tools', err instanceof Error ? err.message : String(err)))
      }
      if (knowledgeCollections.length === 0) {
        api.get<{ collections: KnowledgeCollection[] }>('/admin/ai/knowledge-collections').then((res) => {
          setKnowledgeCollections(res.collections ?? [])
        }).catch((err) => toast.error('Failed to load knowledge collections', err instanceof Error ? err.message : String(err)))
      }
    }
  }, [drawerOpen, toolCatalog.length, knowledgeCollections.length])

  const filtered = useMemo(() => {
    let result = [...agents]
    const q = search.toLowerCase()
    if (q) {
      result = result.filter((a) => a.name.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q))
    }
    if (statusFilter === 'enabled') result = result.filter((a) => a.is_enabled)
    if (statusFilter === 'disabled') result = result.filter((a) => !a.is_enabled)

    result.sort((a, b) => {
      let cmp = 0
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name)
      else if (sortBy === 'type') cmp = a.type.localeCompare(b.type)
      else if (sortBy === 'updatedAt') cmp = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
      return sortOrder === 'asc' ? cmp : -cmp
    })
    return result
  }, [agents, search, statusFilter, sortBy, sortOrder])

  // Pagination
  const totalCount = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endIdx = Math.min(currentPage * pageSize, totalCount)

  // Reset to first page when filters or sorting change
  useEffect(() => {
    setPage(1)
  }, [search, statusFilter, sortBy, sortOrder, pageSize])

  const changeSort = (field: AgentSortField): void => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const openDrawer = (agent: Agent, tab: 'basic' | 'tools' | 'knowledge' | 'prompt'): void => {
    setEditingAgent(agent)
    setDrawerTab(tab)
    setDrawerOpen(true)
  }

  const toDrawerAgent = (a: Agent): DrawerAgent => {
    const category = (a.type === 'data'
      ? 'Data'
      : a.type === 'routing'
        ? 'Routing'
        : 'Response') as DrawerAgent['category']
    return {
      id: a.id,
      slug: a.slug,
      name: a.name,
      description: a.description,
      type: a.type,
      category,
      color: a.type === 'data' ? 'emerald' : a.type === 'routing' ? 'blue' : 'purple',
      status: { enabled: a.is_enabled, isSystem: a.is_system },
      tools: a.tools.map((t) => t.tool_name),
      knowledge: a.knowledge_collections.map((kc) => kc.id),
      instructions: a.system_prompt ?? undefined,
      promptId: a.prompt_id ?? null,
    }
  }

  const drawerTools: ToolDef[] = useMemo(
    () => toolCatalog.map((t) => ({ name: t.name, category: t.handlerType || 'General' })),
    [toolCatalog],
  )

  const drawerKnowledge: KnowledgeDef[] = useMemo(
    () => knowledgeCollections.map((kc) => ({ id: kc.id, name: kc.name, category: kc.is_system ? 'System' : 'Custom' })),
    [knowledgeCollections],
  )

  const handleDrawerSave = async (updated: DrawerAgent, selectedGuardrailIds: string[]): Promise<void> => {
    if (!editingAgent) return
    setDrawerSaving(true)
    try {
      await api.put(`/admin/ai/agents/${editingAgent.slug}`, {
        name: updated.name,
        description: updated.description,
        system_prompt: updated.instructions,
        is_enabled: updated.status.enabled,
        prompt_id: updated.promptId ?? null,
      })

      // Save guardrails attached to this agent
      await api.put(`/admin/ai/agents/${editingAgent.slug}/guardrails`, {
        guardrails: selectedGuardrailIds.map((guardrailId, index) => ({ guardrailId, sortOrder: index })),
      })

      const currentTools = new Set(editingAgent.tools.map((t) => t.tool_name))
      const updatedTools = new Set(updated.tools)
      const toolPromises: Promise<unknown>[] = []
      for (const toolName of updated.tools) {
        if (!currentTools.has(toolName)) {
          toolPromises.push(api.put(`/admin/ai/agents/${editingAgent.slug}/tools`, { toolName, isEnabled: true }))
        }
      }
      for (const t of editingAgent.tools) {
        if (!updatedTools.has(t.tool_name)) {
          toolPromises.push(api.delete(`/admin/ai/agents/${editingAgent.slug}/tools/${t.tool_name}`))
        }
      }

      const currentKnowledge = new Set(editingAgent.knowledge_collections.map((kc) => kc.id))
      const updatedKnowledge = new Set(updated.knowledge)
      const knowledgePromises: Promise<unknown>[] = []
      for (const id of updated.knowledge) {
        if (!currentKnowledge.has(id)) {
          knowledgePromises.push(api.post(`/admin/ai/agents/${editingAgent.slug}/knowledge`, { collectionId: id }))
        }
      }
      for (const kc of editingAgent.knowledge_collections) {
        if (!updatedKnowledge.has(kc.id)) {
          knowledgePromises.push(api.delete(`/admin/ai/agents/${editingAgent.slug}/knowledge/${kc.id}`))
        }
      }

      await Promise.all([...toolPromises, ...knowledgePromises])
      setDrawerOpen(false)
      onUpdate()
      toast.success('Agent updated', `"${updated.name}" has been saved.`)
    } catch (err) {
      toast.error('Failed to save agent:', err instanceof Error ? err.message : String(err))
    } finally {
      setDrawerSaving(false)
    }
  }

  const handleDrawerDelete = async (_id: string): Promise<void> => {
    if (!editingAgent) return
    try {
      await api.delete(`/admin/ai/agents/${editingAgent.slug}`)
      setDrawerOpen(false)
      onUpdate()
      toast.success('Agent deleted', `"${editingAgent.name}" has been deleted.`)
    } catch (err) {
      toast.error('Failed to delete agent:', err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or slug..."
              className="w-64 rounded-sm border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
            />
          </div>
          <div className="flex items-center gap-2">
                        <Select
              ariaLabel={"Status filter"}
              size="sm"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as AgentStatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
                        </Select>
            <span className="text-[10px] font-medium text-gray-400">
              {filtered.length} agent{filtered.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Table */}
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="cursor-pointer px-4 py-2 font-medium hover:text-gray-700" onClick={() => changeSort('name')}>
                Agent
              </th>
              <th className="cursor-pointer px-4 py-2 font-medium hover:text-gray-700" onClick={() => changeSort('type')}>
                Type
              </th>
              <th className="px-4 py-2 font-medium">Tools</th>
              <th className="px-4 py-2 font-medium">Knowledge</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="cursor-pointer px-4 py-2 font-medium hover:text-gray-700" onClick={() => changeSort('updatedAt')}>
                Updated
              </th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td className="px-4 py-6 text-center text-xs text-gray-400" colSpan={7}>
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </span>
                </td>
              </tr>
            ) : (
              <>
                {paginated.map((agent) => {
                  const meta = AGENT_TYPE_META[agent.type] ?? AGENT_TYPE_META['custom']!
                  return (
                    <tr key={agent.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`flex h-6 w-6 items-center justify-center rounded-sm ${meta.iconBg}`}>
                            {meta.icon}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 font-medium text-gray-900">
                              {agent.name}
                              {agent.is_system && (
                                <span className="rounded bg-[#3454cf]/5 px-1 py-0.5 text-[9px] font-semibold text-[#3454cf]">SYSTEM</span>
                              )}
                            </div>
                            <div className="text-[10px] text-gray-400">{agent.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${meta.text} opacity-70`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{agent.tools.length} tools</td>
                      <td className="px-4 py-3 text-gray-600">{agent.knowledge_collections.length} collections</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${agent.is_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {agent.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(agent.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* View */}
                          <button
                            className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                            onClick={() => setViewingAgent(agent)}
                            title="View"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          {/* Edit — opens drawer on Basic tab */}
                          <button
                            className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                            onClick={() => openDrawer(agent, 'basic')}
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {/* Delete — only for non-system agents */}
                          {!agent.is_system && (
                            <button
                              className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"
                              onClick={() => setViewingAgent(agent)}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-xs text-gray-400" colSpan={7}>
                      No agents match the current filters.
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between rounded-sm border border-gray-200 bg-white px-4 py-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          {/* Left: results info + page size */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500">
              Showing <span className="font-medium text-gray-700">{startIdx}-{endIdx}</span> of <span className="font-medium text-gray-700">{totalCount}</span> agent{totalCount !== 1 ? 's' : ''}
            </span>
                        <Select
              ariaLabel={"Page Size"}
              size="sm"
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
            >
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
                        </Select>
          </div>

          {/* Right: page navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="First page"
              >
                «
              </button>
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-3 w-3" />
                Prev
              </button>

              {/* Page number buttons */}
              {(() => {
                const pages: number[] = []
                const maxButtons = 5
                let start = Math.max(1, currentPage - Math.floor(maxButtons / 2))
                const end = Math.min(totalPages, start + maxButtons - 1)
                start = Math.max(1, end - maxButtons + 1)
                for (let i = start; i <= end; i++) pages.push(i)
                return pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`h-7 w-7 rounded-sm text-[11px] font-medium ${
                      p === currentPage
                        ? 'bg-[#3454cf] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))
              })()}

              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
                className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="Last page"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}

      {/* View dialog */}
      <AgentViewDialog agent={viewingAgent} onClose={() => setViewingAgent(null)} onRestored={onUpdate} />

      {/* Edit drawer — tabbed form matching the Tools tab pattern */}
      {editingAgent && (
        <EditAgentDrawer
          agent={toDrawerAgent(editingAgent)}
          isOpen={drawerOpen}
          allTools={drawerTools}
          allKnowledge={drawerKnowledge}
          initialTab={drawerTab}
          onClose={() => setDrawerOpen(false)}
          onSave={handleDrawerSave}
          onDelete={handleDrawerDelete}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AiAgentsPage(): JSX.Element {
  return (
    <RequirePermission permission="ai:agents_view">
      <AiAgentsPageInner />
    </RequirePermission>
  )
}

function AiAgentsPageInner(): JSX.Element {
  const toast = useToast()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'agents' | 'knowledge' | 'training' | 'chat' | 'tools' | 'prompts' | 'orchestrators' | 'guardrails' | 'handoff'>('orchestrators')
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Create drawer state — uses EditAgentDrawer in create mode
  const [createTools, setCreateTools] = useState<ToolDef[]>([])
  const [createKnowledge, setCreateKnowledge] = useState<KnowledgeDef[]>([])

  // Orchestrator graph state — lazy-loaded when the user opens the graph drawer.
  const [graphOrchestrators, setGraphOrchestrators] = useState<GraphOrchestrator[]>([])
  const [graphOpen, setGraphOpen] = useState(false)
  const [graphLoading, setGraphLoading] = useState(false)

  const loadAgents = useCallback(async () => {
    try {
      const data = await api.get<{ agents: Agent[] }>('/admin/ai/agents')
      setAgents(data.agents ?? [])
    } catch (err) {
      toast.error('Failed to load agents:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  // Load tool and knowledge catalogs for the create drawer
  useEffect(() => {
    if (showCreateModal) {
      if (createTools.length === 0) {
        api.get<{ tools: ToolCatalogEntry[] }>('/admin/ai/tools').then((res) => {
          setCreateTools((res.tools ?? []).map((t) => ({ name: t.name, category: t.handlerType || 'General' })))
        }).catch((err) => toast.error('Failed to load tools', err instanceof Error ? err.message : String(err)))
      }
      if (createKnowledge.length === 0) {
        api.get<{ collections: KnowledgeCollection[] }>('/admin/ai/knowledge-collections').then((res) => {
          setCreateKnowledge((res.collections ?? []).map((kc) => ({ id: kc.id, name: kc.name, category: kc.is_system ? 'System' : 'Custom' })))
        }).catch((err) => toast.error('Failed to load knowledge collections', err instanceof Error ? err.message : String(err)))
      }
    }
  }, [showCreateModal, createTools.length, createKnowledge.length])

  const handleCreateAgent = async (data: CreateAgentData): Promise<void> => {
    // 1. Create the agent
    const res = await api.post<{ agent: { id: string; slug: string } }>('/admin/ai/agents', {
      name: data.name,
      slug: data.slug,
      description: data.description || undefined,
      type: data.type,
      system_prompt: data.systemPrompt || undefined,
      prompt_id: data.promptId || undefined,
      is_enabled: data.isEnabled,
    })
    const agentSlug = res.agent.slug
    // 2. Attach selected tools
    for (const toolName of data.tools) {
      try {
        await api.put(`/admin/ai/agents/${agentSlug}/tools`, { toolName, isEnabled: true })
      } catch { /* ignore individual tool errors */ }
    }
    // 3. Attach selected knowledge collections
    for (const collectionId of data.knowledge) {
      try {
        await api.post(`/admin/ai/agents/${agentSlug}/knowledge`, { collectionId })
      } catch { /* ignore individual knowledge errors */ }
    }
    // 4. Attach selected guardrails
    if (data.guardrailIds.length > 0) {
      try {
        await api.put(`/admin/ai/agents/${agentSlug}/guardrails`, {
          guardrails: data.guardrailIds.map((guardrailId, index) => ({ guardrailId, sortOrder: index })),
        })
      } catch { /* ignore guardrail errors */ }
    }
    toast.success('Agent created', `Agent "${data.name}" has been created successfully.`)
    loadAgents()
  }

  const blankAgent: DrawerAgent = {
    id: '',
    slug: '',
    name: '',
    description: '',
    type: 'custom',
    category: 'Response',
    color: 'purple',
    status: { enabled: true, isSystem: false },
    tools: [],
    knowledge: [],
    instructions: undefined,
  }

  const openGraph = useCallback(async () => {
    setGraphOpen(true)
    if (graphOrchestrators.length > 0) return
    setGraphLoading(true)
    try {
      const data = await api.get<{ orchestrators: GraphOrchestrator[] }>(
        '/admin/ai/orchestrators?includeArchived=false&all=true',
      )
      setGraphOrchestrators(data.orchestrators ?? [])
    } catch (err) {
      toast.error('Failed to load orchestrator graph:', err instanceof Error ? err.message : String(err))
    } finally {
      setGraphLoading(false)
    }
  }, [graphOrchestrators.length, toast])

  const graphMain = useMemo(
    () => graphOrchestrators.find((o) => o.type === 'main' && !o.isArchived),
    [graphOrchestrators],
  )
  const graphSides = useMemo(
    () =>
      graphOrchestrators
        .filter((o) => o.type === 'side' && !o.isArchived)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [graphOrchestrators],
  )

  const tabs = [
    { key: 'orchestrators' as const, label: 'Orchestrators', icon: <Network className="h-3.5 w-3.5" /> },
    { key: 'agents' as const, label: 'Agents', icon: <Bot className="h-3.5 w-3.5" /> },
    { key: 'tools' as const, label: 'Tool Catalog', icon: <Wrench className="h-3.5 w-3.5" /> },
    { key: 'knowledge' as const, label: 'Knowledge Base', icon: <Database className="h-3.5 w-3.5" /> },
    { key: 'prompts' as const, label: 'Prompts', icon: <FileText className="h-3.5 w-3.5" /> },
    { key: 'guardrails' as const, label: 'Guardrails', icon: <Shield className="h-3.5 w-3.5" /> },
    { key: 'handoff' as const, label: 'Handoff Rules', icon: <ArrowRightLeft className="h-3.5 w-3.5" /> },
    { key: 'training' as const, label: 'Training', icon: <BookOpen className="h-3.5 w-3.5" /> },
    { key: 'chat' as const, label: 'Chat', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  ]

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab.key ? 'border-[#3454cf] text-[#3454cf]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'agents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Agents</h3>
              <p className="text-xs text-gray-500">Manage AI agents, their tools, knowledge, and system prompts</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                onClick={openGraph}
              >
                <Eye className="h-3.5 w-3.5" />
                View Graph
              </button>
              <button
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Create Agent
              </button>
            </div>
          </div>
          <AgentTable agents={agents} loading={loading} onUpdate={loadAgents} />
        </div>
      )}

      {activeTab === 'tools' && <ToolsPanel agents={agents} />}
      {activeTab === 'orchestrators' && <OrchestratorsPanel />}
      {activeTab === 'guardrails' && <GuardrailsPanel />}
      {activeTab === 'handoff' && <HandoffRulesPanel />}
      {activeTab === 'knowledge' && <KnowledgePanel />}
      {activeTab === 'prompts' && <AiPromptsPage />}
      {activeTab === 'training' && <TrainingPanel />}
      {activeTab === 'chat' && <ChatPanel />}

      <EditAgentDrawer
        mode="create"
        agent={blankAgent}
        isOpen={showCreateModal}
        allTools={createTools}
        allKnowledge={createKnowledge}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateAgent}
      />

      <ResourceDrawer open={graphOpen} onClose={() => setGraphOpen(false)} title="Orchestrator Graph" width="full">
        {graphLoading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading orchestrator graph...
          </div>
        ) : graphMain ? (
          <OrchestratorGraph
            main={graphMain}
            sides={graphSides.map((s) => ({ ...s, members: s.members ?? [] }))}
          />
        ) : (
          <div className="py-12 text-center text-sm text-gray-400">
            No active orchestrators found. Create one in the Orchestrators tab.
          </div>
        )}
      </ResourceDrawer>
    </div>
  )
}
